import StrategyBuilder from "./pages/StrategyBuilder";
import StrategyPrompt from "./pages/StrategyPrompt";
import StrategyTerminal from "./pages/StrategyTerminal";
import StrategyBacktest from "./pages/StrategyBacktest";
import SimulatedTrading from "./pages/SimulatedTrading";
import OptionChart from "./pages/OptionChart";
import CryptoChart from "./pages/CryptoChart";
import ReportsView from "./pages/ReportsView";
import DataAnalysis from "./pages/DataAnalysis";
import AdminPanel from "./pages/AdminPanel";
import Settings from "./pages/Settings";
import AiAgentLog from "./pages/AiAgentLog";
import { authAPI } from "./services/api";
import { fmtDate, fmtDateTime } from "./utils/date";
import { useBrokers, BrokerPickModal } from "./components/BrokerPicker";
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { RadialBarChart, RadialBar, PolarAngleAxis, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';

const API = '/api';
// Crypto portal mode — crypto.<domain> is a STANDALONE crypto-algo platform (Delta
// BTC/ETH); every other host is the NSE portal. The crypto portal shares the build
// but is fully rebranded, shows only crypto nav, and only crypto strategies/data —
// it has no relation to the NSE portal.
const IS_CRYPTO_PORTAL = typeof window !== 'undefined' && /^crypto\./i.test(window.location.hostname || '');
// A crypto (Delta Exchange) broker — provider 'delta', or named like one. NSE portal
// hides these; crypto portal shows only these. Keeps the two broker pools separate.
const isDeltaBroker = (b) => (b && (b.provider === 'delta' || /delta/i.test(b.name || '')));
// Brand identity per portal (name, tagline, logo gradient + glyph).
const BRAND = IS_CRYPTO_PORTAL
  ? { name: 'CryptoAlgo', tagline: 'Crypto Algo Trading', g1: '#f59e0b', g2: '#ea580c', glyph: '₿' }
  : { name: 'Power of Algo Trading', tagline: 'Algo Trading Platform', g1: '#6366f1', g2: '#2563eb', glyph: null };
// On the crypto portal show ONLY these tabs (crypto-relevant); everything else
// (pre-market, market-mood, option-chart, AI-prompt, simulated-trading) is NSE-only.
// '/broker-manage' shows the Delta Exchange connect screen on the crypto portal
// (CryptoBrokerView), not the NSE Zerodha/Upstox flow.
const CRYPTO_NAV_ALLOW = new Set(['/', '/broker-manage', '/strategy-builder', '/strategies', '/positions', '/activity', '/backtesting', '/data-analysis', '/option-chart', '/reports', '/subscription', '/settings', '/admin']);
// A strategy doc belongs to the crypto portal when its assetClass is crypto_*.
const isCryptoStrategyDoc = (s) => /^crypto/.test((s && s.assetClass) || '');
// Filter a strategy list to the active portal (crypto host → crypto only; NSE host
// → hide crypto). Keeps each portal's Strategies/Dashboard/Positions self-contained.
const portalStrategies = (list) => (Array.isArray(list) ? list : []).filter(s => IS_CRYPTO_PORTAL ? isCryptoStrategyDoc(s) : !isCryptoStrategyDoc(s));
// Positions auto-refresh cadence: crypto runs on the engine's 1s fast loop, NSE on 4s.
const POLL_MS = IS_CRYPTO_PORTAL ? 1000 : 4000;
if (IS_CRYPTO_PORTAL && typeof document !== 'undefined') {
  document.title = 'CryptoAlgo — Crypto Algo Trading';
  document.body.classList.add('crypto-theme'); // dark exchange skin (see public/index.html)
}
const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': 'Bearer ' + token } : {}) };
};

// Global expired-session guard: every data fetch ends in `.catch(()=>{})`, so a
// 401 (expired JWT) would otherwise be swallowed silently and leave the user
// staring at fully-rendered but EMPTY tabs (no broker, no strategies). Intercept
// 401s on our own /api calls once, clear the dead token, and bounce to /login.
// Guarded on an existing token so a failed login attempt (no token yet) can't
// trigger a redirect loop.
if (typeof window !== 'undefined' && !window.__arAuthGuard) {
  window.__arAuthGuard = true;
  const _fetch = window.fetch.bind(window);
  window.fetch = (url, opts) =>
    _fetch(url, opts).then((res) => {
      const u = typeof url === 'string' ? url : (url && url.url) || '';
      if (res.status === 401 && u.startsWith(API) && localStorage.getItem('token')) {
        localStorage.removeItem('token');
        if (!window.location.pathname.startsWith('/login')) window.location.href = '/login';
      }
      return res;
    });
}

// ─── Shared design system ───────────────────────────────
// Crypto portal wears a dark exchange theme (Binance/Delta vibe: near-black canvas,
// elevated cards, green/red for up/down). NSE portal keeps the original light theme.
const UI = IS_CRYPTO_PORTAL ? {
  bg: '#0b0e11', card: '#181a20', border: '#2b3139', text: '#eaecef',
  sub: '#b7bdc6', muted: '#848e9c', blue: '#2c8cff', indigo: '#7a5cff',
  green: '#0ecb81', red: '#f6465d',
} : {
  bg: '#eef2f7', card: '#fff', border: '#e8edf3', text: '#0f172a',
  sub: '#64748b', muted: '#94a3b8', blue: '#2563eb', indigo: '#4f46e5',
  green: '#10b981', red: '#ef4444',
};
const card = (extra) => ({ background: UI.card, border: `1px solid ${UI.border}`, borderRadius: 20, boxShadow: IS_CRYPTO_PORTAL ? '0 1px 2px rgba(0,0,0,0.5), 0 18px 44px rgba(0,0,0,0.45)' : '0 1px 2px rgba(15,23,42,0.03), 0 18px 44px rgba(15,23,42,0.05)', ...extra });

// Collapsible "what am I actually looking at" note for a chart/section.
// The analytics sections on this page are dense and only self-evident to someone
// who already knows the concept — but a permanently-expanded paragraph under
// every chart would bury the charts themselves. Collapsed by default keeps the
// explanation one click away and costs no vertical space.
function InfoNote({ label = 'Ye kya hai?', children }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ marginTop: 6 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 10.5, fontWeight: 800, color: UI.blue, letterSpacing: '0.03em' }}>
        ℹ️ {label} {open ? '▲' : '▼'}
      </button>
      {open && (
        <div style={{ marginTop: 7, background: UI.bg, border: `1px solid ${UI.border}`, borderRadius: 9, padding: '11px 13px', fontSize: 12, color: UI.sub, lineHeight: 1.6 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// Gradient hero header used at the top of every page. Sticky so it stays pinned
// at the top while the page content scrolls underneath (sidebar is fixed too).
function PageHead({ title, subtitle, icon, right }) {
  return (
    <div className="page-hero" style={{ position: 'sticky', top: 0, zIndex: 50, background: IS_CRYPTO_PORTAL ? 'linear-gradient(120deg,#11233f 0%,#0d1117 55%,#0b0e11 100%)' : 'linear-gradient(120deg,#4f46e5 0%,#2563eb 55%,#1d4ed8 100%)', borderRadius: 18, padding: '22px 26px', marginBottom: 22, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, boxShadow: IS_CRYPTO_PORTAL ? '0 14px 34px rgba(0,0,0,0.5)' : '0 14px 34px rgba(37,99,235,0.26)', border: IS_CRYPTO_PORTAL ? '1px solid #2b3139' : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {icon && <div className="hero-icon" style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 23, backdropFilter: 'blur(4px)' }}>{icon}</div>}
        <div>
          <h1 style={{ fontSize: 23, fontWeight: 900, margin: 0, letterSpacing: '-0.4px' }}>{title}</h1>
          {subtitle && <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>{subtitle}</div>}
        </div>
      </div>
      {right && <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>{right}</div>}
    </div>
  );
}

// Small status pill
function Pill({ ok, okText = 'Connected', noText = 'Not Connected' }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, background: ok ? '#d1fae5' : '#fee2e2', color: ok ? '#065f46' : '#991b1b', fontSize: 12, fontWeight: 700 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: ok ? '#10b981' : '#ef4444' }} />
      {ok ? okText : noText}
    </span>
  );
}

// Global "reconnect Zerodha" prompt. Listens for the `broker-session-expired`
// window event (fired by the axios 401 interceptor and on a live
// connected→disconnected transition) and offers a one-click reconnect. This is
// NOT a logout — the user's own session stays intact; only the daily broker
// token expired.
function ReconnectBanner() {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const onExpire = () => setShow(true);
    window.addEventListener('broker-session-expired', onExpire);
    return () => window.removeEventListener('broker-session-expired', onExpire);
  }, []);
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, background: 'linear-gradient(90deg,#b91c1c,#ef4444)', color: '#fff', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, boxShadow: '0 4px 14px rgba(0,0,0,0.18)', fontSize: 14, fontWeight: 600 }}>
      <span>⚠️ Your Zerodha session has expired. Reconnect to resume live trading.</span>
      <button onClick={() => { window.location.href = '/broker-manage'; }}
        style={{ background: UI.card, color: '#b91c1c', border: 'none', borderRadius: 8, padding: '7px 16px', fontWeight: 800, cursor: 'pointer', fontSize: 13 }}>
        Reconnect Zerodha
      </button>
      <button onClick={() => setShow(false)} aria-label="Dismiss"
        style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: 18, cursor: 'pointer', lineHeight: 1, opacity: 0.85 }}>
        ×
      </button>
    </div>
  );
}

function Sidebar({ open, onClose }) {
  const logout = () => { localStorage.removeItem('token'); localStorage.removeItem('adminToken'); localStorage.removeItem('impersonating'); window.location.href = '/login'; };
  // Show the Admin link only to admin accounts (server still enforces access).
  const [isAdmin, setIsAdmin] = React.useState(false);
  // Strategy Terminal nav entry: gated by the `strategyChat` flag from /auth/me
  // (admins always resolve true — beta channel; everyone else only once the
  // admin applies the flag to all — see routes/auth.js `/me`).
  const [strategyChatOn, setStrategyChatOn] = React.useState(false);
  React.useEffect(() => { authAPI.me().then(r => { setIsAdmin(!!r.data.isAdmin); setStrategyChatOn(!!r.data.flags?.strategyChat); }).catch(() => {}); }, []);
  const NAV = [
    { to: '/', label: 'Dashboard', icon: '📊' },
    { to: '/pre-market', label: 'Pre-Market', icon: '🔔' },
    { to: '/broker-manage', label: 'Broker', icon: '🏦' },
    { to: '/strategy-prompt', label: 'Create with Prompt', icon: '✨' },
    ...(strategyChatOn ? [{ to: '/strategy-terminal', label: 'Strategy Terminal', icon: '💬' }] : []),
    { to: '/strategy-builder', label: 'Strategy Builder', icon: '⚙️' },
    { to: '/strategies', label: 'Strategies', icon: '📈' },
    { to: '/positions', label: 'Positions', icon: '📌' },
    { to: '/activity', label: 'Activity', icon: '📜' },
    { to: '/ai-agent', label: 'AI Trader Agent', icon: '🤖' },
    { to: '/backtesting', label: 'Backtesting', icon: '🔬' },
    { to: '/data-analysis', label: 'Data Analysis', icon: '📊' },
    { to: '/simulated-trading', label: 'Simulated Trading', icon: '🎮' },
    { to: '/option-chart', label: IS_CRYPTO_PORTAL ? 'Chart' : 'Option Chart', icon: '📉' },
    { to: '/reports', label: 'Reports', icon: '📋' },
    { to: '/subscription', label: 'Subscription', icon: '💳' },
    { to: '/settings', label: 'Settings', icon: '⚙️' },
    ...(isAdmin ? [{ to: '/admin', label: 'Admin', icon: '🛡️' }] : []),
  ].filter(item => !IS_CRYPTO_PORTAL || CRYPTO_NAV_ALLOW.has(item.to));
  return (
    <aside className={'ar-sidebar' + (open ? ' ar-open' : '')} style={{ width: 230, minHeight: '100vh', background: 'linear-gradient(180deg,#0f172a 0%,#1e293b 100%)', position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '22px 18px', display: 'flex', alignItems: 'center', gap: 11 }}>
        <div style={{ width: 36, height: 36, background: `linear-gradient(135deg,${BRAND.g1},${BRAND.g2})`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${BRAND.g2}66`, color: '#fff', fontWeight: 900, fontSize: 20 }}>
          {BRAND.glyph || <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 17L9 11L13 15L21 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" /></svg>}
        </div>
        <span style={{ fontWeight: 900, fontSize: 18, color: '#fff', letterSpacing: '-0.3px' }}>{BRAND.name}</span>
        {IS_CRYPTO_PORTAL && <span style={{ fontWeight: 800, fontSize: 9, color: '#fff', background: 'linear-gradient(135deg,#10b981,#059669)', padding: '2px 6px', borderRadius: 6, letterSpacing: '0.05em' }}>24/7</span>}
      </div>
      <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 12px' }}>
        {NAV.map(item => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'} onClick={onClose} style={({ isActive }) => ({ position: 'relative', display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', textDecoration: 'none', fontSize: 13.5, fontWeight: 600, color: isActive ? '#fff' : '#94a3b8', background: isActive ? 'linear-gradient(135deg,rgba(99,102,241,0.9),rgba(37,99,235,0.9))' : 'transparent', borderRadius: 10, marginBottom: 3, boxShadow: isActive ? '0 4px 14px rgba(37,99,235,0.35)' : 'none' })}>
            <span style={{ fontSize: 16 }}>{item.icon}</span>{item.label}
          </NavLink>
        ))}
      </nav>
      <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <button onClick={logout} style={{ width: '100%', background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '11px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
          🚪 Logout
        </button>
      </div>
    </aside>
  );
}

// ── ABOUT FOOTER ────────────────────────────────────
// Plain-language explanation of the whole portal — what each tab does and how
// the engine actually decides to enter/exit a trade. Lives in the footer so it
// shows on every page. Collapsed by default; click to expand the full guide.
// The trade-logic blurbs below mirror the real engine (StrategyRunner /
// RiskManager / Strategy model) so the description never drifts from behaviour.
function AppFooter() {
  const [open, setOpen] = React.useState(false);
  // The "how it works" guide is NSE-options specific — hide it on the crypto portal.
  if (IS_CRYPTO_PORTAL) return null;

  const TABS = [
    { icon: '📊', name: 'Dashboard', desc: 'Live overview — total P&L, broker status, deployed strategies aur ek master ENGINE ON/OFF switch. Jab tak engine ON nahi hai, koi live trade nahi lagta.' },
    { icon: '🔔', name: 'Pre-Market', desc: 'Market khulne se pehle ka analysis — option chain, India VIX, support/resistance, expiry & holiday info. Din ka view banane ke liye.' },
    { icon: '🏦', name: 'Broker', desc: 'Zerodha (Kite) ko connect karein. Token har roz expire hota hai, is liye daily login zaroori hai — warna engine "disconnected" ho jayega aur orders nahi jayenge.' },
    { icon: '✨', name: 'Create with Prompt', desc: 'Apni strategy plain English/Hinglish me likho ("sell 30 delta straddle, SL 30%") — AI use ek ready strategy me convert kar deta hai. Review karke deploy karo.' },
    { icon: '⚙️', name: 'Strategy Builder', desc: 'Strategy ko manually banao — legs (CE/PE, buy/sell), strike selection, per-leg SL/target, overall SL/target, trailing, re-entry, advance features sab yahin set hote hain.' },
    { icon: '📈', name: 'Strategies', desc: 'Apni saari strategies ki list — edit, backtest, ya Deploy (Paper ya Live). Deployed strategy ko engine har 15 second me monitor karta hai.' },
    { icon: '🔬', name: 'Backtesting', desc: 'Strategy ko purane data par chalakar dekho ki historically kaisa perform karti — paisa lagane se pehle confidence build karne ke liye.' },
    { icon: '📜', name: 'Activity', desc: 'Har deployed strategy ki poori din ki live activity — order kab aur kyu laga, exit kyu hua, broker ne reject kiya to kyu, SL rest hui ya nahi, entry block hui to kis wajah se. Pura audit trail ek jagah. Ya strategy chun kar start–end date daalo aur pure hafte/mahine ka journal ek saath dekho.' },
    { icon: '📋', name: 'Reports', desc: 'Har trade ka log — entry/exit reason, P&L, manual vs auto exit. Time ke saath apni performance track karne ke liye.' },
    { icon: '💳', name: 'Subscription', desc: 'Apna plan choose/manage karo.' },
  ];

  const LOGIC = [
    { icon: '⏱️', title: 'Kaise chalta hai (Engine loop)', body: 'Ek shared scheduler har ~5 second me tick karta hai, ~9:15 AM se 3:35 PM IST tak. Har tick par wo: deployed + "Running" strategies dekhta hai, entry conditions check karta hai, aur open positions ko SL/target ke liye monitor karta hai. Dashboard ka ENGINE switch OFF hai to kuch nahi hota.' },
    { icon: '🎯', title: 'Kab ENTRY leta hai', body: 'Entry tabhi lagti hai jab saari gates khulein: (1) Time — entry time (default 09:20) aa gaya ho aur "No Trade After" cutoff cross na hua ho; (2) Indicator gate (agar ON ho) — live candle par RSI/MACD/SuperTrend/VWAP jaisi conditions match karein; (3) Wait & Trade / Premium-diff delay (agar set ho). Phir strike resolve hoti hai — ATM±offset, Delta-based (e.g. 0.30 delta), ya premium-based — aur LIMIT order lagta hai.' },
    { icon: '🛑', title: 'Kab EXIT karta hai', body: 'Koi bhi ek condition fire hone par leg/strategy band: per-leg SL (%/points/₹) ya target; overall strategy SL/target; trailing SL (profit lock ke baad ratchet); indicator-based exit; ya exit-time (default 15:15) par force square-off. Move-SL-to-cost break-even par SL la deta hai. Zerodha me khud position band karo to engine use detect karke "Manual Exit" mark kar deta hai.' },
    { icon: '🛡️', title: 'Risk control', body: 'Daily MTM loss-limit aur profit-lock, per-leg + whole-strategy SL/target, Pre-Punch SL (broker-side resting SL jo engine down hone par bhi bachata hai), re-entry on SL / re-execute on target, aur advance hedging — Delta-Neutral rebalance & Strike-Roll short strangles ke liye. ⚠️ Overall SL set zaroor karein.' },
    { icon: '🧾', title: 'Orders & Mode', body: 'Sab LIMIT orders (spread cross karne ke liye aggressive priced). Product MIS (intraday) ya NRML (carry-forward). Qty = leg_qty × lot_size × lots. PAPER mode me fill simulate hota hai (no real order); LIVE mode me asli Zerodha order jata hai — asli paisa risk par.' },
  ];

  return (
    <footer style={{ marginTop: 32, borderTop: `1px solid ${UI.border}`, background: UI.card, borderRadius: 16, boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'transparent', border: 'none', cursor: 'pointer', padding: '16px 20px', textAlign: 'left' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>ℹ️</span>
          <span style={{ fontWeight: 800, fontSize: 14, color: UI.text }}>Yeh portal kaise kaam karta hai? — Power of Algo Trading ke baare me & har tab ka guide</span>
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: UI.blue }}>{open ? 'Chhupayein ▲' : 'Padhein ▼'}</span>
      </button>

      {open && (
        <div style={{ padding: '4px 20px 24px' }}>
          {/* Intro */}
          <p style={{ fontSize: 13.5, lineHeight: 1.7, color: UI.sub, margin: '0 0 20px', maxWidth: 900 }}>
            <b style={{ color: UI.text }}>Power of Algo Trading</b> ek automated options-trading platform hai. Aap apni strategy banate ho (manually ya AI prompt se),
            use Zerodha account se connect karke <b>Paper</b> ya <b>Live</b> deploy karte ho — aur backend ka engine usko market hours me
            khud-ba-khud chalata hai: aapke rules ke hisaab se entry leta hai, har leg ko monitor karta hai, aur SL/target/time par exit kar deta hai.
            Aapko screen ke saamne baithne ki zaroorat nahi.
          </p>

          {/* How / when it trades */}
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.5px', color: UI.muted, textTransform: 'uppercase', marginBottom: 12 }}>Trade logic — kaise & kab</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 12, marginBottom: 26 }}>
            {LOGIC.map(l => (
              <div key={l.title} style={{ border: `1px solid ${UI.border}`, borderRadius: 12, padding: 14, background: IS_CRYPTO_PORTAL ? '#1e2329' : '#fbfcfe' }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: UI.text, marginBottom: 6 }}>{l.icon} {l.title}</div>
                <div style={{ fontSize: 12.5, lineHeight: 1.6, color: UI.sub }}>{l.body}</div>
              </div>
            ))}
          </div>

          {/* Tabs guide */}
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.5px', color: UI.muted, textTransform: 'uppercase', marginBottom: 12 }}>Har tab kya karta hai</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 12 }}>
            {TABS.map(t => (
              <div key={t.name} style={{ border: `1px solid ${UI.border}`, borderRadius: 12, padding: 14, background: IS_CRYPTO_PORTAL ? '#1e2329' : '#fbfcfe' }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: UI.text, marginBottom: 6 }}>{t.icon} {t.name}</div>
                <div style={{ fontSize: 12.5, lineHeight: 1.6, color: UI.sub }}>{t.desc}</div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div style={{ marginTop: 20, padding: '12px 14px', borderRadius: 10, background: IS_CRYPTO_PORTAL ? 'rgba(245,158,11,0.12)' : '#fff7ed', border: '1px solid #fed7aa', fontSize: 12, lineHeight: 1.6, color: '#9a3412' }}>
            ⚠️ <b>Zaroori:</b> Options trading me poora capital risk par hota hai. Engine sirf aapke diye rules follow karta hai — koi "guaranteed profit" nahi.
            Live jaane se pehle har strategy par <b>overall SL set karein</b> aur <b>Paper mode</b> me test karein. Backtest results past data par hain, future ki guarantee nahi.
          </div>
        </div>
      )}

      <div style={{ borderTop: `1px solid ${UI.border}`, padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 12, color: UI.muted, fontWeight: 600 }}>© {new Date().getFullYear()} Power of Algo Trading</span>
        <span style={{ fontSize: 11.5, color: UI.muted }}>Market hours: 9:15 AM – 3:30 PM IST · Engine ticks every ~5s</span>
      </div>
    </footer>
  );
}

// Sticky banner shown while an admin is logged in "as" another user (set from
// the Admin panel's "Login as" button). One click restores the admin's own
// token and bounces back to the Admin panel.
function ImpersonationBanner() {
  const who = typeof window !== 'undefined' ? localStorage.getItem('impersonating') : null;
  if (!who) return null;
  const exit = () => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) localStorage.setItem('token', adminToken);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('impersonating');
    window.location.href = '/admin';
  };
  return (
    <div style={{ background: 'linear-gradient(90deg,#b45309,#f59e0b)', color: '#fff', padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, fontSize: 13, fontWeight: 700 }}>
      <span>👁️ Viewing as <b>{who}</b> (admin impersonation)</span>
      <button onClick={exit} style={{ background: UI.card, color: '#b45309', border: 'none', borderRadius: 8, padding: '5px 12px', fontWeight: 800, cursor: 'pointer', fontSize: 12.5 }}>
        Exit to Admin
      </button>
    </div>
  );
}

function Layout({ children }) {
  // Off-canvas nav drawer state (only visible ≤860px; on desktop the sidebar is
  // always shown and these have no effect — see the .ar-* CSS in index.html).
  const [navOpen, setNavOpen] = React.useState(false);
  const loc = useLocation();
  // Close the drawer whenever the route changes (e.g. tapping a nav link).
  React.useEffect(() => { setNavOpen(false); }, [loc.pathname]);
  // Lock body scroll while the drawer is open so the page behind doesn't move.
  React.useEffect(() => {
    document.body.style.overflow = navOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [navOpen]);

  // Toggle `is-scrolled` on <html> once the page leaves the top, so the pinned
  // page hero can go frosted-translucent (CSS in public/index.html).
  React.useEffect(() => {
    const onScroll = () => document.documentElement.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: UI.bg }}>
      <button className="ar-hamburger" aria-label="Menu" onClick={() => setNavOpen(o => !o)}>☰</button>
      {navOpen && <div className="ar-backdrop" onClick={() => setNavOpen(false)} />}
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
      <div className="ar-main" style={{ marginLeft: 230, flex: 1, padding: 28, maxWidth: 'calc(100vw - 230px)', display: 'flex', flexDirection: 'column' }}>
        <ImpersonationBanner />
        <div style={{ flex: 1 }}>{children}</div>
        <AppFooter />
      </div>
    </div>
  );
}

// ── AUTO CAPTURE REQUEST TOKEN ──────────────────────
function BrokerCallback() {
  const location = useLocation();
  const [status, setStatus] = React.useState('Connecting to Zerodha...');

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestToken = params.get('request_token');
    const loginStatus = params.get('status');

    // A Zerodha request_token is SINGLE-USE and expires within minutes. Strip it
    // from the address bar immediately so that reloading this tab can't replay an
    // already-spent token (which always fails) — the captured value above is the
    // only copy we exchange.
    if (requestToken) window.history.replaceState({}, '', '/broker');

    // Kite sends `status=success`; treat a missing status as success too so a
    // valid token is never thrown away on a param quirk.
    if ((loginStatus === 'success' || !loginStatus) && requestToken) {
      setStatus('Token received! Connecting broker...');
      (async () => {
        try {
          // Connect the exact broker the user clicked (saved just before the
          // redirect). Fall back to their first broker for older sessions.
          let brokerId = localStorage.getItem('zerodha_broker_id');
          if (!brokerId) {
            const brokers = await fetch(API + '/brokers', { headers: getHeaders() }).then(r => r.json());
            if (!Array.isArray(brokers) || brokers.length === 0) {
              setStatus('No broker found. Please add a broker first.');
              return;
            }
            brokerId = brokers[0]._id;
          }
          const res = await fetch(API + '/kite/callback', {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ requestToken, brokerId })
          });
          const data = await res.json();
          localStorage.removeItem('zerodha_broker_id');
          if (data.success) {
            setStatus('✅ Zerodha Connected Successfully!');
            setTimeout(() => window.location.href = '/broker-manage', 1500);
          } else {
            setStatus('❌ Connection failed: ' + (data.message || 'Unknown error'));
          }
        } catch (e) {
          setStatus('❌ Error: ' + e.message);
        }
      })();
    } else {
      setStatus('❌ Login failed or cancelled.');
      setTimeout(() => window.location.href = '/broker-manage', 2000);
    }
  }, []);

  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background: IS_CRYPTO_PORTAL ? '#1e2329' : '#f5f6fa'}}>
      <div style={{background:UI.card,borderRadius:16,padding:40,textAlign:'center',boxShadow:'0 4px 20px rgba(0,0,0,0.1)',minWidth:320}}>
        <div style={{fontSize:48,marginBottom:16}}>🔗</div>
        <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Zerodha Connection</div>
        <div style={{fontSize:14,color: IS_CRYPTO_PORTAL ? '#a7afba' : '#6b7280'}}>{status}</div>
        {status.includes('✅') && (
          <div style={{marginTop:16,fontSize:13,color:'#10b981'}}>Redirecting to broker page...</div>
        )}
      </div>
    </div>
  );
}

// ── DASHBOARD ───────────────────────────────────────
function Dashboard() {
  const [data, setData] = React.useState({ totalPnl: 0, broker: null, deployedStrategies: [], user: null });
  const [strategies, setStrategies] = React.useState([]);
  const [engineRunning, setEngineRunning] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const brokers = useBrokers();
  const [pickFor, setPickFor] = React.useState(null); // strategy id awaiting a broker choice
  // Track the broker's last-seen connected state so we can detect the daily
  // token expiry the moment it flips (connected → disconnected) and prompt.
  const prevConnected = React.useRef(false);

  const fetchData = () => {
    fetch(API + '/dashboard/summary', { headers: getHeaders() })
      .then(r => r.json()).then(d => {
        if (d && !d.message) {
          setData(d);
          const connected = !!(d.broker && d.broker.connected);
          if (prevConnected.current && !connected) {
            window.dispatchEvent(new CustomEvent('broker-session-expired'));
          }
          prevConnected.current = connected;
        }
      }).catch(() => {});
    fetch(API + '/strategies', { headers: getHeaders() })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setStrategies(portalStrategies(d)); }).catch(() => {});
    fetch(API + '/engine/status', { headers: getHeaders() })
      .then(r => r.json()).then(d => { if (d) setEngineRunning(d.isRunning); }).catch(() => {})
      .finally(() => setLoading(false));
  };

  React.useEffect(() => { fetchData(); const t = setInterval(fetchData, 30000); return () => clearInterval(t); }, []);

  const toggleEngine = async () => {
    const endpoint = engineRunning ? '/engine/stop' : '/engine/start';
    const res = await fetch(API + endpoint, { method: 'POST', headers: getHeaders() });
    const d = await res.json();
    setEngineRunning(d.status?.isRunning || false);
    alert(engineRunning ? '🛑 Trading Engine Stopped!' : '🚀 Trading Engine Started!');
  };

  // Deploy a strategy straight from the dashboard, then refresh so it moves into
  // the deployed list and its status flips to LIVE without leaving the page.
  const doDeploy = async (id, mode, brokerId) => {
    const d = await deployStrategyReq(id, mode, brokerId);
    if (d._id) {
      setStrategies(p => p.map(s => s._id === id ? d : s));
      const bname = brokerId ? (brokers.find(b => b._id === brokerId)?.name || 'broker') : '';
      alert('Strategy deployed (' + mode + (bname ? ' · ' + bname : '') + ')!'); fetchData();
    } else alert(d.message || 'Deploy failed');
  };

  const deployStrategy = async (id, mode) => {
    if (mode !== 'Live') return doDeploy(id, mode);
    // Always open the Go Live modal — broker choice + Terms & Conditions accept.
    setPickFor(id);
  };

  if (loading) return <Layout><div style={{textAlign:'center',padding:60,color:'#9ca3af'}}>Loading...</div></Layout>;

  // Crypto P&L is USD (fractional → 2dp); NSE is ₹ (whole, en-IN grouping).
  const money = (n) => IS_CRYPTO_PORTAL
    ? '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })
    : '₹' + Number(n || 0).toLocaleString('en-IN');

  return (
    <Layout>
      <PageHead title="My Dashboard" subtitle="Live overview of your automated trading" icon="📊"
        right={
          <button onClick={toggleEngine} style={{ background: engineRunning ? 'rgba(239,68,68,0.95)' : 'rgba(16,185,129,0.95)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 12, padding: '12px 22px', fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>
            {engineRunning ? '🛑 Stop Engine' : '🚀 Start Engine'}
          </button>
        } />

      {/* Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20, marginBottom: 22 }}>
        {/* P&L */}
        <div style={{ background: 'linear-gradient(135deg,#4f46e5 0%,#2563eb 100%)', borderRadius: 18, padding: 28, color: '#fff', boxShadow: '0 12px 30px rgba(37,99,235,0.28)' }}>
          <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 8, fontWeight: 700, letterSpacing: '0.06em' }}>LIVE P&L</div>
          <div style={{ fontSize: 38, fontWeight: 900, marginBottom: 8 }}>{money(data.livePnl ?? data.totalPnl ?? 0)}</div>
          <div style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.16)', marginBottom: 20 }}>📝 Paper: {money(data.paperPnl || 0)}</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{data.user?.name || 'User'}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{data.user?.userId}</div>
        </div>

        {/* Broker */}
        <div style={card({ padding: 24 })}>
          <div style={{ fontSize: 10.5, color: UI.muted, fontWeight: 700, marginBottom: 12, letterSpacing: '0.06em' }}>BROKER STATUS</div>
          {data.broker ? (
            <>
              <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6, color: UI.text }}>{data.broker.name}</div>
              <div style={{ fontSize: 13, color: UI.sub, marginBottom: 10 }}>{data.broker.clientId}</div>
              <Pill ok={data.broker.connected} />
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '18px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🏦</div>
              <div style={{ fontSize: 13, color: UI.muted, marginBottom: 12 }}>No broker connected</div>
              <a href="/broker-manage" style={{ background: UI.blue, color: '#fff', padding: '9px 18px', borderRadius: 9, textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>Add Broker</a>
            </div>
          )}
        </div>

        {/* Engine */}
        <div style={card({ padding: 24 })}>
          <div style={{ fontSize: 10.5, color: UI.muted, fontWeight: 700, marginBottom: 12, letterSpacing: '0.06em' }}>TRADING ENGINE</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: engineRunning ? UI.green : '#9ca3af', boxShadow: engineRunning ? '0 0 10px #10b981' : '' }} />
            <div style={{ fontSize: 20, fontWeight: 800, color: engineRunning ? UI.green : '#374151' }}>{engineRunning ? 'RUNNING' : 'STOPPED'}</div>
          </div>
          <div style={{ fontSize: 12, color: UI.muted, marginBottom: 16 }}>{engineRunning ? 'Monitoring your strategies' : 'Start engine to begin auto trading'}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: UI.text }}>Active: {data.deployedStrategies?.length || 0} strategies</div>
        </div>
      </div>

      {/* Deployed Strategies */}
      {data.deployedStrategies?.length > 0 && (
        <div style={card({ padding: 24, marginBottom: 20 })}>
          <h2 style={{ fontSize: 15, fontWeight: 800, marginBottom: 16, color: UI.text }}>Deployed Strategies</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {data.deployedStrategies.map(s => (
              <div key={s.id} style={{ border: `1px solid ${UI.border}`, borderRadius: 12, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: IS_CRYPTO_PORTAL ? '#1e2329' : '#fbfcfe' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: UI.text }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: UI.green, fontWeight: 700 }}>{s.status} · {s.mode}</div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: s.pnl >= 0 ? UI.green : UI.red }}>{money(s.pnl || 0)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open Positions — Paper & Live tabs, live P&L, one-click exit (was "My Strategies") */}
      <div style={card({ padding: 24, marginBottom: 20 })}>
        <PositionsPanel embedded />
      </div>

      {/* Support */}
      <div style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)', borderRadius: 16, padding: 24, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>Need Help? We're Here for You!</div>
          <div style={{ fontSize: 13, opacity: 0.8 }}>Our support team is ready to assist you.</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a href="https://wa.me/" style={{ background: '#25d366', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 13, textDecoration: 'none' }}>💬 WhatsApp</a>
          <button style={{ background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: '11px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>📞 Call Us</button>
        </div>
      </div>
      {pickFor && (
        <BrokerPickModal brokers={brokers} strategyId={pickFor}
          onConfirm={(brokerId) => { const id = pickFor; setPickFor(null); doDeploy(id, 'Live', brokerId); }}
          onClose={() => setPickFor(null)} />
      )}
    </Layout>
  );
}

// ── PRE-MARKET ANALYSIS ─────────────────────────────
function PreMarket() {
  const [d, setD] = React.useState(null);
  const [cal, setCal] = React.useState(null);
  const [dg, setDg] = React.useState(null); // aaj ka event digest (web se)
  const [mood, setMood] = React.useState(null); // AAJ KA MOOD — har section ka consolidated read
  const [pulse, setPulse] = React.useState(null); // live market-mood pulse (30-min AI)
  const [pulseSeries, setPulseSeries] = React.useState([]); // VIX/PCR trend (intraday or all-time)
  const [pulseRange, setPulseRange] = React.useState('today'); // 'today' = intraday | 'all' = EOD per day
  const [pulseHi, setPulseHi] = React.useState(true); // Hinglish toggle (default on)
  const [newsCat, setNewsCat] = React.useState('all'); // news card-grid filter
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');

  const load = () => {
    fetch(API + '/market/preopen-analysis', { headers: getHeaders() })
      .then(r => r.json())
      .then(x => { if (x && x.message) { setErr(x.message); setD(null); } else if (x) { setD(x); setErr(''); } })
      .catch(() => setErr('Could not load analysis'))
      .finally(() => setLoading(false));
  };
  // Live "market mood" pulse — refreshed on the backend every 30 min; the card
  // just polls the latest so it stays current without a reload.
  const loadPulse = () => {
    fetch(API + '/pulse/latest', { headers: getHeaders() }).then(r => r.json())
      .then(x => setPulse(x && x.pulse ? x : null)).catch(() => { });
  };
  // Trend series is fetched separately because it depends on the AAJ / ALL-TIME
  // toggle (range=all → one EOD point per day). Its own effect re-fetches + polls
  // whenever pulseRange changes so switching the toggle updates all three charts.
  const loadSeries = (range) => {
    fetch(API + '/pulse/series' + (range === 'all' ? '?range=all' : ''), { headers: getHeaders() }).then(r => r.json())
      .then(x => setPulseSeries(Array.isArray(x) ? x : [])).catch(() => { });
  };
  // Today's brief carries two things this page renders: the AI event digest and
  // the consolidated cross-section mood read. The mood is recomputed server-side
  // on every call (and folds in the newest pulse), so it is re-polled with the
  // rest rather than fetched once at mount.
  const loadBrief = () => {
    fetch(API + '/premarket/today', { headers: getHeaders() }).then(r => r.json())
      .then(x => { setDg(x && x.eventDigest ? x.eventDigest : null); setMood(x && x.mood ? x.mood : null); })
      .catch(() => { setDg(null); setMood(null); });
  };
  React.useEffect(() => {
    load(); loadPulse(); loadBrief();
    // F&O expiries + NSE trading holidays (moved here from the old News tab).
    fetch(API + '/news/calendar', { headers: getHeaders() }).then(r => r.json()).then(setCal).catch(() => setCal({ holidays: [], expiries: [] }));
    const t = setInterval(() => { load(); loadPulse(); loadBrief(); }, 30000); return () => clearInterval(t);
  }, []);
  // Trend series: (re)load on toggle change and keep it fresh on the same 30s cadence.
  React.useEffect(() => {
    loadSeries(pulseRange);
    const t = setInterval(() => loadSeries(pulseRange), 30000);
    return () => clearInterval(t);
  }, [pulseRange]);

  const fmt = (n) => (n || n === 0 ? Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—');
  // Pre-market event/holiday label: weekday + "15-May-2026" (name month).
  const fmtEventDate = (dt) => { const d = new Date(dt); return isNaN(d.getTime()) ? fmtDate(dt) : `${d.toLocaleDateString('en-IN', { weekday: 'short' })}, ${fmtDate(dt)}`; };
  const daysTo = (dt) => Math.ceil((new Date(dt) - Date.now()) / 86400000);
  const tone = { bull: UI.green, bear: UI.red, neutral: '#64748b' };
  const riskCol = (l) => l === 'HIGH' ? UI.red : l === 'MODERATE' ? '#f59e0b' : UI.green;
  const impactRank = (x) => ({ high: 3, medium: 2, low: 1 }[x] || 0);
  const lbl = { fontSize: 10.5, color: UI.muted, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 8 };

  const head = (
    <PageHead title="Pre-Market Open Analysis" icon="🔔"
      subtitle={d ? `NIFTY ${fmt(d.spot)} · Expiry ${fmtDate(d.expiry)} · ${d.daysToExpiry}D left` : 'Options analytics for the day ahead'}
      right={<button onClick={load} style={{ background: UI.card, color: UI.blue, border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>↻ Refresh</button>} />
  );

  // ── AAJ KA MOOD — every section of this page reduced to one probability split ─
  // The page shows nine-plus blocks of evidence (global cues, option chain, VIX,
  // trend, prior session, FII/DII, pivots, news, the AI brief) and until now left
  // the reader to reconcile them. This card is that reconciliation: engine/
  // preMarketMood.js scores each block to −1..+1, then splits the day into
  // bullish / sideways / bearish percentages. Independent of the broker feed, so
  // it renders on the error/loading paths too.
  const moodSummaryCard = (() => {
    if (!mood) return null;
    const p = mood.probs;
    const C = { bullish: UI.green, sideways: UI.muted, bearish: UI.red };
    const bands = [
      { k: 'bullish', label: 'BULLISH', hi: 'TEZI', pct: p.bullish },
      { k: 'sideways', label: 'SIDEWAYS', hi: 'SIDEWAYS', pct: p.sideways },
      { k: 'bearish', label: 'BEARISH', hi: 'MANDI', pct: p.bearish },
    ];
    const vTxt = { bullish: '🟢 Tezi ka jhukaav', sideways: '⚪ Sideways / range-bound', bearish: '🔴 Mandi ka jhukaav' }[mood.verdict];
    const convC = { high: UI.green, medium: UI.blue, low: UI.muted, conflicted: '#f59e0b' }[mood.conviction] || UI.muted;
    const charC = { trending: UI.indigo, rangebound: UI.muted, mixed: UI.blue }[mood.dayCharacter] || UI.muted;
    const secC = (v) => (v === 'bullish' ? UI.green : v === 'bearish' ? UI.red : UI.muted);
    const chip = (c, t) => (
      <span style={{ fontSize: 10, fontWeight: 800, color: c, background: UI.bg, border: `1px solid ${UI.border}`, borderRadius: 6, padding: '2px 8px' }}>{t}</span>
    );
    return (
      <div style={card({ padding: 18, marginBottom: 14, borderTop: `4px solid ${C[mood.verdict]}` })}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginBottom: 3 }}>
          <span style={{ fontSize: 14, fontWeight: 900, color: UI.text }}>🎯 AAJ KA MOOD</span>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: C[mood.verdict] }}>{vTxt}</span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {/* Two independent staleness facts, and they can disagree. `briefCarried`
                = the PERCENTAGES are a previous session's read (before 08:30 today's
                brief doesn't exist yet); `liveFresh` = the overlay on top of them.
                A carried brief with a live 08:25 pulse is normal at 08:20 and the
                card must not imply one from the other. */}
            {mood.briefCarried && chip('#f59e0b', `📅 ${fmtBriefDay(mood.briefDate)} ka brief`)}
            {mood.live && (mood.liveFresh
              ? chip(UI.blue, `● LIVE · ${mood.liveAtLabel}`)
              : chip('#f59e0b', `⏸ ${mood.liveAtLabel} tak ka data`))}
            {chip(charC, `${mood.dayCharacter.toUpperCase()} DAY`)}
            {chip(convC, `CONVICTION: ${mood.conviction.toUpperCase()}`)}
          </span>
        </div>
        <div style={{ fontSize: 11.5, color: UI.muted, marginBottom: 12 }}>
          Neeche ke saare sections ko score karke — aaj market bullish / sideways / bearish rehne ka chance.
          {mood.briefCarried && (
            <span style={{ color: '#f59e0b', fontWeight: 700 }}>
              {' '}Aaj ka brief abhi bana nahi (roz 08:30 IST par banta hai) — ye percentages {fmtBriefDay(mood.briefDate)} ke brief ke hain.
            </span>
          )}
          {mood.live && !mood.liveFresh && (
            <span style={{ color: '#f59e0b', fontWeight: 700 }}>
              {' '}Live feed {mood.liveAgeMin} min se update nahi hua (pulse 08:00–15:45, uske baad overnight watch 23:30 tak) — ye {mood.liveAtLabel} ka read hai.
            </span>
          )}
        </div>

        {/* The headline: one stacked bar + three tiles */}
        <div style={{ display: 'flex', height: 34, borderRadius: 9, overflow: 'hidden', border: `1px solid ${UI.border}` }}>
          {bands.filter(x => x.pct > 0).map(x => (
            <div key={x.k} title={`${x.label} ${x.pct}%`}
              style={{ width: `${x.pct}%`, background: C[x.k], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12.5, fontWeight: 900 }}>
              {x.pct >= 9 ? `${x.pct}%` : ''}
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 9, marginTop: 10 }}>
          {bands.map(x => (
            <div key={x.k} style={{ background: UI.bg, border: `1px solid ${mood.verdict === x.k ? C[x.k] : UI.border}`, borderRadius: 12, padding: '9px 11px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: UI.muted, letterSpacing: '0.06em' }}>{x.hi}</div>
              <div style={{ fontSize: 26, fontWeight: 900, color: C[x.k], lineHeight: 1.15 }}>{x.pct}%</div>
            </div>
          ))}
        </div>

        {mood.lineHi && <div style={{ fontSize: 13, color: UI.text, lineHeight: 1.6, marginTop: 12, background: UI.bg, border: `1px solid ${UI.border}`, borderRadius: 10, padding: '10px 13px' }}>{mood.lineHi}</div>}

        {/* Section-by-section: which block of the page voted which way, and how hard */}
        {mood.sections?.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ ...lbl, marginBottom: 3 }}>SECTION-BY-SECTION</div>
            <div style={{ fontSize: 11, color: UI.muted, marginBottom: 9 }}>
              Har section ka score −1 (mandi) se +1 (tezi); bar ki lambai = kitna strong, weight = kitna count hota hai.
            </div>
            <div style={{ display: 'grid', gap: 7 }}>
              {mood.sections.map(s => {
                const col = secC(s.verdict);
                const mag = Math.min(Math.abs(s.score), 1) * 50; // % of half-track
                return (
                  <div key={s.key} style={{ border: `1px solid ${UI.border}`, borderLeft: `3px solid ${col}`, borderRadius: 10, padding: '9px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12.5, fontWeight: 800, color: UI.text }}>{s.labelHi || s.label}</span>
                      {/* Per-section LIVE marker only while the pulse is actually
                          fresh — a stale overlay is flagged once in the header,
                          not re-asserted as "LIVE" on every row. */}
                      {s.live && (mood.liveFresh
                        ? <span style={{ fontSize: 9, fontWeight: 800, color: UI.blue }}>● LIVE</span>
                        : <span style={{ fontSize: 9, fontWeight: 800, color: UI.muted }}>{mood.liveAtLabel}</span>)}
                      <span style={{ fontSize: 10, fontWeight: 800, color: col, textTransform: 'uppercase' }}>
                        {s.verdict === 'neutral' ? 'koi view nahi' : s.verdict}
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: 10.5, color: UI.muted, fontWeight: 700 }}>
                        {s.score >= 0 ? '+' : ''}{s.score} · wt {s.weight}
                      </span>
                    </div>
                    {/* Diverging bar: centre = neutral, right = bullish, left = bearish */}
                    <div style={{ position: 'relative', height: 7, background: UI.bg, border: `1px solid ${UI.border}`, borderRadius: 4, margin: '7px 0 6px' }}>
                      <div style={{ position: 'absolute', left: '50%', top: -2, bottom: -2, width: 1, background: UI.border }} />
                      <div style={{ position: 'absolute', top: 0, bottom: 0, borderRadius: 4, background: col, left: s.score >= 0 ? '50%' : `${50 - mag}%`, width: `${mag}%` }} />
                    </div>
                    <div style={{ fontSize: 11.5, color: UI.sub, lineHeight: 1.45 }}>{s.detailHi || s.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ fontSize: 10.5, color: UI.muted, marginTop: 10 }}>
          ⚠️ Probabilistic decision-support hai, prediction nahi — ise dekh ke aankh band karke trade mat karo.
        </div>
      </div>
    );
  })();

  // ── LIVE MARKET MOOD — AI pulse, auto-refreshed every 30 min ────────────────
  // Independent of the option-chain analysis (runs off free cues + news), so it
  // renders even when the broker analysis is loading or unavailable.
  const moodCard = (() => {
    if (!pulse || !pulse.pulse) return null;
    const en = pulse.pulse;                                   // english (mood enum + colours)
    const hi = pulse.pulseHi;                                 // hinglish prose (best-effort)
    const showHi = pulseHi && hi && hi.headline;
    const P = showHi ? { ...en, ...hi } : en;                 // text to render
    const snap = pulse.snapshot || {};
    const score = Math.max(0, Math.min(100, en.moodScore == null ? 50 : en.moodScore));
    const moodC = score >= 60 ? UI.green : score <= 40 ? UI.red : '#f59e0b';
    const moodTxt = { strongly_bullish: 'STRONGLY BULLISH', bullish: 'BULLISH', neutral: 'NEUTRAL', bearish: 'BEARISH', strongly_bearish: 'STRONGLY BEARISH' }[en.mood] || String(en.mood || '').toUpperCase();
    const ageMin = Math.max(0, Math.round((Date.now() - new Date(pulse.at)) / 60000));
    const grad = score >= 60 ? 'linear-gradient(135deg,#047857,#10b981)' : score <= 40 ? 'linear-gradient(135deg,#b91c1c,#ef4444)' : 'linear-gradient(135deg,#b45309,#f59e0b)';
    const winTag = { 'pre-open': 'PRE-OPEN', market: 'LIVE SESSION', post: 'POST-CLOSE' }[pulse.window] || '';
    const pct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v}%`);
    const chip = (label, val, col) => val == null || val === '—' ? null : (
      <div style={{ border: `1px solid ${UI.border}`, borderRadius: 9, padding: '6px 11px', background: UI.bg }}>
        <span style={{ fontSize: 9.5, color: UI.muted, fontWeight: 800, letterSpacing: '0.04em' }}>{label} </span>
        <span style={{ fontSize: 12.5, fontWeight: 900, color: col || UI.text }}>{val}</span>
      </div>
    );
    const nc = (v) => v == null ? UI.text : v >= 0 ? UI.green : UI.red;
    // After 15:30 the exchange stops computing India VIX and the option chain
    // freezes, so those chips are a CLOSING read, not a live one. The overnight
    // watch keeps writing docs through the evening (global cues, news, flows), and
    // without this suffix the card would keep presenting 15:30 numbers under a
    // "⟳ abhi" stamp — the one way this page could actively mislead.
    const frz = pulse.postClose ? ' (close)' : '';
    return (
      <div style={card({ padding: 0, marginBottom: 14, overflow: 'hidden' })}>
        {/* Gradient hero banner coloured by mood */}
        <div style={{ background: grad, color: '#fff', padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 900, letterSpacing: '0.02em' }}>{pulse.postClose ? '🌙 MARKET MOOD' : '📟 LIVE MARKET MOOD'}</span>
            {/* Post-close the prose is deliberately NOT re-narrated (nothing new to
                say about a shut market, and the quota is needed at the open), so the
                badge must not keep promising a 5-minute AI refresh. */}
            <span style={{ fontSize: 9.5, fontWeight: 900, background: 'rgba(255,255,255,0.22)', borderRadius: 6, padding: '2px 8px' }}>{pulse.postClose ? 'AI read · close ka' : 'AI · har 5 min'}</span>
            {winTag && <span style={{ fontSize: 9.5, fontWeight: 900, background: 'rgba(255,255,255,0.22)', borderRadius: 6, padding: '2px 8px' }}>{winTag}</span>}
            {pulse.postClose && <span style={{ fontSize: 9.5, fontWeight: 900, background: 'rgba(255,255,255,0.22)', borderRadius: 6, padding: '2px 8px' }} title="Global cues, news, FII/DII aur results calendar close ke baad bhi har 15 min refresh hote hai">🌙 OVERNIGHT · har 15 min</span>}
            {pulse.source !== 'llm' && <span style={{ fontSize: 9.5, fontWeight: 900, background: 'rgba(0,0,0,0.25)', borderRadius: 6, padding: '2px 8px' }} title="AI provider ka credit/quota khatam — abhi numbers-based read dikha rahe hain">RULE-BASED</span>}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10.5, opacity: 0.9, fontWeight: 700 }}>⟳ {ageMin === 0 ? 'abhi' : `${ageMin}m ago`}</span>
              {hi && hi.headline && (
                <button onClick={() => setPulseHi(v => !v)} style={{ background: 'rgba(255,255,255,0.22)', color: '#fff', border: 'none', borderRadius: 7, padding: '3px 9px', fontSize: 10.5, fontWeight: 900, cursor: 'pointer' }}>{showHi ? 'EN' : 'HI'}</button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 128 }}>
              <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.1 }}>{moodTxt}</div>
              <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 700, marginTop: 2 }}>Mood score {score}/100</div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              {/* Bearish ↔ Bullish gauge */}
              <div style={{ position: 'relative', height: 10, borderRadius: 6, background: 'rgba(255,255,255,0.28)' }}>
                <div style={{ position: 'absolute', top: -3, left: `calc(${score}% - 2px)`, width: 4, height: 16, borderRadius: 2, background: '#fff', boxShadow: '0 0 0 2px rgba(0,0,0,0.15)' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, opacity: 0.85, fontWeight: 800, marginTop: 4 }}><span>BEARISH</span><span>NEUTRAL</span><span>BULLISH</span></div>
            </div>
          </div>
          {P.headline && <div style={{ fontSize: 14.5, fontWeight: 800, marginTop: 12, lineHeight: 1.4 }}>{P.headline}</div>}
        </div>

        <div style={{ padding: 18 }}>
          {/* The one read to look at first: what news, flows and vol say TOGETHER.
              Analysis is computed every tick; the prose is re-narrated only when
              the numbers actually move, so it stays still when the market does. */}
          {pulse.summary && pulse.summary.summary && (() => {
            const S = pulse.summary;
            const convC = { high: UI.green, conflicted: '#f59e0b', mixed: UI.muted, low: UI.muted }[S.conviction] || UI.muted;
            const convT = { high: '✅ SAB SIGNALS EK TARAF', conflicted: '⚠️ SIGNALS TAKRA RAHE HAIN', mixed: '🤷 MILA-JULA', low: '· HALKA SIGNAL' }[S.conviction] || '';
            const sAge = S && pulse.summaryAt ? Math.max(0, Math.round((Date.now() - new Date(pulse.summaryAt)) / 60000)) : null;
            // Each family's pull, as a diverging bar off a shared centre line.
            //
            // Laid out as label | track | value rows, matching MOVE KI PRICING
            // below — as four side-by-side columns the labels truncated ("Vol &
            // positio…") and there was nowhere to put the number.
            //
            // A diverging bar is genuinely zero-length at score 0, so a neutral
            // signal rendered as nothing at all and read as broken rather than
            // as neutral. Non-zero scores now keep a minimum visible sliver, an
            // exact zero gets an explicit dot on the centre line, and the value
            // is printed either way — so every row says something.
            const bar = (s) => {
              const mag = Math.abs(s.score);
              const w = mag === 0 ? 0 : Math.max(1.5, Math.min(50, mag * 50));
              const pos = s.score >= 0;
              const weak = mag < 0.15;
              const c = weak ? UI.muted : pos ? UI.green : UI.red;
              return (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5 }} title={`${s.label}: ${s.detail}`}>
                  <span style={{ fontSize: 10, fontWeight: 800, color: UI.muted, minWidth: 104, whiteSpace: 'nowrap' }}>{s.label}</span>
                  <div style={{ position: 'relative', flex: 1, height: 8, background: UI.border, borderRadius: 4, minWidth: 80 }}>
                    {w > 0 && (
                      <div style={{
                        position: 'absolute', top: 0, height: '100%', width: `${w}%`,
                        left: pos ? '50%' : `${50 - w}%`,
                        background: c,
                        borderRadius: pos ? '0 4px 4px 0' : '4px 0 0 4px',
                      }} />
                    )}
                    {/* Centre tick paints last so the bar can't hide the origin. */}
                    <div style={{ position: 'absolute', left: '50%', top: -2, width: 1, height: 12, background: UI.sub, opacity: 0.7 }} />
                    {mag === 0 && <div style={{ position: 'absolute', left: 'calc(50% - 3px)', top: 1, width: 6, height: 6, borderRadius: 3, background: UI.muted }} />}
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 900, color: c, minWidth: 34, textAlign: 'right' }}>
                    {s.score > 0 ? '+' : ''}{s.score}
                  </span>
                </div>
              );
            };
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderLeft: `3px solid ${convC}`, borderRadius: 10, padding: '13px 15px', marginBottom: 14, background: UI.bg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>🧠 ABHI KA READ</span>
                  <span style={{ fontSize: 10.5, fontWeight: 900, color: convC }}>{convT}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    {S.source !== 'llm' && <span title="AI quota khatam — computed read dikha rahe hain" style={{ fontSize: 9, fontWeight: 900, color: UI.muted, border: `1px solid ${UI.border}`, borderRadius: 5, padding: '1px 5px' }}>COMPUTED</span>}
                    <span style={{ fontSize: 10.5, color: UI.muted, fontWeight: 700 }}>⟳ {sAge === 0 ? 'abhi' : `${sAge}m`}</span>
                  </span>
                </div>
                <div style={{ fontSize: 13.5, color: UI.text, lineHeight: 1.65, fontWeight: 600 }}>{S.summary}</div>
                {S.takeaway && (
                  <div style={{ fontSize: 12.5, color: UI.text, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${UI.border}`, fontWeight: 800 }}>👉 {S.takeaway}</div>
                )}
                {Array.isArray(S.signals) && S.signals.length > 0 && (
                  <div style={{ marginTop: 11, paddingTop: 9, borderTop: `1px solid ${UI.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, fontWeight: 800, color: UI.muted, marginBottom: 6, letterSpacing: '0.04em' }}>
                      <span>SIGNAL BREAKDOWN</span>
                      <span>📉 BEARISH ←&nbsp;&nbsp;0&nbsp;&nbsp;→ BULLISH 📈</span>
                    </div>
                    {S.signals.map(bar)}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Live snapshot strip */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            {chip('NIFTY' + frz, snap.nifty != null ? `${fmt(snap.nifty)} ${pct(snap.niftyChangePct)}` : null, nc(snap.niftyChangePct))}
            {/* Off-low: how far NIFTY has bounced from the day's intraday low. Shows a
                recovery even when the day is still red vs prevClose (the NIFTY chip),
                so "rising off the low" isn't mistaken for the mood being wrong. */}
            {chip('OFF LOW', snap.niftyOffLowPct != null && snap.niftyOffLowPct > 0.05
              ? `▲ +${snap.niftyOffLowPct}%${snap.niftyLow != null ? ` (L ${fmt(snap.niftyLow)})` : ''}` : null, UI.green)}
            {chip('GIFT', snap.gift != null ? `${fmt(snap.gift)} ${pct(snap.giftChangePct)}` : null, nc(snap.giftChangePct))}
            {chip('VIX' + frz, snap.vix != null ? `${snap.vix}${snap.vixDir ? ` (${snap.vixDir})` : ''}` : null)}
            {chip('PCR' + frz, snap.pcr != null ? `${snap.pcr}${snap.pcrBias ? ` ${snap.pcrBias}` : ''}${snap.pcrVolume != null ? ` · Vol ${snap.pcrVolume}` : ''}` : null)}
            {chip('EXP MOVE' + frz, snap.expectedLow != null ? `${fmt(snap.expectedLow)}–${fmt(snap.expectedHigh)}` : null)}
            {chip('CRUDE', snap.crude != null ? pct(snap.crude) : null, nc(snap.crude))}
            {/* Expiry is the frame every other number sits in for an option
                seller — 0 DTE makes the same VIX/PCR mean something else. */}
            {chip('EXPIRY', snap.daysToExpiry != null ? (snap.daysToExpiry === 0 ? 'AAJ ⚠️' : `${snap.daysToExpiry}d`) : null,
              snap.daysToExpiry === 0 ? UI.red : snap.daysToExpiry === 1 ? '#f59e0b' : UI.text)}
            {chip('STRADDLE' + frz, snap.atmStraddle != null ? `${snap.atmStraddle} (±${snap.straddleMovePts})` : null)}
          </div>

          {/* 🌙 OVERNIGHT WATCH — the post-close half of the card.
              The market being shut does not mean the inputs stopped: US futures and
              the US cash session, crude, gold, USDINR/DXY and the news all keep
              moving, and they are what tomorrow's open is priced off. Every number
              here is the move SINCE OUR 15:30 CLOSE, not the cue's own day-change —
              a global move that happened during our session is already inside the
              NIFTY close and counting it again would read as fresh overnight news.
              Zero LLM spend: the lean is computed, not narrated. */}
          {pulse.postClose && pulse.overnight && (() => {
            const O = pulse.overnight;
            const leanC = { positive: UI.green, mildly_positive: UI.green, flat: UI.muted, mildly_negative: UI.red, negative: UI.red }[O.lean] || UI.muted;
            // 'unknown' = too few cues had a 15:45 anchor, so the only lean we could
            // state would be the day's own move relabelled as overnight. The rows
            // still show (marked), the verdict is withheld.
            const leanT = {
              positive: '🟢 OVERNIGHT SUPPORTIVE', mildly_positive: '🟢 HALKA POSITIVE', flat: '⚪ FLAT — kuch naya nahi',
              mildly_negative: '🔴 HALKA NEGATIVE', negative: '🔴 OVERNIGHT PRESSURE',
              unknown: '⏳ LEAN PENDING — 15:45 anchor nahi mila',
            }[O.lean] || '';
            const oAge = O.at ? Math.max(0, Math.round((Date.now() - new Date(O.at)) / 60000)) : null;
            // An inverted cue (crude, USDINR, DXY, US VIX) rising is BEARISH for
            // NIFTY, so the colour follows the meaning, not the sign.
            const dirC = (r) => {
              const v = r.chg == null ? null : (r.invert ? -r.chg : r.chg);
              if (v == null) return UI.muted;
              return Math.abs(v) < 0.05 ? UI.muted : v > 0 ? UI.green : UI.red;
            };
            const sgn = (v, unit) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v}${unit}`;
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderLeft: `3px solid ${leanC}`, borderRadius: 10, padding: '13px 15px', marginBottom: 14, background: UI.bg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>🌙 OVERNIGHT WATCH</span>
                  <span style={{ fontSize: 10.5, fontWeight: 900, color: leanC }}>{leanT}</span>
                  {O.gapPts != null && (
                    <span style={{ fontSize: 10.5, fontWeight: 900, color: nc(O.gapPts), border: `1px solid ${UI.border}`, borderRadius: 6, padding: '1px 7px' }}
                      title="GIFT Nifty ke close ke baad ke move se nikala approx gap — estimate hai, prediction nahi">
                      GIFT implied gap {sgn(O.gapPts, ' pts')}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 10.5, color: UI.muted, fontWeight: 700 }}>⟳ {oAge === 0 ? 'abhi' : `${oAge}m`}</span>
                </div>
                <div style={{ fontSize: 11.5, color: UI.muted, fontWeight: 600, lineHeight: 1.5, marginBottom: 10 }}>
                  Market band hai. NIFTY · VIX · PCR · OI · straddle 15:30 pe freeze ho gaye — neeche wale cues abhi bhi chal rahe hai, aur <b>close ke baad ka change</b> dikha rahe hai (yehi kal ke open ke liye naya input hai).
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 7 }}>
                  {O.rows.map((r) => (
                    <div key={r.key} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                      border: `1px solid ${UI.border}`, borderRadius: 8, background: UI.card,
                      opacity: r.stale ? 0.55 : 1,
                    }} title={r.stale ? `Feed ka last print ${r.printedAt || '?'} — evening session ka data nahi mil raha, is liye lean mein count nahi kiya` : `Day change ${sgn(r.pct, '%')}${r.raw ? ' · 15:45 anchor nahi mila, day-change hi dikha rahe hai' : ''}`}>
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: UI.muted, minWidth: 78 }}>{r.label}{r.lead ? ' ★' : ''}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: UI.text }}>{r.last != null ? fmt(r.last) : '—'}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 900, color: dirC(r) }}>
                        {r.stale ? 'stale' : r.chg == null ? sgn(r.pct, '%') : sgn(r.chg, 'pp')}
                      </span>
                    </div>
                  ))}
                </div>
                {(O.giftStale || !O.anchored) && (
                  <div style={{ fontSize: 10.5, color: UI.muted, fontWeight: 600, marginTop: 9, paddingTop: 8, borderTop: `1px solid ${UI.border}`, lineHeight: 1.5 }}>
                    {O.giftStale && <div>⚠️ GIFT Nifty ka evening-session print feed se nahi aa raha (last print day-session close ka hai) — is liye usko lean se hata diya aur gap number nahi banaya.</div>}
                    {!O.anchored && <div>ℹ️ {O.lean === 'unknown'
                      ? 'Aaj ke 15:45 close doc mein anchor nahi tha, is liye overnight lean nahi bana rahe — jo dikha raha hai wo cues ka apna DAY change hai (aaj ka move, raat ka naya move nahi). Kal shaam se poora since-close diff aur lean milega.'
                      : 'Kuch cues ka 15:45 anchor nahi mila, unka apna day-change dikha rahe hai — baaki ka since-close diff sahi hai.'}</div>}
                  </div>
                )}
              </div>
            );
          })()}

          {/* MARKET BREADTH. The index print says WHAT happened; breadth says how
              MANY of the 50 stocks did it. NIFTY is cap-weighted, so a +0.4% day
              carried by five heavyweights and a +0.4% day with 40 names
              participating are the identical number and completely different days
              to trade — and nothing else on this page can tell them apart. */}
          {pulse.breadth && (() => {
            const B = pulse.breadth;
            const age = B.asOf ? Math.max(0, Math.round((Date.now() - new Date(B.asOf).getTime()) / 60000)) : null;
            const head = (extra) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>📊 MARKET BREADTH — NIFTY 50 KE ANDAR</span>
                {extra}
                {age != null && <span style={{ marginLeft: 'auto', fontSize: 10, color: UI.muted, fontWeight: 700 }}>⟳ {age === 0 ? 'abhi' : `${age}m`}</span>}
              </div>
            );
            // Pre-open every constituent quote still carries the prior close, so the
            // count is 0 vs 0. Saying that out loud beats printing a flat breadth
            // the tape never produced.
            if (!B.open) return (
              <div style={{ border: `1px solid ${UI.border}`, borderRadius: 10, padding: '11px 13px', marginBottom: 14, background: UI.bg }}>
                {head(null)}
                <div style={{ fontSize: 11.5, color: UI.sub, lineHeight: 1.5 }}>
                  Tape abhi khula nahi — {B.total} stocks pichhle close pe hi hain, to advance/decline count banta hi nahi. 09:15 ke baad ye card live ho jayega.
                </div>
              </div>
            );
            const bandC = { strong: UI.green, positive: UI.green, flat: UI.muted, negative: UI.red, weak: UI.red }[B.band] || UI.muted;
            const bandT = {
              strong: '🟢 BREADTH STRONG', positive: '🟢 BREADTH POSITIVE', flat: '⚪ BREADTH SPLIT',
              negative: '🔴 BREADTH WEAK', weak: '🔴 BREADTH VERY WEAK',
            }[B.band] || '';
            // A narrow move is the finding worth an amber chip: the index is lying
            // about how much of the market agrees with it.
            const narrow = B.divergence && /^narrow/.test(B.divergence.kind);
            const sgn = (n) => (n == null ? '—' : `${n >= 0 ? '+' : ''}${n}%`);
            const seg = (n, c, key) => (n > 0 ? <div key={key} style={{ width: `${(n / B.total) * 100}%`, background: c }} /> : null);
            const count = (n, label, c) => (
              <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
                <b style={{ fontSize: 16, fontWeight: 900, color: c }}>{n}</b>
                <span style={{ fontSize: 10.5, fontWeight: 800, color: UI.muted }}>{label}</span>
              </span>
            );
            // % above the 20/50 DMA is the slow half of breadth: participation that
            // holds above trend, not just a one-session pop.
            const dmaBar = (label, cnt, p) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, color: UI.muted, minWidth: 74 }}>{label}</span>
                <div style={{ flex: 1, height: 7, background: UI.border, borderRadius: 4, minWidth: 60, overflow: 'hidden' }}>
                  <div style={{ width: `${p}%`, height: '100%', background: p >= 60 ? UI.green : p <= 40 ? UI.red : '#f59e0b' }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 900, color: UI.text, minWidth: 70, textAlign: 'right' }}>{cnt}/{B.dmaTotal} · {p}%</span>
              </div>
            );
            const movers = (label, arr, c) => (!arr || !arr.length) ? null : (
              <div style={{ fontSize: 11, color: UI.sub, display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 900, color: UI.muted, minWidth: 44 }}>{label}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  {arr.map((x) => (
                    <span key={x.symbol} style={{ marginRight: 9, whiteSpace: 'nowrap' }}>
                      {x.symbol} <b style={{ color: c }}>{sgn(x.changePct)}</b>
                    </span>
                  ))}
                </span>
              </div>
            );
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderLeft: `3px solid ${bandC}`, borderRadius: 10, padding: '11px 13px', marginBottom: 14, background: UI.bg }}>
                {head(<>
                  <span style={{ fontSize: 12.5, fontWeight: 900, color: bandC }}>{bandT}</span>
                  {narrow && <span style={{ fontSize: 10, fontWeight: 900, color: '#f59e0b', border: '1px solid #f59e0b', borderRadius: 6, padding: '1px 7px' }}>⚠️ NARROW MOVE</span>}
                </>)}

                {/* Participation at a glance: one bar, three counts, the A/D ratio */}
                <div style={{ display: 'flex', height: 26, borderRadius: 7, overflow: 'hidden', border: `1px solid ${UI.border}`, marginBottom: 8 }}>
                  {seg(B.advancing, UI.green, 'a')}
                  {seg(B.unchanged, UI.border, 'u')}
                  {seg(B.declining, UI.red, 'd')}
                </div>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 8 }}>
                  {count(B.advancing, 'UP', UI.green)}
                  {count(B.declining, 'DOWN', UI.red)}
                  {B.unchanged > 0 && count(B.unchanged, 'FLAT', UI.muted)}
                  {B.adRatio != null && (
                    <span style={{ fontSize: 11, fontWeight: 800, color: UI.sub, paddingLeft: 12, borderLeft: `1px solid ${UI.border}` }}>
                      A/D <b style={{ color: B.adRatio >= 1 ? UI.green : UI.red, fontSize: 13 }}>{B.adRatio}</b>
                    </span>
                  )}
                  {(B.strong > 0 || B.weak > 0) && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: UI.muted }}>
                      <b style={{ color: UI.green }}>{B.strong}</b> stocks &gt;+1% · <b style={{ color: UI.red }}>{B.weak}</b> &lt;−1%
                    </span>
                  )}
                </div>

                {/* The divergence line — equal-weight (one vote per stock) against
                    the cap-weighted index print. This IS the narrow/broad answer. */}
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 11.5, paddingTop: 8, borderTop: `1px solid ${UI.border}` }}>
                  <span style={{ color: UI.muted, fontWeight: 800 }}>Average stock <b style={{ color: B.avgChangePct >= 0 ? UI.green : UI.red, fontSize: 13 }}>{sgn(B.avgChangePct)}</b></span>
                  <span style={{ color: UI.muted, fontWeight: 800 }}>vs index <b style={{ color: B.indexChangePct >= 0 ? UI.green : UI.red, fontSize: 13 }}>{sgn(B.indexChangePct)}</b></span>
                </div>
                {B.divergence && <div style={{ fontSize: 11.5, color: narrow ? '#f59e0b' : UI.sub, marginTop: 5, lineHeight: 1.5 }}>{narrow ? '⚠️ ' : '✅ '}{B.divergence.text}</div>}

                {B.dmaTotal && (
                  <div style={{ display: 'grid', gap: 5, marginTop: 9, paddingTop: 8, borderTop: `1px solid ${UI.border}` }}>
                    {dmaBar('20 DMA ke upar', B.aboveDma20, B.aboveDma20Pct)}
                    {dmaBar('50 DMA ke upar', B.aboveDma50, B.aboveDma50Pct)}
                  </div>
                )}

                <div style={{ display: 'grid', gap: 4, marginTop: 9, paddingTop: 8, borderTop: `1px solid ${UI.border}` }}>
                  {movers('LEAD', B.gainers, UI.green)}
                  {movers('DRAG', B.losers, UI.red)}
                </div>

                <div style={{ fontSize: 10, color: UI.muted, marginTop: 8, lineHeight: 1.5 }}>
                  NIFTY cap-weighted hai — 5 heavyweights akele index ko hila sakte hain. Ye card batata hai kitne stocks saath hai: A/D ratio ↑ = broad participation · average stock vs index ka gap = move top-heavy hai ya nahi · DMA % = participation trend ke upar tik raha hai ya sirf aaj ka pop.
                  {B.constituentSource === 'snapshot' && ' (Constituent list live NSE feed se nahi mili — snapshot pe count kiya.)'}
                </div>
              </div>
            );
          })()}

          {/* Move pricing. An expected-move band answers nothing on its own —
              ±200 pts only means something next to what NIFTY actually does. The
              seller's real question is whether today's price for movement is
              rich or thin, and that is a comparison, not a number. */}
          {pulse.moves && pulse.moves.impliedPct != null && (() => {
            const M = pulse.moves;
            const vC = { rich: UI.green, fair: UI.muted, thin: UI.red }[M.verdict] || UI.muted;
            const vT = { rich: '🟢 PREMIUM RICH — seller ko edge', fair: '⚖️ PREMIUM FAIR', thin: '🔴 PREMIUM THIN — buyer ko edge' }[M.verdict] || '';
            // One shared scale so the three bars are actually comparable.
            const max = Math.max(M.impliedPct, M.p90, M.avg20) * 1.1;
            const line = (label, pct, pts, c, note) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5 }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, color: UI.muted, minWidth: 92 }}>{label}</span>
                <div style={{ flex: 1, height: 8, background: UI.border, borderRadius: 4, minWidth: 60 }}>
                  <div style={{ width: `${Math.min(100, pct / max * 100)}%`, height: '100%', background: c, borderRadius: 4 }} />
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 900, color: UI.text, minWidth: 92, textAlign: 'right' }}>±{pts} pts · {pct}%</span>
                {note && <span style={{ fontSize: 9.5, color: UI.muted, minWidth: 40 }}>{note}</span>}
              </div>
            );
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderLeft: `3px solid ${vC}`, borderRadius: 10, padding: '11px 13px', marginBottom: 14, background: UI.bg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 9 }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>📊 MOVE KI PRICING</span>
                  <span style={{ fontSize: 12.5, fontWeight: 900, color: vC }}>{vT}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: UI.muted, fontWeight: 700 }}>{M.ratio}× normal din</span>
                </div>
                {line('MARKET MAANG RAHA', M.impliedPct, M.impliedPts, UI.indigo, M.basis === 'straddle' ? 'straddle' : 'VIX')}
                {line('ASLI AVG (20 din)', M.avg20, M.avg20Pts, UI.blue)}
                {line('BADA DIN (p90)', M.p90, M.p90Pts, UI.muted)}
                <div style={{ fontSize: 11, color: UI.sub, marginTop: 7 }}>
                  Pichhle 60 sessions mein se <b style={{ color: UI.text }}>{Math.round(M.within)}%</b> aaj ki priced range ke andar band hue — yani seller ka historical hit-rate.
                </div>
                {M.event && (
                  <div style={{ fontSize: 11.5, color: UI.text, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${UI.border}`, lineHeight: 1.55 }}>
                    {M.event.thinForEvent ? '⚠️ ' : '✅ '}{M.event.note}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Index-weighted earnings calendar. A single result matters to an index
              trader in proportion to that name's weight, and the day that matters
              is the one carrying the most WEIGHT — not the most companies. */}
          {pulse.earnings && pulse.earnings.days && pulse.earnings.days.length > 0 && (() => {
            const E = pulse.earnings;
            // Bands are about what a session can actually do to the index.
            const band = (w) => (w >= 8 ? { c: UI.red, t: 'BADA EVENT' } : w >= 3 ? { c: '#f59e0b', t: 'DHYAAN DO' } : { c: UI.muted, t: '' });
            const dayLabel = (d) => {
              if (d.isToday) return 'AAJ';
              const dt = new Date(d.date + 'T00:00:00+05:30');
              const t = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
              t.setHours(0, 0, 0, 0);
              const n = Math.round((dt - t) / 86400000);
              return n === 1 ? 'KAL' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' });
            };
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderRadius: 10, padding: '11px 13px', marginBottom: 14, background: UI.bg }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>🏢 INDEX HEAVYWEIGHT RESULTS</span>
                  {E.heaviest && E.heaviest.weight >= 8 && (
                    <span style={{ fontSize: 10.5, fontWeight: 900, color: UI.red }}>⚠️ {dayLabel(E.heaviest)}: NIFTY ka {E.heaviest.weight}% report karega</span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 9.5, color: UI.muted, fontWeight: 700 }} title={`Index weights as of ${E.weightsAsOf}`}>weights ~{E.weightsAsOf}</span>
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {E.days.slice(0, 5).map((d) => {
                    const b = band(d.weight);
                    return (
                      <div key={d.date} style={{ display: 'flex', gap: 9, alignItems: 'baseline', borderLeft: `3px solid ${b.c}`, paddingLeft: 8 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 900, color: d.isToday ? UI.text : UI.sub, minWidth: 62 }}>{dayLabel(d)}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 900, color: b.c, minWidth: 52 }}>{d.weight}%</span>
                        <span style={{ flex: 1, fontSize: 11.5, color: UI.sub, minWidth: 0 }}>
                          {d.names.map((n) => `${n.symbol}${n.weight ? ` (${n.weight}%)` : ''}`).join(' · ')}
                        </span>
                        {b.t && <span style={{ fontSize: 8.5, fontWeight: 900, color: '#fff', background: b.c, borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap' }}>{b.t}</span>}
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 10, color: UI.muted, marginTop: 7 }}>% = us din report karne wale NIFTY 50 stocks ka kul index weight — jitna zyada, utna bada index move ka risk.</div>
              </div>
            );
          })()}

          {/* FII/DII — the first number Indian desks look at. Explicitly dated:
              it is the PRIOR session's provisional figure, never today's. */}
          {pulse.fiidii && (pulse.fiidii.fii || pulse.fiidii.dii) && (() => {
            const F = pulse.fiidii;
            const cr = (n) => `${n >= 0 ? '+' : '−'}₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })} cr`;
            const side = (label, o, icon) => o == null ? null : (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, minWidth: 30 }}>{label}</span>
                <span style={{ fontSize: 14, fontWeight: 900, color: o.net >= 0 ? UI.green : UI.red }}>{cr(o.net)}</span>
                <span style={{ fontSize: 11, color: UI.sub }}>{icon} {o.net >= 0 ? 'khareed rahe' : 'bech rahe'}</span>
              </div>
            );
            const f = F.fii, d = F.dii;
            // Combined institutional flow — the number that actually says whether
            // money net entered or left the cash market once DII buying is set
            // against FII selling. This is the "absorption" answer.
            const net = (f && d) ? (f.net + d.net) : null;
            let read = null;
            if (f && d) {
              if (f.net < 0 && d.net > 0) {
                // How much of FII's selling DII's buying actually covered.
                const cover = Math.min(999, Math.round((d.net / Math.abs(f.net)) * 100));
                read = net >= 0
                  ? `DII ne FII ki poori selling absorb kar li (${cover}% cover) — net paisa market MEIN aaya. Downside cushioned, upside capped.`
                  : `DII ne FII selling ka ~${cover}% absorb kiya — phir bhi net paisa bahar gaya. Support hai par adhoora.`;
              } else if (f.net > 0 && d.net > 0) read = 'Dono khareed rahe — broad support, dips khareedne wale maujood.';
              else if (f.net < 0 && d.net < 0) read = '⚠️ Dono bech rahe — koi absorber nahi, net paisa bahar. Risk zyada.';
              else read = 'FII khareed rahe, DII bech rahe — foreign-led move.';
            }
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderRadius: 10, padding: '11px 13px', marginBottom: 14, background: UI.bg }}>
                <div style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em', marginBottom: 7 }}>
                  💰 FII / DII CASH FLOW <span style={{ color: UI.sub, fontWeight: 700 }}>· {F.date || 'pichhla session'} (provisional)</span>
                </div>
                <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'baseline' }}>
                  {side('FII', f, f && f.net >= 0 ? '🟢' : '🔴')}
                  {side('DII', d, d && d.net >= 0 ? '🟢' : '🔴')}
                  {net != null && (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, paddingLeft: 12, borderLeft: `1px solid ${UI.border}` }}>
                      <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted }}>NET</span>
                      <span style={{ fontSize: 14, fontWeight: 900, color: net >= 0 ? UI.green : UI.red }}>{cr(net)}</span>
                      <span style={{ fontSize: 11, color: UI.sub }}>{net >= 0 ? 'market mein aaya' : 'bahar gaya'}</span>
                    </div>
                  )}
                </div>
                {read && <div style={{ fontSize: 11.5, color: UI.sub, marginTop: 6 }}>{read}</div>}
              </div>
            );
          })()}

          {/* AAJ / ALL-TIME toggle — flips all three pulse charts below between
              today's intraday snapshots and one EOD point per day. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>TREND VIEW</span>
            <div style={{ display: 'inline-flex', border: `1px solid ${UI.border}`, borderRadius: 8, overflow: 'hidden' }}>
              {[['today', 'AAJ'], ['all', 'ALL-TIME']].map(([val, label]) => (
                <button key={val} onClick={() => setPulseRange(val)} style={{
                  border: 'none', cursor: 'pointer', padding: '5px 12px', fontSize: 11.5, fontWeight: 800, letterSpacing: '0.03em',
                  background: pulseRange === val ? UI.indigo : UI.card, color: pulseRange === val ? '#fff' : UI.muted,
                }}>{label}</button>
              ))}
            </div>
            {pulseRange === 'all' && <span style={{ fontSize: 10.5, color: UI.muted }}>din-wise (har din ka EOD)</span>}
          </div>

          {/* VIX & PCR trend — rising / falling through the day (or across days) */}
          {pulseSeries && pulseSeries.length >= 2 && (() => {
            const vals = (k) => pulseSeries.map((p) => p[k]).filter((v) => v != null);
            const dir = (k) => { const a = vals(k); if (a.length < 2) return null; const d = Math.round((a[a.length - 1] - a[0]) * 100) / 100; return { last: a[a.length - 1], delta: d, up: d > 0, flat: d === 0 }; };
            const vx = dir('vix'), pc = dir('pcr'), pv = dir('pcrVolume');
            const tag = (o) => o == null ? '' : `${o.up ? '▲' : o.flat ? '▬' : '▼'} ${o.up ? '+' : ''}${o.delta}`;
            const VIXC = UI.indigo, PCRC = UI.blue, PCRVC = '#0891b2'; // PCR-OI solid, PCR-Vol dashed cyan
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderRadius: 10, padding: '12px 12px 6px', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>📉 VIX &amp; PCR — {pulseRange === 'all' ? 'ALL-TIME TREND' : 'AAJ KA TREND'}</span>
                  {vx && <span style={{ fontSize: 12.5, fontWeight: 800, color: VIXC }}>● VIX {vx.last} <span style={{ color: vx.up ? UI.red : vx.flat ? UI.muted : UI.green }}>{tag(vx)}</span></span>}
                  {pc && <span style={{ fontSize: 12.5, fontWeight: 800, color: PCRC }}>● PCR OI {pc.last} <span style={{ color: pc.up ? UI.green : pc.flat ? UI.muted : UI.red }}>{tag(pc)}</span></span>}
                  {pv && <span style={{ fontSize: 12.5, fontWeight: 800, color: PCRVC }}>◌ PCR Vol {pv.last} <span style={{ color: pv.up ? UI.green : pv.flat ? UI.muted : UI.red }}>{tag(pv)}</span></span>}
                </div>
                <ResponsiveContainer width="100%" height={168}>
                  <LineChart data={pulseSeries} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={UI.border} vertical={false} />
                    <XAxis dataKey="t" tick={{ fontSize: 10, fill: UI.muted }} interval="preserveStartEnd" minTickGap={24} />
                    <YAxis yAxisId="v" tick={{ fontSize: 10, fill: VIXC }} domain={['auto', 'auto']} width={40} />
                    <YAxis yAxisId="p" orientation="right" tick={{ fontSize: 10, fill: PCRC }} domain={['auto', 'auto']} width={38} />
                    <Tooltip contentStyle={{ background: UI.card, border: `1px solid ${UI.border}`, borderRadius: 8, fontSize: 12 }} />
                    <Line yAxisId="v" type="monotone" dataKey="vix" name="India VIX" stroke={VIXC} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                    <Line yAxisId="p" type="monotone" dataKey="pcr" name="PCR (OI)" stroke={PCRC} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                    <Line yAxisId="p" type="monotone" dataKey="pcrVolume" name="PCR (Vol)" stroke={PCRVC} strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
                <div style={{ fontSize: 10, color: UI.muted, marginTop: 2 }}>VIX ↑ = ghabrahat/volatility badh rahi · PCR ↑ = put-writing/bullish lean · PCR OI = positioning, Vol = trade activity · {pulseRange === 'all' ? 'din-wise (har din ka EOD)' : 'har 5 min ka data'}</div>
              </div>
            );
          })()}

          {/* Mood score trend — the same cross-signal read behind the LIVE MARKET
              MOOD card above, but as a true 5-min line (not the throttled prose) —
              from PulseSeries via /pulse/series, so it flips with the AAJ/ALL-TIME
              toggle exactly like the other three panels. */}
          {pulseSeries && pulseSeries.filter((p) => p.moodScore != null).length >= 2 && (() => {
            const vals = pulseSeries.map((p) => p.moodScore).filter((v) => v != null);
            const last = vals[vals.length - 1], delta = Math.round((last - vals[0]) * 10) / 10;
            const MC = delta > 0 ? UI.green : delta < 0 ? UI.red : UI.muted;
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderRadius: 10, padding: '12px 12px 6px', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>🎯 MOOD SCORE — {pulseRange === 'all' ? 'ALL-TIME TREND' : 'AAJ KA TREND'}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: MC }}>● {last}/100 <span>{delta > 0 ? '▲ +' : delta < 0 ? '▼ ' : '▬ '}{delta}</span></span>
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={pulseSeries} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={UI.border} vertical={false} />
                    <XAxis dataKey="t" tick={{ fontSize: 10, fill: UI.muted }} interval="preserveStartEnd" minTickGap={24} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: UI.muted }} width={30} />
                    <ReferenceLine y={50} stroke={UI.border} strokeDasharray="4 3" />
                    <Tooltip contentStyle={{ background: UI.card, border: `1px solid ${UI.border}`, borderRadius: 8, fontSize: 12 }} />
                    <Line type="monotone" dataKey="moodScore" name="Mood score" stroke={UI.indigo} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
                <div style={{ fontSize: 10, color: UI.muted, marginTop: 2 }}>0 = strongly bearish · 50 = neutral · 100 = strongly bullish · engine/moodSynth.js cross-signal read · {pulseRange === 'all' ? 'din-wise (har din ka EOD)' : 'har 5 min ka data'}</div>
              </div>
            );
          })()}

          {/* CALL vs PUT OI build-up — the two sides that make up PCR, so you see
              WHICH side is accumulating, not just the ratio. The section stays
              visible with a placeholder until it has ≥2 points to draw a line. */}
          {pulseSeries && pulseSeries.length >= 1 && (() => {
            const hasData = pulseSeries.filter((p) => p.peOiL != null || p.ceOiL != null).length >= 2;
            const vals = (k) => pulseSeries.map((p) => p[k]).filter((v) => v != null);
            const dir = (k) => { const a = vals(k); if (a.length < 2) return null; const d = Math.round((a[a.length - 1] - a[0]) * 100) / 100; return { last: a[a.length - 1], delta: d, up: d > 0, flat: d === 0 }; };
            const ce = dir('ceOiL'), pe = dir('peOiL');
            const tag = (o) => o == null ? '' : `${o.up ? '▲' : o.flat ? '▬' : '▼'} ${o.up ? '+' : ''}${o.delta}L`;
            const CEC = UI.red, PEC = UI.green; // CE = call resistance wall, PE = put support wall
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderRadius: 10, padding: '12px 12px 6px', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>🏗️ CALL vs PUT OI — {pulseRange === 'all' ? 'ALL-TIME BUILD-UP' : 'DIN BHAR KA BUILD-UP'}</span>
                  {ce && <span style={{ fontSize: 12.5, fontWeight: 800, color: CEC }}>● Call OI {ce.last}L <span style={{ color: ce.up ? UI.red : ce.flat ? UI.muted : UI.green }}>{tag(ce)}</span></span>}
                  {pe && <span style={{ fontSize: 12.5, fontWeight: 800, color: PEC }}>● Put OI {pe.last}L <span style={{ color: pe.up ? UI.green : pe.flat ? UI.muted : UI.red }}>{tag(pe)}</span></span>}
                </div>
                {hasData ? (
                  <ResponsiveContainer width="100%" height={168}>
                    <LineChart data={pulseSeries} margin={{ top: 6, right: 10, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={UI.border} vertical={false} />
                      <XAxis dataKey="t" tick={{ fontSize: 10, fill: UI.muted }} interval="preserveStartEnd" minTickGap={24} />
                      <YAxis tick={{ fontSize: 10, fill: UI.muted }} domain={['auto', 'auto']} width={56} tickFormatter={(v) => `${Math.round(v)}L`} />
                      <Tooltip contentStyle={{ background: UI.card, border: `1px solid ${UI.border}`, borderRadius: 8, fontSize: 12 }} formatter={(v) => `${v} L`} />
                      <Line type="monotone" dataKey="ceOiL" name="Call OI" stroke={CEC} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                      <Line type="monotone" dataKey="peOiL" name="Put OI" stroke={PEC} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: 168, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: UI.muted, fontSize: 12, background: UI.bg, border: `1px dashed ${UI.border}`, borderRadius: 8, padding: '0 16px' }}>
                    Full-chain Call/Put OI data build ho raha hai — chart 2+ points ({pulseRange === 'all' ? 'har din ek EOD' : 'har 5 min ek'}) aane par draw hoga.
                  </div>
                )}
                <div style={{ fontSize: 10, color: UI.muted, marginTop: 2 }}>CE OI ↑ = call resistance strong (bearish) · PE OI ↑ = put support strong (bullish) · full-chain, lakh (L) me · PCR = PE÷CE · {pulseRange === 'all' ? 'din-wise (EOD)' : 'har 5 min'}</div>
                <InfoNote>
                  <b style={{ color: UI.text }}>Kya dikha raha hai:</b> Do lines — poori chain ka <b>total Call OI</b> (laal) aur <b>Put OI</b> (hara),
                  ATM ke ±2500 points tak, lakh contracts me, har 5 min refresh.
                  <div style={{ marginTop: 8 }}>
                    <b style={{ color: UI.text }}>PCR se alag kyun:</b> PCR ek <i>ratio</i> hai (PE ÷ CE) — wo chhupa deta hai ki hila kaun.
                    PCR 0.87 se 1.08 do bilkul alag wajah se ho sakta hai: Put OI badha (naya support ban raha) <i>ya</i> Call OI ghata
                    (call writers ne cover kar liya). Ratio dono me same dikhega, matlab alag hai. Ye chart wahi batata hai.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <b style={{ color: UI.text }}>Kaise padhein:</b> OI = abhi zinda contracts. Badhna = nayi positions ban rahi, ghatna = square ho rahi.
                    Index options me bulk <b>writers</b> (sellers) ka hota hai, isliye ulta padha jaata hai:
                    <div style={{ marginTop: 5, paddingLeft: 2 }}>
                      <div>• <b style={{ color: UI.red }}>Call OI ↑</b> — writers upar ke strikes bech rahe = "yahan se upar nahi jayega" = <b>resistance strong</b> = bearish</div>
                      <div>• <b style={{ color: UI.green }}>Put OI ↑</b> — writers neeche ke strikes bech rahe = "yahan se neeche nahi girega" = <b>support strong</b> = bullish</div>
                    </div>
                    Upar ka chip (jaise ▲ +285L) <b>din ki shuruaat se ab tak ka change</b> hai, total OI nahi — aur uska colour ulta flip hota hai,
                    kyunki Call OI ka badhna bearish news hai.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <b style={{ color: UI.text }}>Dhyan rakho:</b>
                    <div style={{ marginTop: 5, paddingLeft: 2 }}>
                      <div>• OI badhna hamesha writing nahi hoti — <b>buying se bhi OI badhta hai</b>. Price ke saath padho: OI↑ + price↑ = long build-up; OI↑ + price↓ = short build-up.</div>
                      <div>• <b>Expiry ke din OI dhah jaata hai</b> (settlement) — wo trend nahi, mechanics hai.</div>
                      <div>• Dono lines ek saath upar = dono taraf writing = range ban rahi, ek-tarfa dhakka nahi.</div>
                      <div>• <b>ALL-TIME</b> toggle pe har din ka sirf ek EOD point aata hai, intraday nahi.</div>
                    </div>
                  </div>
                </InfoNote>
              </div>
            );
          })()}

          {/* IV Skew intraday — puts richer (>0 = fear) vs calls richer (<0 = greed). */}
          {pulseSeries && pulseSeries.length >= 1 && (() => {
            const hasData = pulseSeries.filter((p) => p.ivSkew != null).length >= 2;
            const a = pulseSeries.map((p) => p.ivSkew).filter((v) => v != null);
            const sk = a.length >= 2 ? { last: a[a.length - 1], delta: Math.round((a[a.length - 1] - a[0]) * 10) / 10 } : (a.length === 1 ? { last: a[0], delta: null } : null);
            const up = sk && sk.delta != null && sk.delta > 0, flat = sk && sk.delta === 0;
            const zone = sk == null ? '' : sk.last > 1 ? 'FEAR (put skew)' : sk.last < -1 ? 'GREED (call skew)' : 'BALANCED';
            const zc = sk == null ? UI.muted : sk.last > 1 ? UI.red : sk.last < -1 ? UI.green : UI.muted;
            const SKC = '#d97706'; // amber line
            return (
              <div style={{ border: `1px solid ${UI.border}`, borderRadius: 10, padding: '12px 12px 6px', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>📐 IV SKEW — FEAR ↔ GREED</span>
                  {sk && <span style={{ fontSize: 12.5, fontWeight: 800, color: zc }}>● Skew {sk.last > 0 ? '+' : ''}{sk.last}% <span style={{ fontWeight: 700 }}>({zone})</span>{sk.delta != null ? <span style={{ color: up ? UI.red : flat ? UI.muted : UI.green }}> {up ? '▲' : flat ? '▬' : '▼'} {up ? '+' : ''}{sk.delta}</span> : null}</span>}
                </div>
                {hasData ? (
                  <ResponsiveContainer width="100%" height={168}>
                    <LineChart data={pulseSeries} margin={{ top: 6, right: 10, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={UI.border} vertical={false} />
                      <XAxis dataKey="t" tick={{ fontSize: 10, fill: UI.muted }} interval="preserveStartEnd" minTickGap={24} />
                      <YAxis tick={{ fontSize: 10, fill: UI.muted }} domain={['auto', 'auto']} width={44} tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`} />
                      <Tooltip contentStyle={{ background: UI.card, border: `1px solid ${UI.border}`, borderRadius: 8, fontSize: 12 }} formatter={(v) => `${v > 0 ? '+' : ''}${v}% (${v > 1 ? 'fear' : v < -1 ? 'greed' : 'balanced'})`} />
                      <ReferenceLine y={0} stroke={UI.muted} strokeDasharray="4 3" />
                      <ReferenceLine y={1} stroke={UI.red} strokeOpacity={0.35} strokeDasharray="2 4" />
                      <ReferenceLine y={-1} stroke={UI.green} strokeOpacity={0.35} strokeDasharray="2 4" />
                      <Line type="monotone" dataKey="ivSkew" name="IV Skew" stroke={SKC} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: 168, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: UI.muted, fontSize: 12, background: UI.bg, border: `1px dashed ${UI.border}`, borderRadius: 8, padding: '0 16px' }}>
                    IV Skew data build ho raha hai — chart 2+ points ({pulseRange === 'all' ? 'har din ek EOD' : 'har 5 min ek'}) aane par draw hoga.
                  </div>
                )}
                <div style={{ fontSize: 10, color: UI.muted, marginTop: 2 }}>0 ke <b>upar</b> = Put Skew (fear, hedging) · <b>neeche</b> = Call Skew (greed) · &gt;+1% ya &lt;−1% se meaningful · ATM Put IV − Call IV · {pulseRange === 'all' ? 'din-wise (EOD)' : 'har 5 min'}</div>
              </div>
            );
          })()}

          {/* What's happening */}
          {P.whatsHappening && (
            <div style={{ fontSize: 13.5, color: UI.text, lineHeight: 1.65, background: UI.bg, border: `1px solid ${UI.border}`, borderRadius: 10, padding: '11px 14px', marginBottom: 14 }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: UI.muted, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>ABHI KYA CHAL RAHA HAI</span>
              {P.whatsHappening}
            </div>
          )}

          {/* Positives vs Negatives */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 12, marginBottom: (P.newsHighlights && P.newsHighlights.length) || (P.watch && P.watch.length) ? 14 : 0 }}>
            <div style={{ border: `1px solid ${UI.border}`, borderLeft: `3px solid ${UI.green}`, borderRadius: 10, padding: '11px 14px' }}>
              <div style={{ fontSize: 11.5, fontWeight: 900, color: UI.green, marginBottom: 8 }}>✅ POSITIVE</div>
              <ul style={{ margin: 0, paddingLeft: 17, fontSize: 12.5, color: UI.text, lineHeight: 1.7 }}>
                {(P.positives || []).map((x, i) => <li key={i}>{x}</li>)}
                {!(P.positives || []).length && <li style={{ color: UI.muted, listStyle: 'none', marginLeft: -17 }}>—</li>}
              </ul>
            </div>
            <div style={{ border: `1px solid ${UI.border}`, borderLeft: `3px solid ${UI.red}`, borderRadius: 10, padding: '11px 14px' }}>
              <div style={{ fontSize: 11.5, fontWeight: 900, color: UI.red, marginBottom: 8 }}>⛔ NEGATIVE / RISK</div>
              <ul style={{ margin: 0, paddingLeft: 17, fontSize: 12.5, color: UI.text, lineHeight: 1.7 }}>
                {(P.negatives || []).map((x, i) => <li key={i}>{x}</li>)}
                {!(P.negatives || []).length && <li style={{ color: UI.muted, listStyle: 'none', marginLeft: -17 }}>—</li>}
              </ul>
            </div>
          </div>

          {/* News & events — rich categorised digest (geopolitical / macro /
              corporate / economic / leader-tweets), with the LLM's curated
              newsHighlights as a fallback when the categorised digest is absent. */}
          {(() => {
            const nd = pulse.newsDigest;
            const hasNd = nd && nd.total > 0;
            const hasHl = P.newsHighlights && P.newsHighlights.length > 0;
            if (!hasNd && !hasHl) return null;
            // Two independent encodings, deliberately kept apart:
            //   DIRECTION (which way it pushes NIFTY) owns colour — a diverging
            //     red↔grey↔green, the sign a trader already reads by instinct.
            //   IMPACT (how much) is a countable mark, not a second colour ramp.
            // The old card spent red on high-impact, so a red badge meant "big"
            // in one place and "bad" in another. Colour now means exactly one
            // thing. Every direction also carries an icon + word, so nothing is
            // ever encoded by colour alone (the red/green pair sits at CVD ΔE 8.1
            // — safe only because of that second channel).
            const DIR = {
              bearish: { c: UI.red, icon: '📉', label: 'BEARISH' },
              bullish: { c: UI.green, icon: '📈', label: 'BULLISH' },
              neutral: { c: UI.muted, icon: '⚖️', label: 'NEUTRAL' },
            };
            const dirOf = (n) => DIR[n.direction] || DIR.neutral;
            const dots = (imp) => (imp === 'high' || imp === 'critical' ? '●●●' : imp === 'medium' ? '●●○' : '●○○');
            const cats = [
              { key: 'geopolitical', icon: '🌍', label: 'GLOBAL & GEOPOLITICAL' },
              { key: 'macro', icon: '📅', label: 'MACRO DATA CALENDAR' },
              { key: 'corporate', icon: '🏢', label: 'CORPORATE' },
              { key: 'economic', icon: '💰', label: 'ECONOMIC & POLICY' },
              { key: 'leaders', icon: '🐦', label: 'LEADERS / TWEETS' },
            ];
            const all = hasNd ? cats.flatMap((c) => (nd[c.key] || []).map((n) => ({ ...n, cat: c }))) : [];
            const cnt = { bearish: 0, neutral: 0, bullish: 0 };
            all.forEach((n) => { cnt[n.direction] = (cnt[n.direction] || 0) + 1; });
            const tot = all.length;
            // Lean is impact-WEIGHTED: one high-impact war headline outranks three
            // low-impact green ones, which a raw count would hide.
            const wt = (n) => (n.impact === 'high' || n.impact === 'critical' ? 3 : n.impact === 'medium' ? 2 : 1);
            const lean = all.reduce((s, n) => s + (n.direction === 'bullish' ? wt(n) : n.direction === 'bearish' ? -wt(n) : 0), 0);
            const leanTag = lean > 2 ? { t: 'BULLISH-LEANING', ...DIR.bullish } : lean < -2 ? { t: 'BEARISH-LEANING', ...DIR.bearish } : { t: 'MIXED / NO CLEAR LEAN', ...DIR.neutral };
            const hiCount = all.filter((n) => n.impact === 'high' || n.impact === 'critical').length;

            const row = (n, i) => {
              const d = dirOf(n);
              // Clamp rather than truncate: full text stays in the DOM (and the
              // title attr) so nothing is lost, but every card keeps one height
              // and the grid stays on a rhythm.
              const clamp = (n) => ({ display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden' });
              return (
                <div key={i} style={{ border: `1px solid ${UI.border}`, borderTop: `3px solid ${d.c}`, borderRadius: 9, padding: '9px 11px', display: 'flex', flexDirection: 'column', gap: 4, background: UI.card }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span title={`${d.label} for NIFTY`} style={{ fontSize: 8, fontWeight: 900, color: '#fff', background: d.c, borderRadius: 4, padding: '2px 5px', whiteSpace: 'nowrap' }}>{d.icon} {d.label}</span>
                    <span title={`Impact: ${n.impact}`} style={{ color: UI.muted, fontSize: 8.5, letterSpacing: 1 }}>{dots(n.impact)}</span>
                    {n.time && <span style={{ fontSize: 9, fontWeight: 800, color: UI.sub, background: UI.bg, borderRadius: 5, padding: '1px 5px' }}>⏰ {n.time}</span>}
                    <span style={{ marginLeft: 'auto', fontSize: 9, color: UI.muted, whiteSpace: 'nowrap' }}>{n.ageMin != null ? (n.ageMin < 1 ? 'abhi' : `${n.ageMin}m`) : ''}</span>
                  </div>
                  <div title={n.title} style={{ fontSize: 12, fontWeight: 800, color: UI.text, lineHeight: 1.35, ...clamp(2) }}>
                    {n.url ? <a href={n.url} target="_blank" rel="noreferrer" style={{ color: UI.text, textDecoration: 'none' }}>{n.title}</a> : n.title}
                  </div>
                  {n.why && <div title={n.why} style={{ fontSize: 11, color: UI.sub, lineHeight: 1.4, ...clamp(2) }}>{n.why}</div>}
                  <div style={{ fontSize: 9.5, color: UI.muted, marginTop: 'auto', paddingTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {n.cat ? `${n.cat.icon} ` : ''}{n.source}
                  </div>
                </div>
              );
            };
            // Diverging stacked bar: segment width = share of stories. Only
            // non-empty segments exist, so the rounded ends and the 2px surface
            // gaps land on what's actually drawn rather than on a zero-width slot.
            const order = ['bearish', 'neutral', 'bullish'];
            const shown = order.filter((k) => cnt[k] > 0);
            const seg = (k, i) => (
              <div key={k} title={`${cnt[k]} ${DIR[k].label.toLowerCase()}`} style={{
                width: `${(cnt[k] / tot) * 100}%`, background: DIR[k].c, height: '100%',
                borderTopLeftRadius: i === 0 ? 4 : 0, borderBottomLeftRadius: i === 0 ? 4 : 0,
                borderTopRightRadius: i === shown.length - 1 ? 4 : 0, borderBottomRightRadius: i === shown.length - 1 ? 4 : 0,
                marginRight: i < shown.length - 1 ? 2 : 0,
              }} />
            );
            return (
              <div style={{ marginBottom: (P.watch && P.watch.length) ? 14 : 0 }}>
                <div style={lbl}>📰 NEWS &amp; EVENTS — MARKET KO KYA HILA RAHA HAI</div>

                {hasNd && tot > 0 && (
                  <div style={{ border: `1px solid ${UI.border}`, borderRadius: 10, padding: '11px 13px', marginBottom: 12, background: UI.bg }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 900, color: UI.muted, letterSpacing: '0.05em' }}>AAJ KI KHABRON KA JHUKAAV</span>
                      <span style={{ fontSize: 13.5, fontWeight: 900, color: leanTag.c }}>{leanTag.icon} {leanTag.t}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 10.5, color: UI.muted, fontWeight: 700 }}>
                        {tot} khabrein{hiCount ? ` · ${hiCount} high-impact` : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', height: 10, marginBottom: 7 }}>{shown.map(seg)}</div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {order.map((k) => cnt[k] ? (
                        <span key={k} style={{ fontSize: 11.5, fontWeight: 800, color: UI.sub }}>
                          <span style={{ color: DIR[k].c }}>{DIR[k].icon}</span> {cnt[k]} {DIR[k].label.toLowerCase()}
                        </span>
                      ) : null)}
                    </div>
                  </div>
                )}

                {hasNd && nd.summary && (
                  <div style={{ fontSize: 12.5, color: UI.text, lineHeight: 1.6, background: UI.bg, border: `1px solid ${UI.border}`, borderRadius: 9, padding: '9px 12px', marginBottom: 12 }}>{nd.summary}</div>
                )}

                {/* One filterable grid, not five stacked lists.
                    Five headed sections of full-width rows ran ~28 rows deep and
                    printed the top stories twice — once as a "front page", once
                    again inside their bucket. Sorting by impact does that job
                    without the duplicate, category becomes a filter instead of a
                    heading, and the cards tile across the width the rows wasted. */}
                {hasNd ? (() => {
                  const rank = { high: 0, critical: 0, medium: 1, low: 2 };
                  const sorted = [...all].sort((a, b) =>
                    (rank[a.impact] ?? 3) - (rank[b.impact] ?? 3) ||
                    (a.direction === 'neutral' ? 1 : 0) - (b.direction === 'neutral' ? 1 : 0) ||
                    (a.ageMin ?? 999) - (b.ageMin ?? 999));
                  const tabs = [{ key: 'all', icon: '🔥', label: 'SAB', n: sorted.length },
                    ...cats.map((c) => ({ key: c.key, icon: c.icon, label: c.label.split(' ')[0], n: (nd[c.key] || []).length })).filter((t) => t.n)];
                  const active = tabs.some((t) => t.key === newsCat) ? newsCat : 'all';
                  const items = active === 'all' ? sorted : sorted.filter((n) => n.cat && n.cat.key === active);
                  return (
                    <>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 9 }}>
                        {tabs.map((t) => {
                          const on = t.key === active;
                          return (
                            <button key={t.key} onClick={() => setNewsCat(t.key)} style={{
                              cursor: 'pointer', border: `1px solid ${on ? UI.indigo : UI.border}`,
                              background: on ? UI.indigo : UI.bg, color: on ? '#fff' : UI.sub,
                              borderRadius: 7, padding: '4px 9px', fontSize: 10.5, fontWeight: 900, letterSpacing: '0.02em',
                            }}>{t.icon} {t.label} <span style={{ opacity: 0.75 }}>{t.n}</span></button>
                          );
                        })}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 8, alignItems: 'stretch' }}>
                        {items.map(row)}
                      </div>
                    </>
                  );
                })() : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 8 }}>{P.newsHighlights.map(row)}</div>
                )}
              </div>
            );
          })()}

          {/* Watch + one-liner */}
          {P.watch && P.watch.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center' }}>
              <span style={{ fontSize: 10, fontWeight: 900, color: UI.muted, letterSpacing: '0.06em' }}>👀 WATCH</span>
              {P.watch.map((w, i) => <span key={i} style={{ fontSize: 11.5, fontWeight: 700, color: UI.text, background: UI.bg, border: `1px solid ${UI.border}`, borderRadius: 7, padding: '4px 9px' }}>{w}</span>)}
            </div>
          )}

          <div style={{ fontSize: 10.5, color: UI.muted, marginTop: 14, lineHeight: 1.5 }}>
            {P.oneLiner ? <span style={{ fontWeight: 800, color: UI.sub }}>💡 {P.oneLiner}<br /></span> : null}
            ℹ️ AI decision-support · har 5 min auto-update · trade se pehle apna analysis karo.
          </div>
        </div>
      </div>
    );
  })();

  if (loading && !d) return <Layout>{head}{moodCard}<div style={{ textAlign: 'center', padding: 60, color: UI.muted }}>Analysing option chain…</div></Layout>;
  if (err) return <Layout>{head}{moodSummaryCard}{moodCard}<div style={card({ padding: 50, textAlign: 'center' })}><div style={{ fontSize: 44, marginBottom: 12 }}>🔌</div><div style={{ fontSize: 16, fontWeight: 800, color: UI.text, marginBottom: 6 }}>{err}</div><a href="/broker-manage" style={{ background: UI.blue, color: '#fff', padding: '10px 22px', borderRadius: 10, textDecoration: 'none', fontSize: 13, fontWeight: 700 }}>Connect Broker</a></div></Layout>;
  // Broker option-chain analysis unavailable (null without an explicit error) —
  // still show the mood card (it's broker-independent), not a blank page.
  if (!d) return <Layout>{head}{moodSummaryCard}{moodCard}{!moodCard && !moodSummaryCard && <div style={{ textAlign: 'center', padding: 60, color: UI.muted }}>Loading…</div>}</Layout>;

  const Stat = ({ label, value, sub, color }) => (
    <div style={card({ padding: 18 })}>
      <div style={lbl}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 900, color: color || UI.text }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: UI.muted, marginTop: 4, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
  const sitBg = d.riskLevel === 'HIGH' ? 'linear-gradient(135deg,#dc2626,#ef4444)' : d.riskLevel === 'MODERATE' ? 'linear-gradient(135deg,#d97706,#f59e0b)' : 'linear-gradient(135deg,#059669,#10b981)';
  const riskTag = (r) => r === 'High' ? UI.red : r === 'Moderate' ? '#d97706' : UI.green;

  return (
    <Layout>
      {head}

      {/* AAJ KA MOOD — every section of this page reduced to one probability split */}
      {moodSummaryCard}

      {/* LIVE MARKET MOOD — AI pulse, top of page, auto-refreshed every 30 min */}
      {moodCard}

      {/* ROW 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div style={{ ...card({ padding: 18 }), background: sitBg, color: '#fff', border: 'none' }}>
          <div style={{ fontSize: 10.5, opacity: 0.85, fontWeight: 700, letterSpacing: '0.06em' }}>TODAY'S MARKET SITUATION</div>
          <div style={{ fontSize: 22, fontWeight: 900, marginTop: 8 }}>{d.riskLevel} RISK DAY</div>
          <div style={{ fontSize: 12, opacity: 0.92, marginTop: 6 }}>{d.sentiment} bias · {d.daysToExpiry}D to expiry</div>
        </div>
        <Stat label="NIFTY SPOT" value={fmt(d.spot)} sub="Live" color={UI.blue} />
        <Stat label="INDIA VIX" value={fmt(d.vix)} sub={d.vix >= 18 ? 'High volatility' : d.vix >= 13 ? 'Elevated' : 'Calm'} />
        <Stat label="PCR (OI)" value={d.pcr} sub={`${d.pcr >= 1.2 ? 'Put-heavy' : d.pcr <= 0.8 ? 'Call-heavy' : 'Neutral'}${d.pcrVolume != null ? ` · Vol ${d.pcrVolume}` : ''}`} />
        <Stat label="DAYS TO EXPIRY" value={d.daysToExpiry} sub={fmtDate(d.expiry)} />
      </div>

      {/* AAJ KE EVENTS & DRIVERS — web se live calendar + market-movers */}
      {dg && (dg.events?.length > 0 || dg.marketMovers?.length > 0 || dg.summary) && (
        <div style={card({ padding: 18, marginBottom: 14 })}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 900, color: UI.text }}>🗓️ AAJ KE EVENTS & DRIVERS</span>
            <span style={{ fontSize: 10, fontWeight: 800, color: UI.blue, background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.12)' : '#e0f2fe', borderRadius: 6, padding: '2px 8px' }}>LIVE · web</span>
          </div>
          {dg.summary && (
            <div style={{ fontSize: 13, color: UI.text, lineHeight: 1.6, marginBottom: 14, background: UI.bg, border: `1px solid ${UI.border}`, borderRadius: 10, padding: '10px 13px' }}>{dg.summary}</div>
          )}
          {dg.events?.length > 0 && (
            <div style={{ display: 'grid', gap: 8, marginBottom: dg.marketMovers?.length ? 14 : 0 }}>
              {[...dg.events].sort((a, b) => impactRank(b.impact) - impactRank(a.impact)).map((e, i) => {
                const ic = { high: { c: UI.red, t: 'HIGH' }, medium: { c: '#f59e0b', t: 'MID' }, low: { c: UI.muted, t: 'LOW' } }[e.impact] || { c: UI.muted, t: '' };
                return (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', border: `1px solid ${UI.border}`, borderLeft: `3px solid ${ic.c}`, borderRadius: 10, padding: '9px 12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56 }}>
                      <span style={{ fontSize: 9, fontWeight: 900, color: '#fff', background: ic.c, borderRadius: 5, padding: '1px 6px' }}>{ic.t}</span>
                      <span style={{ fontSize: 10, color: UI.muted, marginTop: 3, textAlign: 'center', lineHeight: 1.2 }}>{e.time}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: UI.text }}>
                        {e.title}
                        {e.region && <span style={{ fontSize: 9.5, fontWeight: 800, color: UI.sub, background: UI.bg, borderRadius: 5, padding: '1px 6px', marginLeft: 7 }}>{e.region}</span>}
                      </div>
                      {e.why && <div style={{ fontSize: 12, color: UI.sub, marginTop: 2 }}>{e.why}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {dg.marketMovers?.length > 0 && (
            <>
              <div style={lbl}>ABHI KE MARKET MOVERS</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: UI.sub, lineHeight: 1.7 }}>
                {dg.marketMovers.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      {/* GLOBAL INDICES */}
      {((d.globalCues && d.globalCues.length) || d.gift) && (
        <div style={card({ padding: 18, marginBottom: 14 })}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={lbl}>GLOBAL INDICES <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>(pre-market cues)</span></div>
            <span style={{ fontSize: 11, color: UI.muted, fontWeight: 600 }}>🌐 Drives today's {d.sentiment} bias</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${(d.gift ? 1 : 0) + (d.globalCues ? d.globalCues.length : 0)}, 1fr)`, gap: 12 }}>
            {d.gift && (() => {
              const up = d.gift.changePct >= 0; const c = up ? UI.green : UI.red;
              return (
                <div style={{ border: `1px solid ${UI.border}`, borderRadius: 12, padding: 14, borderTop: `3px solid ${UI.indigo}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: UI.text }}>GIFT NIFTY</span>
                    <span style={{ fontSize: 9.5, fontWeight: 800, color: UI.indigo, background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.10)' : '#eef2ff', borderRadius: 5, padding: '2px 6px' }}>NSE IX</span>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: UI.text }}>{fmt(d.gift.value)}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: c, marginTop: 3 }}>{up ? '▲' : '▼'} {fmt(Math.abs(d.gift.change))} ({(up ? '+' : '') + d.gift.changePct.toFixed(2)}%)</div>
                </div>
              );
            })()}
            {(d.globalCues || []).map((g, i) => {
              const up = g.changePct >= 0; const c = up ? UI.green : UI.red;
              return (
                <div key={i} style={{ border: `1px solid ${UI.border}`, borderRadius: 12, padding: 14, borderTop: `3px solid ${c}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: UI.text }}>{g.name}</span>
                    <span style={{ fontSize: 9.5, fontWeight: 800, color: UI.sub, background: UI.bg, borderRadius: 5, padding: '2px 6px' }}>{g.region}</span>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: UI.text }}>{fmt(g.price)}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: c, marginTop: 3 }}>{up ? '▲' : '▼'} {fmt(Math.abs(g.change))} ({(up ? '+' : '') + g.changePct.toFixed(2)}%)</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ROW 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.4fr', gap: 14, marginBottom: 14 }}>
        {/* Risk-O-Meter */}
        <div style={card({ padding: 18 })}>
          <div style={lbl}>RISK-O-METER</div>
          <div style={{ position: 'relative' }}>
            <ResponsiveContainer width="100%" height={140}>
              <RadialBarChart innerRadius="74%" outerRadius="100%" data={[{ value: d.riskScore, fill: riskCol(d.riskLevel) }]} startAngle={180} endAngle={0}>
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar background={{ fill: '#eef1f6' }} dataKey="value" cornerRadius={8} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div style={{ position: 'absolute', top: '60%', left: 0, right: 0, textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 900, color: riskCol(d.riskLevel) }}>{d.riskScore}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: UI.sub, letterSpacing: '0.05em' }}>{d.riskLevel}</div>
            </div>
          </div>
        </div>
        {/* Strategy Suggester */}
        <div style={card({ padding: 18 })}>
          <div style={lbl}>STRATEGY SUGGESTER</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: UI.text, marginBottom: 8 }}>💡 {d.strategy.name}</div>
          <div style={{ fontSize: 12.5, color: UI.sub, lineHeight: 1.55, marginBottom: 12 }}>{d.strategy.why}</div>
          <span style={{ fontSize: 11.5, fontWeight: 800, padding: '4px 12px', borderRadius: 20, background: riskTag(d.strategy.risk) + '22', color: riskTag(d.strategy.risk) }}>{d.strategy.risk} Risk</span>
        </div>
        {/* Market Signals */}
        <div style={card({ padding: 18 })}>
          <div style={lbl}>MARKET SIGNALS</div>
          {d.signals.map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < d.signals.length - 1 ? `1px solid ${UI.border}` : 'none' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: UI.text }}>{s.name}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: UI.muted, fontWeight: 600 }}>{s.value}</span>
                <span style={{ fontSize: 11, fontWeight: 800, padding: '3px 9px', borderRadius: 6, background: tone[s.tone] + '1f', color: tone[s.tone] }}>{s.signal}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ROW 3 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1fr', gap: 14, marginBottom: 14 }}>
        {/* Volatility Analysis */}
        <div style={card({ padding: 18 })}>
          <div style={lbl}>VOLATILITY ANALYSIS</div>
          <div style={{ fontSize: 11.5, color: UI.sub, fontWeight: 700, marginBottom: 4 }}>IV Percentile <span style={{ float: 'right', color: UI.text, fontWeight: 800 }}>{d.iv.ivPercentile != null ? d.iv.ivPercentile + '%' : '—'}</span></div>
          <div style={{ height: 8, borderRadius: 5, background: IS_CRYPTO_PORTAL ? '#1e2329' : '#eef1f6', overflow: 'hidden', marginBottom: 14 }}>
            <div style={{ width: (d.iv.ivPercentile || 0) + '%', height: '100%', background: 'linear-gradient(90deg,#10b981,#f59e0b,#ef4444)' }} />
          </div>
          {[['ATM Call IV', d.iv.atmCeIv != null ? d.iv.atmCeIv + '%' : '—'], ['ATM Put IV', d.iv.atmPeIv != null ? d.iv.atmPeIv + '%' : '—'], ['IV Skew (PE-CE)', d.iv.ivSkew != null ? (d.iv.ivSkew >= 0 ? '+' : '') + d.iv.ivSkew + '%' : '—']].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${UI.border}`, fontSize: 12.5 }}>
              <span style={{ color: UI.sub, fontWeight: 600 }}>{k}</span><span style={{ color: UI.text, fontWeight: 800 }}>{v}</span>
            </div>
          ))}
        </div>
        {/* Recommended Strikes */}
        <div style={card({ padding: 18 })}>
          <div style={lbl}>RECOMMENDED STRIKES <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>(~0.30 delta)</span></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['CE', d.recommended.ce, UI.green], ['PE', d.recommended.pe, UI.red]].map(([t, o, c]) => (
              <div key={t} style={{ border: `1px solid ${UI.border}`, borderRadius: 12, padding: 14, borderTop: `3px solid ${c}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: UI.text }}>{o ? o.strike : '—'} {t}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: c }}>SELL</span>
                </div>
                {o ? [['Premium', '₹' + o.premium], ['Delta', o.delta], ['IV', o.iv + '%'], ['Theta', o.theta]].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12 }}>
                    <span style={{ color: UI.muted }}>{k}</span><span style={{ color: UI.text, fontWeight: 700 }}>{v}</span>
                  </div>
                )) : <div style={{ fontSize: 12, color: UI.muted }}>No match</div>}
              </div>
            ))}
          </div>
        </div>
        {/* Pre-Trade Checklist */}
        <div style={card({ padding: 18 })}>
          <div style={lbl}>PRE-TRADE CHECKLIST</div>
          {d.checklist.map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
              <span style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', background: c.ok === true ? UI.green : c.ok === false ? UI.red : '#cbd5e1' }}>{c.ok === true ? '✓' : c.ok === false ? '✕' : '?'}</span>
              <span style={{ fontSize: 12.5, color: UI.text, fontWeight: 600 }}>{c.label}{c.note ? ` (${c.note})` : ''}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ROW 4 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 0.8fr', gap: 14, marginBottom: 20 }}>
        {/* OI Distribution */}
        <div style={card({ padding: 18 })}>
          <div style={lbl}>OI DISTRIBUTION <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>(in '000)</span></div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={d.oiDistribution} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
              <XAxis dataKey="strike" tick={{ fontSize: 9, fill: UI.muted }} interval={2} />
              <YAxis tick={{ fontSize: 9, fill: UI.muted }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="ceOi" name="Call OI" fill="#ef4444" radius={[3, 3, 0, 0]} />
              <Bar dataKey="peOi" name="Put OI" fill="#10b981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 11, color: UI.sub, fontWeight: 600 }}>
            <span>🟥 Call OI (resistance)</span><span>🟩 Put OI (support)</span>
          </div>
        </div>
        {/* Theta Decay */}
        <div style={card({ padding: 18 })}>
          <div style={lbl}>THETA DECAY <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>(ATM straddle)</span></div>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={d.thetaDecay} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: UI.muted }} />
              <YAxis tick={{ fontSize: 9, fill: UI.muted }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Line type="monotone" dataKey="value" name="Straddle ₹" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: UI.muted, textAlign: 'center' }}>Premium erosion till expiry</div>
        </div>
        {/* Max Pain */}
        <div style={card({ padding: 18 })}>
          <div style={lbl}>MAX PAIN</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: UI.indigo, marginTop: 8 }}>{d.maxPain}</div>
          <div style={{ fontSize: 12.5, color: UI.sub, marginTop: 8, lineHeight: 1.5 }}>
            Spot is <b style={{ color: d.spot > d.maxPain ? UI.red : UI.green }}>{Math.abs(d.spot - d.maxPain).toFixed(0)} pts {d.spot > d.maxPain ? 'above' : 'below'}</b> max pain.
          </div>
          <div style={{ fontSize: 11.5, color: UI.muted, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${UI.border}` }}>
            Options often gravitate toward this strike near expiry.
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: UI.muted }}>Spot</span><span style={{ fontWeight: 800, color: UI.text }}>{fmt(d.spot)}</span>
          </div>
        </div>
      </div>

      {/* MARKET CALENDAR — F&O expiries + trading holidays */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
        {/* Events / Expiries */}
        <div style={card({ padding: 18 })}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 17 }}>📅</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: UI.text }}>Market Events</span>
          </div>
          {(cal?.expiries || []).length === 0 ? (
            <div style={{ color: UI.muted, fontSize: 12.5 }}>Connect a broker to see upcoming F&O expiries.</div>
          ) : cal.expiries.map((e, i) => {
            const monthly = /Monthly/i.test(e.label);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < cal.expiries.length - 1 ? `1px solid ${UI.border}` : 'none' }}>
                <div style={{ width: 44, textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 900, color: monthly ? UI.indigo : UI.text, lineHeight: 1 }}>{new Date(e.date).getDate()}</div>
                  <div style={{ fontSize: 10, color: UI.muted, fontWeight: 700, textTransform: 'uppercase' }}>{new Date(e.date).toLocaleDateString('en-IN', { month: 'short' })}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: UI.text }}>{e.label}</div>
                  <div style={{ fontSize: 11, color: UI.muted }}>{fmtEventDate(e.date)}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: UI.sub, background: UI.bg, borderRadius: 6, padding: '3px 8px' }}>{daysTo(e.date) <= 0 ? 'Today' : 'in ' + daysTo(e.date) + 'd'}</span>
              </div>
            );
          })}
        </div>

        {/* Trading holidays */}
        <div style={card({ padding: 18 })}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 17 }}>🏖️</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: UI.text }}>Trading Holidays</span>
            <span style={{ fontSize: 11, color: UI.muted }}>NSE</span>
          </div>
          {(cal?.holidays || []).length === 0 ? (
            <div style={{ color: UI.muted, fontSize: 12.5 }}>No upcoming holidays found.</div>
          ) : cal.holidays.slice(0, 10).map((h, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: i < Math.min(cal.holidays.length, 10) - 1 ? `1px solid ${UI.border}` : 'none' }}>
              <div style={{ width: 44, textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: UI.red, lineHeight: 1 }}>{new Date(h.date).getDate()}</div>
                <div style={{ fontSize: 10, color: UI.muted, fontWeight: 700, textTransform: 'uppercase' }}>{new Date(h.date).toLocaleDateString('en-IN', { month: 'short' })}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: UI.text }}>{h.label}</div>
                <div style={{ fontSize: 11, color: UI.muted }}>{fmtEventDate(h.date)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 12, color: UI.muted, textAlign: 'right' }}>
        Updated {d.updatedAt ? new Date(d.updatedAt).toLocaleTimeString('en-IN') : '—'} · auto-refresh 30s
      </div>
    </Layout>
  );
}

// ── BROKER ──────────────────────────────────────────
// Delta Exchange (crypto) broker connect — shown on the crypto portal instead of
// the Zerodha/Upstox flow. Delta uses a static API key + secret (no daily login),
// so connecting is a one-time validate-and-store via POST /brokers/delta/connect.
function CryptoBrokerView({ brokers, reload }) {
  const [form, setForm] = React.useState({ name: 'Delta Exchange', apiKey: '', apiSecret: '' });
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const deltaBrokers = (brokers || []).filter(isDeltaBroker);

  const connect = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(API + '/brokers/delta/connect', {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Connect failed');
      setMsg({ ok: true, text: '✅ Connected to Delta Exchange' });
      setForm(f => ({ ...f, apiKey: '', apiSecret: '' }));
      reload && reload();
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };
  const disconnect = async (id) => {
    if (!window.confirm('Disconnect this Delta account?')) return;
    await fetch(API + '/brokers/' + id, { method: 'DELETE', headers: getHeaders() });
    reload && reload();
  };

  const fld = { width: '100%', padding: '11px 13px', fontSize: 14, border: `1px solid ${UI.border}`, borderRadius: 10, background: UI.card, color: UI.text, boxSizing: 'border-box', outline: 'none' };
  return (
    <div className="ar-main" style={{ flex: 1, padding: 24, marginLeft: 230 }}>
      <PageHead icon="₿" title="Delta Exchange" subtitle="Connect your Delta (crypto) account with an API key + secret — no daily login needed." />
      {deltaBrokers.length > 0 && (
        <div style={card({ padding: 18, marginBottom: 18 })}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 12, color: UI.text }}>Connected accounts</div>
          {deltaBrokers.map(b => (
            <div key={b._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: `1px solid ${UI.border}`, borderRadius: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: b.connected ? UI.green : UI.red }} />
                <div><div style={{ fontWeight: 700, color: UI.text }}>{b.name}</div><div style={{ fontSize: 12, color: UI.muted }}>{b.connected ? 'Connected' : 'Disconnected'} · key …{String(b.clientId || '').slice(-6)}</div></div>
              </div>
              <button onClick={() => disconnect(b._id)} style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${UI.red}`, background: 'transparent', color: UI.red, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Disconnect</button>
            </div>
          ))}
        </div>
      )}
      <div style={card({ padding: 22, maxWidth: 560 })}>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: UI.text }}>{deltaBrokers.length ? 'Reconnect / update keys' : 'Connect Delta Exchange'}</div>
        <div style={{ fontSize: 12.5, color: UI.muted, marginBottom: 16 }}>Create an API key at delta.exchange → Account → API Keys. Enable trading + <b>whitelist this server's IP</b>.</div>
        {['name', 'apiKey', 'apiSecret'].map(k => (
          <div key={k} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: UI.sub, marginBottom: 5 }}>{k === 'apiKey' ? 'API Key' : k === 'apiSecret' ? 'API Secret' : 'Label'}</div>
            <input style={fld} type={k === 'apiSecret' ? 'password' : 'text'} value={form[k]} placeholder={k === 'name' ? 'Delta Exchange' : ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
          </div>
        ))}
        {msg && <div style={{ fontSize: 13, fontWeight: 600, color: msg.ok ? UI.green : UI.red, margin: '4px 0 12px' }}>{msg.text}</div>}
        <button onClick={connect} disabled={busy || !form.apiKey || !form.apiSecret} style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: (busy || !form.apiKey || !form.apiSecret) ? UI.muted : UI.blue, color: '#fff', fontWeight: 800, fontSize: 14, cursor: busy ? 'wait' : 'pointer' }}>{busy ? 'Validating…' : 'Connect'}</button>
      </div>
    </div>
  );
}

function Broker() {
  const [brokers, setBrokers] = React.useState([]);
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({ name: 'Zerodha', clientId: '', apiKey: '', apiSecret: '' });
  const [connecting, setConnecting] = React.useState(null);
  // Feedback banner for the per-broker Upstox OAuth bounce-back (?upstox=ok/fail).
  const [upMsg, setUpMsg] = React.useState('');
  // Daily auto-login modal (per broker).
  const [autoFor, setAutoFor] = React.useState(null); // broker being edited
  const [autoForm, setAutoForm] = React.useState({ password: '', totpSecret: '', enabled: false });
  const [autoBusy, setAutoBusy] = React.useState(false);
  const [autoMsg, setAutoMsg] = React.useState('');
  // Edit-credentials modal (per broker).
  const [editFor, setEditFor] = React.useState(null); // broker being edited
  const [editForm, setEditForm] = React.useState({ name: '', clientId: '', apiKey: '', apiSecret: '', proxyUrl: '' });
  const [editBusy, setEditBusy] = React.useState(false);
  const [editMsg, setEditMsg] = React.useState('');

  const fetchBrokers = () => {
    fetch(API + '/brokers', { headers: getHeaders() }).then(r => r.json()).then(d => { Array.isArray(d) && setBrokers(d); }).catch(() => {});
  };

  // The Zerodha app's redirect URL points here (/broker-manage) rather than the
  // dedicated /broker capture route, so Kite hands back ?request_token=… on this
  // page. Without this, the token is silently dropped — the page just looks like
  // it "reloaded" and the broker never connects. Forward to /broker (preserving
  // the query) so the existing BrokerCallback exchange flow runs.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('request_token')) {
      window.location.replace('/broker' + window.location.search);
      return;
    }
    // Upstox OAuth callback bounce-back flag.
    const up = params.get('upstox');
    if (up) {
      setUpMsg(up === 'ok' ? '✅ Upstox connected' : '❌ Upstox connection failed');
      window.history.replaceState({}, '', '/broker-manage');
    }
    fetchBrokers();
  }, []);

  const connectZerodha = async (broker) => {
    setConnecting(broker._id);
    try {
      const res = await fetch(API + '/kite/login-url?brokerId=' + broker._id, { headers: getHeaders() });
      const d = await res.json();
      if (d.loginUrl) {
        localStorage.setItem('zerodha_broker_id', broker._id);
        window.location.href = d.loginUrl;
      } else {
        alert('Could not get login URL: ' + (d.message || 'Unknown error'));
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setConnecting(null);
  };

  // Connect a user's OWN Upstox account for live trading (per-broker OAuth).
  // The backend signs the brokerId into `state`, so the public callback knows
  // which broker doc to attach the token to; it redirects back with ?upstox=ok.
  const connectUpstoxBroker = async (broker) => {
    setConnecting(broker._id);
    try {
      const res = await fetch(API + '/upstox/broker/login-url?brokerId=' + broker._id, { headers: getHeaders() });
      const d = await res.json();
      if (d.url) window.location.href = d.url;
      else alert('Could not get Upstox login URL: ' + (d.message || 'Unknown error'));
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setConnecting(null);
  };

  // Brokers connect via OAuth: Upstox uses its own per-broker flow above.
  const isUpstoxBroker = (b) => /upstox/i.test(b.name || '');
  // Shoonya has no OAuth redirect button — "connecting" IS the auto-login
  // modal (headless Playwright login using password + TOTP). See
  // engine/shoonyaAutoLogin.js on the backend.
  const isShoonyaBroker = (b) => b.provider === 'shoonya' || /shoonya|finvasia/i.test(b.name || '');

  // Per-broker branding: logo glyph + colors, so the list reads at a glance
  // instead of every card looking identical. Falls back to Zerodha's mark for
  // anything unrecognised (kept last so upstox/shoonya are checked first).
  const BROKER_BRAND = [
    { match: isUpstoxBroker, label: 'Upstox', glyph: 'U', fg: '#5b21b6', bg: '#f5f3ff', ring: '#ddd6fe' },
    { match: isShoonyaBroker, label: 'Shoonya', glyph: '∞', fg: '#b45309', bg: '#fef3e2', ring: '#fde3b0' },
    { match: () => true, label: 'Zerodha', glyph: 'Z', fg: '#ef4444', bg: '#fef2f2', ring: '#fecaca' },
  ];
  const brandFor = (b) => BROKER_BRAND.find(x => x.match(b));

  const addBroker = async () => {
    try {
      const res = await fetch(API + '/brokers', { method: 'POST', headers: getHeaders(), body: JSON.stringify(form) });
      const d = await res.json();
      if (d._id) { setBrokers(p => [...p, d]); setShowAdd(false); setForm({ name: 'Zerodha', clientId: '', apiKey: '', apiSecret: '' }); }
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const deleteBroker = async (id) => {
    if (!window.confirm('Remove broker?')) return;
    await fetch(API + '/brokers/' + id, { method: 'DELETE', headers: getHeaders() });
    setBrokers(p => p.filter(b => b._id !== id));
  };

  const openAutoLogin = (b) => {
    setAutoFor(b);
    setAutoForm({ password: '', totpSecret: '', mobile: '', enabled: !!b.autoLoginEnabled });
    setAutoMsg('');
  };

  // Edit a broker's credentials. apiSecret starts blank — leave it blank to keep
  // the stored secret, or type a new one to replace it.
  const openEdit = (b) => {
    setEditFor(b);
    setEditForm({ name: b.name || '', clientId: b.clientId || '', apiKey: b.apiKey || '', apiSecret: '', proxyUrl: b.proxyUrl || '' });
    setEditMsg('');
  };

  const saveEdit = async () => {
    if (!editFor) return;
    setEditBusy(true); setEditMsg('');
    try {
      const res = await fetch(API + '/brokers/' + editFor._id + '/credentials', {
        method: 'PATCH', headers: getHeaders(), body: JSON.stringify(editForm),
      });
      const d = await res.json();
      if (res.ok && d._id) {
        setBrokers(p => p.map(b => b._id === d._id ? { ...b, ...d } : b));
        setEditMsg('✅ Saved' + (d.connected ? '' : ' — ab dobara Connect karein'));
        setTimeout(() => setEditFor(null), 900);
      } else setEditMsg('❌ ' + (d.message || 'Failed to save'));
    } catch (e) { setEditMsg('❌ ' + e.message); }
    setEditBusy(false);
  };

  // Save credentials + enabled flag. Blank secret fields are left untouched on
  // the server, so an already-saved password/secret survives a plain re-save.
  const saveAutoLogin = async () => {
    if (!autoFor) return;
    setAutoBusy(true); setAutoMsg('');
    try {
      const res = await fetch(API + '/brokers/' + autoFor._id + '/auto-login', {
        method: 'PATCH', headers: getHeaders(),
        body: JSON.stringify(autoForm),
      });
      const d = await res.json();
      if (res.ok) {
        setBrokers(p => p.map(b => b._id === d._id ? { ...b, ...d } : b));
        setAutoFor(d);
        setAutoForm(f => ({ ...f, password: '', totpSecret: '' }));
        setAutoMsg('✅ Saved');
      } else setAutoMsg('❌ ' + (d.message || 'Failed to save'));
    } catch (e) { setAutoMsg('❌ ' + e.message); }
    setAutoBusy(false);
  };

  // Trigger an immediate login to confirm the saved credentials work.
  const testAutoLogin = async () => {
    if (!autoFor) return;
    setAutoBusy(true); setAutoMsg('Logging in…');
    try {
      // Persist any just-typed creds first so the test uses them.
      if (autoForm.password.trim() || autoForm.totpSecret.trim()) await saveAutoLogin();
      const res = await fetch(API + '/brokers/' + autoFor._id + '/auto-login/run', {
        method: 'POST', headers: getHeaders(),
      });
      const d = await res.json();
      if (d.success) {
        setAutoMsg('✅ Connected! Token issued ' + fmtDateTime(d.tokenIssuedAt));
        fetchBrokers();
      } else setAutoMsg('❌ ' + (d.message || 'Login failed'));
    } catch (e) { setAutoMsg('❌ ' + e.message); }
    setAutoBusy(false);
  };

  const toggleField = async (id, field, val) => {
    setBrokers(p => p.map(b => b._id === id ? { ...b, [field]: val } : b));
  };

  // Crypto portal gets a dedicated Delta Exchange connect screen (API key + secret),
  // not the Zerodha/Upstox flow. Early return is after all hooks above (rules-of-hooks safe).
  if (IS_CRYPTO_PORTAL) return <CryptoBrokerView brokers={brokers} reload={fetchBrokers} />;

  // NSE portal: hide crypto (Delta) brokers — they belong to the crypto portal only.
  const visibleBrokers = (brokers || []).filter(b => !isDeltaBroker(b));
  // Same broker added more than once (e.g. 3 separate Zerodha accounts for
  // proxy/IP reasons) look identical otherwise — number them "#1", "#2"… so
  // the card grid is scannable. Counted/ordered by brand (Zerodha vs Upstox
  // vs Shoonya), oldest first (Mongo already returns them in that order).
  const brandTally = {};
  visibleBrokers.forEach(b => { const l = brandFor(b).label; brandTally[l] = (brandTally[l] || 0) + 1; });
  const brandSeen = {};
  const displayNameFor = (b) => {
    const l = brandFor(b).label;
    brandSeen[l] = (brandSeen[l] || 0) + 1;
    return brandTally[l] > 1 ? `${b.name} #${brandSeen[l]}` : b.name;
  };

  return (
    <div>
      <PageHead title="Broker" subtitle="Manage your connected brokers" icon="🏦"
        right={<button onClick={() => setShowAdd(true)} style={{ background: UI.card, color: UI.blue, border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>+ Add Broker</button>} />
      <div style={card({ padding: 28 })}>

        {upMsg && (
          <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 700,
            background: upMsg.startsWith('✅') ? '#d1fae5' : '#fee2e2', color: upMsg.startsWith('✅') ? '#065f46' : '#991b1b' }}>
            {upMsg}
          </div>
        )}

        {visibleBrokers.length === 0 && (
          <div style={{textAlign:'center',padding:40,color:'#9ca3af'}}>No brokers added yet. Click + Add Broker.</div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(190px, 1fr))',gap:12}}>
        {visibleBrokers.map(b => {
          const brand = brandFor(b);
          const shoonya = isShoonyaBroker(b);
          const primaryAction = shoonya ? () => openAutoLogin(b) : (isUpstoxBroker(b) ? () => connectUpstoxBroker(b) : () => connectZerodha(b));
          return (
          <div key={b._id} style={{display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center',gap:6,padding:'18px 14px 12px',border:`1px solid ${IS_CRYPTO_PORTAL ? UI.border : '#f1f3f9'}`,borderRadius:14,background: IS_CRYPTO_PORTAL ? '#1e2329' : '#fafafa'}}>
            <div style={{width:52,height:52,borderRadius:'50%',background:brand.bg,border:`2px solid ${brand.ring}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:21,color:brand.fg,fontWeight:800,flexShrink:0}}>{brand.glyph}</div>
            <div style={{fontWeight:800,fontSize:14,color: IS_CRYPTO_PORTAL ? '#eaecef' : '#1f2937',maxWidth:'100%',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{displayNameFor(b)}</div>
            <div style={{fontSize:11,color: IS_CRYPTO_PORTAL ? UI.muted : '#94a3b8',maxWidth:'100%',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{b.clientId}</div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,flexWrap:'wrap',marginTop:2}}>
              <span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:20,background:b.connected?'#d1fae5':'#fee2e2',color:b.connected?'#065f46':'#991b1b',fontSize:10,fontWeight:700}}>
                <div style={{width:4,height:4,borderRadius:'50%',background:b.connected?'#10b981':'#ef4444'}}/>
                {b.connected ? 'Connected' : 'Not Connected'}
              </span>
              {b.autoLoginEnabled && (
                <span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',borderRadius:20,background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.10)' : '#e0e7ff',color:'#3730a3',fontSize:10,fontWeight:700}}>⚡ Auto</span>
              )}
            </div>
            <div style={{width:'100%',borderTop:`1px solid ${IS_CRYPTO_PORTAL ? UI.border : '#eef1f6'}`,margin:'10px 0 9px'}}/>
            <div style={{display:'flex',gap:6,justifyContent:'center'}}>
              <button
                onClick={primaryAction}
                disabled={connecting === b._id}
                title={b.connected ? 'Reconnect' : 'Connect'}
                style={{background: b.connected ? brand.fg : '#f59e0b', color:'#fff', border:'none', borderRadius:7, width:30, height:30, cursor:'pointer', fontSize:13, fontWeight:700, display:'flex',alignItems:'center',justifyContent:'center'}}
              >{connecting === b._id ? '…' : (b.connected ? '🔄' : '🔗')}</button>
              {!shoonya && <button onClick={() => openAutoLogin(b)} title="Daily auto-login" style={{background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.10)' : '#eef2ff',color:'#4f46e5',border:'1px solid #e0e7ff',borderRadius:7,width:30,height:30,cursor:'pointer',fontSize:13,fontWeight:700}}>⚡</button>}
              <button onClick={() => openEdit(b)} title="Edit" style={{background: IS_CRYPTO_PORTAL ? '#1e2329' : '#f3f4f6',color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151',border:'1px solid #e5e7eb',borderRadius:7,width:30,height:30,cursor:'pointer',fontSize:13,fontWeight:700}}>✏️</button>
              <button onClick={() => deleteBroker(b._id)} title="Remove" style={{background: IS_CRYPTO_PORTAL ? 'rgba(246,70,93,0.12)' : '#fee2e2',color:'#ef4444',border:'none',borderRadius:7,width:30,height:30,cursor:'pointer',fontSize:13,fontWeight:600}}>🗑</button>
            </div>
          </div>
          );
        })}
        </div>

        {showAdd && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
            <div style={{background:UI.card,borderRadius:16,padding:32,width:440,boxShadow:'0 20px 60px rgba(0,0,0,0.15)'}}>
              <h2 style={{fontWeight:800,marginBottom:24,fontSize:20}}>Add Broker</h2>
              <label style={{fontSize:12,fontWeight:700,display:'block',marginBottom:6,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Broker</label>
              <select value={form.name} onChange={e => setForm({...form, name: e.target.value})} style={{width:'100%',padding:'10px 12px',border:'1px solid #e8eaf0',borderRadius:8,fontSize:14,marginBottom:16}}>
                {['Zerodha','Upstox','Shoonya','AngelOne'].map(b => <option key={b}>{b}</option>)}
              </select>
              {['clientId','apiKey','apiSecret'].map(field => (
                <div key={field} style={{marginBottom:16}}>
                  <label style={{fontSize:12,fontWeight:700,display:'block',marginBottom:6,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151',textTransform:'capitalize'}}>{field.replace(/([A-Z])/g,' $1')}</label>
                  <input type={field==='apiSecret'?'password':'text'} value={form[field]} onChange={e => setForm({...form,[field]:e.target.value})} placeholder={'Enter '+field} style={{width:'100%',padding:'10px 12px',border:'1px solid #e8eaf0',borderRadius:8,fontSize:14,boxSizing:'border-box'}}/>
                </div>
              ))}
              <div style={{display:'flex',gap:12,marginTop:24}}>
                <button onClick={() => setShowAdd(false)} style={{flex:1,padding:12,border:'1px solid #e8eaf0',borderRadius:8,background:UI.card,cursor:'pointer',fontSize:14,fontWeight:600}}>Cancel</button>
                <button onClick={addBroker} style={{flex:1,padding:12,background: IS_CRYPTO_PORTAL ? '#2c8cff' : '#2563eb',color:'#fff',border:'none',borderRadius:10,cursor:'pointer',fontWeight:700,fontSize:14}}>Add Broker</button>
              </div>
            </div>
          </div>
        )}

        {editFor && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
            <div style={{background:UI.card,borderRadius:16,padding:32,width:440,maxWidth:'92vw',boxShadow:'0 20px 60px rgba(0,0,0,0.15)'}}>
              <h2 style={{fontWeight:800,marginBottom:6,fontSize:20}}>✏️ Edit Broker</h2>
              <div style={{fontSize:12.5,color: IS_CRYPTO_PORTAL ? '#a7afba' : '#6b7280',marginBottom:18}}>API Secret blank chhodo to purana hi rahega. API key/secret badle to dobara Connect karna hoga.</div>
              <label style={{fontSize:12,fontWeight:700,display:'block',marginBottom:6,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Broker</label>
              <select value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} style={{width:'100%',padding:'10px 12px',border:'1px solid #e8eaf0',borderRadius:8,fontSize:14,marginBottom:16}}>
                {['Zerodha','Upstox','Shoonya','AngelOne'].map(b => <option key={b}>{b}</option>)}
              </select>
              {[['clientId','Client Id',false],['apiKey','Api Key',false],['apiSecret','Api Secret',true]].map(([field,label,secret]) => (
                <div key={field} style={{marginBottom:16}}>
                  <label style={{fontSize:12,fontWeight:700,display:'block',marginBottom:6,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>{label}</label>
                  <input type={secret?'password':'text'} value={editForm[field]} onChange={e => setEditForm({...editForm,[field]:e.target.value})} placeholder={secret?'Leave blank to keep current secret':('Enter '+label)} style={{width:'100%',padding:'10px 12px',border:'1px solid #e8eaf0',borderRadius:8,fontSize:14,boxSizing:'border-box'}}/>
                </div>
              ))}
              <div style={{marginBottom:16}}>
                <label style={{fontSize:12,fontWeight:700,display:'block',marginBottom:6,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Proxy URL <span style={{fontWeight:500,color:'#9ca3af'}}>(optional — alag IP ke liye)</span></label>
                <input type="text" value={editForm.proxyUrl} onChange={e => setEditForm({...editForm, proxyUrl: e.target.value})} placeholder="e.g. http://&lt;vps2-ip&gt;:8888 (blank = is server ka IP)" style={{width:'100%',padding:'10px 12px',border:'1px solid #e8eaf0',borderRadius:8,fontSize:14,boxSizing:'border-box'}}/>
                <div style={{fontSize:11,color:'#9ca3af',marginTop:5,lineHeight:1.5}}>Zerodha ek IP sirf ek account pe allow karta hai. Doosre Zerodha account ke liye yahan apne 2nd VPS ka proxy daalo — us account ke saare orders us IP se jaayenge.</div>
              </div>
              {editMsg && <div style={{fontSize:13,fontWeight:600,marginBottom:8,color:editMsg.startsWith('✅')?'#059669':'#dc2626'}}>{editMsg}</div>}
              <div style={{display:'flex',gap:12,marginTop:16}}>
                <button onClick={() => setEditFor(null)} disabled={editBusy} style={{flex:1,padding:12,border:'1px solid #e8eaf0',borderRadius:8,background:UI.card,cursor:'pointer',fontSize:14,fontWeight:600}}>Cancel</button>
                <button onClick={saveEdit} disabled={editBusy} style={{flex:1,padding:12,background: IS_CRYPTO_PORTAL ? '#2c8cff' : '#2563eb',color:'#fff',border:'none',borderRadius:10,cursor:'pointer',fontWeight:700,fontSize:14}}>{editBusy?'Saving…':'Save'}</button>
              </div>
            </div>
          </div>
        )}

        {autoFor && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
            <div style={{background:UI.card,borderRadius:16,padding:32,width:460,maxWidth:'92vw',boxShadow:'0 20px 60px rgba(0,0,0,0.15)'}}>
              <h2 style={{fontWeight:800,marginBottom:6,fontSize:20}}>⚡ Daily Auto-login</h2>
              <div style={{fontSize:12.5,color: IS_CRYPTO_PORTAL ? '#a7afba' : '#6b7280',marginBottom:18}}>{autoFor.name} · <b style={{color: IS_CRYPTO_PORTAL ? '#2c8cff' : '#2563eb'}}>{autoFor.clientId}</b> — har subah token apne aap refresh ho jayega, manual login nahi karna padega.</div>

              <label style={{display:'flex',alignItems:'center',gap:10,marginBottom:18,cursor:'pointer'}}>
                <input type="checkbox" checked={autoForm.enabled} onChange={e => setAutoForm({...autoForm, enabled: e.target.checked})} style={{width:18,height:18}}/>
                <span style={{fontSize:13.5,fontWeight:700,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Enable daily auto-login (8:30 AM, Mon–Fri)</span>
              </label>

              {isUpstoxBroker(autoFor) && (
                <div style={{marginBottom:16}}>
                  <label style={{fontSize:12,fontWeight:700,display:'block',marginBottom:6,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Registered Mobile Number</label>
                  <input value={autoForm.mobile} onChange={e => setAutoForm({...autoForm, mobile: e.target.value})}
                    placeholder={autoFor.hasLoginMobile ? '•••••••• (saved — leave blank to keep)' : 'e.g. 9876543210'}
                    style={{width:'100%',padding:'10px 12px',border:'1px solid #e8eaf0',borderRadius:8,fontSize:14,boxSizing:'border-box'}}/>
                </div>
              )}

              <div style={{marginBottom:16}}>
                <label style={{fontSize:12,fontWeight:700,display:'block',marginBottom:6,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>{isUpstoxBroker(autoFor) ? 'Login PIN (6-digit)' : isShoonyaBroker(autoFor) ? 'Shoonya Login Password' : 'Zerodha Login Password'}</label>
                <input type="password" value={autoForm.password} onChange={e => setAutoForm({...autoForm, password: e.target.value})}
                  placeholder={autoFor.hasLoginPassword ? '•••••••• (saved — leave blank to keep)' : (isUpstoxBroker(autoFor) ? 'Enter 6-digit Upstox PIN' : isShoonyaBroker(autoFor) ? 'Enter Shoonya password' : 'Enter Zerodha password')}
                  style={{width:'100%',padding:'10px 12px',border:'1px solid #e8eaf0',borderRadius:8,fontSize:14,boxSizing:'border-box'}}/>
              </div>

              <div style={{marginBottom:16}}>
                <label style={{fontSize:12,fontWeight:700,display:'block',marginBottom:6,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>TOTP Secret <span style={{fontWeight:500,color:'#9ca3af'}}>(authenticator "can't scan QR" wala key)</span></label>
                <input type="password" value={autoForm.totpSecret} onChange={e => setAutoForm({...autoForm, totpSecret: e.target.value})}
                  placeholder={autoFor.hasTotpSecret ? '•••••••• (saved — leave blank to keep)' : 'e.g. JBSWY3DPEHPK3PXP'}
                  style={{width:'100%',padding:'10px 12px',border:'1px solid #e8eaf0',borderRadius:8,fontSize:14,boxSizing:'border-box'}}/>
              </div>

              <div style={{fontSize:11.5,color:'#9ca3af',marginBottom:8,lineHeight:1.5}}>🔒 Sab fields encrypted store hote hain. {isUpstoxBroker(autoFor) ? 'Upstox auto-login ke liye account pe TOTP/Authenticator 2FA enable hona zaroori hai. TOTP secret enable karte waqt "Can\'t scan?" / "Manual entry" pe milta hai. Roz ~3:30 AM token expire hota hai — cron subah refresh kar deta hai.' : isShoonyaBroker(autoFor) ? 'Shoonya ke Profile → Security → TOTP Setup se base32 secret milta hai (QR ke neeche). Login headless browser se hota hai — kabhi bhi expire ho, cron dobara login kar dega.' : 'TOTP secret Zerodha pe external 2FA setup karte waqt "Can\'t scan the QR?" pe milta hai.'}</div>
              {autoFor.autoLoginLastError && <div style={{fontSize:11.5,color:'#b45309',marginBottom:8}}>Last error: {autoFor.autoLoginLastError}</div>}
              {autoMsg && <div style={{fontSize:12.5,fontWeight:600,marginBottom:8,color: autoMsg.startsWith('✅') ? '#059669' : (autoMsg.startsWith('❌') ? '#dc2626' : '#2563eb')}}>{autoMsg}</div>}

              <div style={{display:'flex',gap:12,marginTop:18}}>
                <button onClick={() => setAutoFor(null)} disabled={autoBusy} style={{flex:1,padding:12,border:'1px solid #e8eaf0',borderRadius:8,background:UI.card,cursor:'pointer',fontSize:14,fontWeight:600}}>Close</button>
                <button onClick={saveAutoLogin} disabled={autoBusy} style={{flex:1,padding:12,border:'1px solid #c7d2fe',borderRadius:8,background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.10)' : '#eef2ff',color:'#4f46e5',cursor:'pointer',fontWeight:700,fontSize:14}}>Save</button>
                <button onClick={testAutoLogin} disabled={autoBusy} style={{flex:1,padding:12,background: IS_CRYPTO_PORTAL ? '#2c8cff' : '#2563eb',color:'#fff',border:'none',borderRadius:10,cursor:'pointer',fontWeight:700,fontSize:14}}>{autoBusy ? '…' : '🔗 Login now'}</button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}


// ── ACTIVITY — full day's engine log per deployed strategy ────────────
// Renders the persisted ActivityLog: every order placed, fill, exit (with the
// reason), broker rejection, blocked entry and SL event — the "kab / kya / kyu"
// audit trail. Grouped by strategy, newest day selectable, auto-refreshes today.
const ACT_STYLE = {
  ENTRY:          { icon: '🟢', label: 'Entry',         color: UI.green },
  EXIT:           { icon: '🔵', label: 'Exit',          color: UI.blue },
  SL_PLACED:      { icon: '🛡️', label: 'SL rested',     color: '#0891b2' },
  SL_REJECTED:    { icon: '⚠️', label: 'SL rejected',   color: '#f59e0b' },
  ORDER_REJECTED: { icon: '❌', label: 'Order rejected', color: UI.red },
  EXIT_REJECTED:  { icon: '❌', label: 'Exit rejected',  color: UI.red },
  BLOCKED:        { icon: '⛔', label: 'Blocked',        color: UI.red },
  ADJUST:         { icon: '♻️', label: 'Adjust',         color: UI.indigo },
  RECOVERY:       { icon: '🔄', label: 'Recovery',       color: UI.indigo },
  INFO:           { icon: 'ℹ️', label: 'Info',           color: UI.sub },
};
const levelColor = { success: UI.green, info: UI.sub, warn: '#f59e0b', error: UI.red };

// The Remark column: a plain-language verdict for the row — placed / rejected
// (+ broker's reason) / exited / blocked — so the user sees outcome at a glance.
function remarkFor(e) {
  const reason = e.reason || '';
  switch (e.type) {
    case 'ENTRY':          return { label: e.paper ? 'Paper fill' : 'Order placed', color: UI.green, detail: reason };
    case 'EXIT':           return { label: 'Exited', color: UI.blue, detail: reason };
    case 'ORDER_REJECTED': return { label: 'REJECTED', color: UI.red, detail: reason || 'Rejected by broker' };
    case 'EXIT_REJECTED':  return { label: 'Exit REJECTED', color: UI.red, detail: reason || 'Rejected by broker' };
    case 'SL_PLACED':      return { label: 'SL rested', color: '#0891b2', detail: reason };
    case 'SL_REJECTED':    return { label: 'SL rejected', color: '#f59e0b', detail: reason };
    case 'BLOCKED':        return { label: 'BLOCKED', color: UI.red, detail: reason };
    case 'ADJUST':         return { label: 'Adjusted', color: UI.indigo, detail: reason };
    case 'RECOVERY':       return { label: 'Recovered', color: UI.indigo, detail: reason };
    default:               return { label: e.type || 'Info', color: UI.sub, detail: reason };
  }
}

// Shared activity-row formatters. Module-level so both the day view and the
// date-range view render identical rows instead of drifting apart.
const actTime = ts => { try { return new Date(ts).toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata' }); } catch { return ''; } };
const actMoney = n => (n >= 0 ? '+' : '-') + (IS_CRYPTO_PORTAL ? '$' : '₹')
  + (IS_CRYPTO_PORTAL
      ? Math.abs(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })
      : Math.abs(Math.round(n || 0)).toLocaleString('en-IN'));
const actPnlColor = n => (n > 0 ? UI.green : n < 0 ? UI.red : UI.sub);

// The event table itself — one row per engine event (order, fill, exit+reason,
// rejection, block). Shared by the Today / Past day view and the range view.
function ActivityEventTable({ events }) {
  return (
    <div className="ar-tablewrap" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: IS_CRYPTO_PORTAL ? '#1e2329' : '#f8fafc', textAlign: 'left', color: UI.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            <th style={{ padding: '9px 12px', fontWeight: 800, whiteSpace: 'nowrap' }}>Time</th>
            <th style={{ padding: '9px 12px', fontWeight: 800 }}>Type</th>
            <th style={{ padding: '9px 12px', fontWeight: 800 }}>Instrument</th>
            <th style={{ padding: '9px 12px', fontWeight: 800 }}>Side</th>
            <th style={{ padding: '9px 12px', fontWeight: 800, textAlign: 'right' }}>Qty</th>
            <th style={{ padding: '9px 12px', fontWeight: 800, textAlign: 'right' }}>Price</th>
            <th style={{ padding: '9px 12px', fontWeight: 800, textAlign: 'right' }}>P&L</th>
            <th style={{ padding: '9px 12px', fontWeight: 800, minWidth: 220 }}>Remark</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => {
            const st = ACT_STYLE[e.type] || ACT_STYLE.INFO;
            const rm = remarkFor(e);
            return (
              <tr key={e._id || i} style={{ borderTop: `1px solid #f1f5f9`, background: e.level === 'error' ? '#fef2f2' : e.level === 'warn' ? '#fffbeb' : '#fff' }}>
                <td style={{ padding: '10px 12px', color: UI.muted, fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{actTime(e.ts)}</td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}><span style={{ color: st.color, fontWeight: 800 }}>{st.icon} {st.label}</span></td>
                <td style={{ padding: '10px 12px', fontWeight: 700, color: UI.text, whiteSpace: 'nowrap' }}>{e.instrument || '—'}</td>
                <td style={{ padding: '10px 12px', fontWeight: 800, color: e.action === 'SELL' ? UI.red : e.action === 'BUY' ? UI.green : UI.muted, whiteSpace: 'nowrap' }}>{e.action || '—'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: UI.sub, fontVariantNumeric: 'tabular-nums' }}>{e.qty != null ? e.qty : '—'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: UI.sub, fontVariantNumeric: 'tabular-nums' }}>{e.price != null ? (IS_CRYPTO_PORTAL ? '$' : '₹') + e.price : '—'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: e.pnl != null ? actPnlColor(e.pnl) : UI.muted }}>{e.pnl != null ? actMoney(e.pnl) : '—'}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ fontWeight: 800, color: rm.color }}>{rm.label}</span>
                  {rm.detail && <span style={{ color: UI.sub }}> — {rm.detail}</span>}
                  {e.orderId && !String(e.orderId).startsWith('PAPER') && <span style={{ color: UI.muted, fontSize: 11 }}> · #{e.orderId}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Activity rows are TTL-purged, so every "nothing found" message has to say how
// far back the journal actually reaches — else an empty range reads as a bug.
const ACTIVITY_RETENTION_NOTE = 'sirf last 60 days tak save rehti hai';

// '2026-07-22' → 'Wed 22 Jul'. Same UTC parse as fmtDayLabel (a local parse turns
// the date into the previous evening west of IST and shows the wrong day), just
// short enough to sit inside a chip.
function fmtBriefDay(d) {
  try {
    return new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', {
      timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short',
    });
  } catch { return d; }
}

// '2026-07-22' → 'Wed, 22 Jul 2026'. Parsed as UTC so the label can't slip a day.
function fmtDayLabel(d) {
  try {
    return new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', {
      timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return d; }
}

// Strategy-wise activity across a date range: pick a strategy + from/to dates and
// read the whole deployment's journal day by day. The Today / Past tabs answer
// "aaj kya hua"; this answers "is hafte is strategy ne kya kiya" — the view you
// need to audit a carry / delta-adjust strategy, whose story spans days.
function ActivityRange({ strategies, days, totals, strategyId, setStrategyId, from, setFrom, to, setTo,
                         earliest, searched, loading, error, onSearch, open, setOpen }) {
  const today = istToday();
  const picked = strategies.find(s => String(s._id) === String(strategyId));
  const inputStyle = { padding: '9px 12px', fontSize: 14, fontWeight: 700, borderRadius: 10, border: `1px solid ${UI.border}`, color: UI.text, background: UI.card, cursor: 'pointer' };
  const label = t => <label style={{ fontSize: 13, fontWeight: 700, color: UI.sub }}>{t}</label>;
  // Days start collapsed — a month-long range is a scannable list of day rows
  // (P&L + event count), and you open the one day you actually want to read.
  const isOpen = d => open[d] === true;
  const toggle = d => setOpen(o => ({ ...o, [d]: !o[d] }));

  // Quick ranges — the spans you actually audit (a deployment week, a month).
  const quick = [['7d', -7], ['15d', -15], ['30d', -30]];

  return (
    <>
      <div style={{ ...card(), padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 220, flex: '1 1 240px' }}>
          {label('Strategy')}
          <select value={strategyId} onChange={e => setStrategyId(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
            <option value="">All strategies</option>
            {strategies.map(s => (
              <option key={s._id} value={s._id}>{s.name}{s.isDeployed ? ' · deployed' : ''}{s.mode ? ` (${s.mode})` : ''}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {label('From')}
          <input type="date" value={from} min={earliest} max={today} onChange={e => setFrom(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {label('To')}
          <input type="date" value={to} min={earliest} max={today} onChange={e => setTo(e.target.value)} style={inputStyle} />
        </div>
        <button onClick={() => onSearch()} disabled={loading}
          style={{ padding: '10px 22px', fontSize: 13.5, fontWeight: 800, borderRadius: 10, border: 'none', cursor: loading ? 'default' : 'pointer', color: '#fff', background: loading ? '#9ca3af' : UI.blue }}>
          {loading ? 'Loading…' : '🔎 Check Activity'}
        </button>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {quick.map(([lbl, d]) => (
            <button key={lbl} onClick={() => { const f = istShift(d); setFrom(f); setTo(today); onSearch({ from: f, to: today }); }}
              style={{ padding: '8px 12px', fontSize: 12, fontWeight: 800, borderRadius: 8, border: `1px solid ${UI.border}`, cursor: 'pointer', color: UI.sub, background: UI.card }}>
              Last {lbl}
            </button>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: UI.muted, marginBottom: 14 }}>
        Activity {ACTIVITY_RETENTION_NOTE}
      </div>

      {error && (
        <div style={{ ...card(), padding: 16, marginBottom: 16, borderLeft: `4px solid ${UI.red}`, fontSize: 13.5, fontWeight: 700, color: UI.red }}>{error}</div>
      )}

      {loading && <div style={{ ...card(), padding: 40, textAlign: 'center', color: UI.muted, fontSize: 14 }}>Loading activity…</div>}

      {!loading && searched && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: UI.text }}>
            {picked ? picked.name : 'All strategies'} · {from} → {to}
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: UI.muted }}>
            {totals?.events || 0} event{(totals?.events || 0) === 1 ? '' : 's'} across {totals?.days || 0} day{(totals?.days || 0) === 1 ? '' : 's'}
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 13.5, fontWeight: 800, color: UI.sub }}>
            Realized P&L: <span style={{ color: actPnlColor(totals?.pnl || 0) }}>{actMoney(totals?.pnl || 0)}</span>
          </span>
        </div>
      )}

      {!loading && searched && days.length === 0 && (
        <div style={{ ...card(), padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🗓️</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151', marginBottom: 6 }}>No activity in this range</div>
          <div style={{ fontSize: 13.5, color: UI.muted }}>
            {picked ? `"${picked.name}" ne ${from} se ${to} ke beech kuch nahi kiya.` : `${from} se ${to} ke beech koi engine activity nahi hui.`}
            {' '}Dates badal kar dekho — {ACTIVITY_RETENTION_NOTE}.
          </div>
        </div>
      )}

      {!loading && days.map(day => (
        <div key={day.date} style={{ marginBottom: 18 }}>
          <button onClick={() => toggle(day.date)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', marginBottom: 8, borderRadius: 10,
                     border: `1px solid ${UI.border}`, background: IS_CRYPTO_PORTAL ? '#1e2329' : '#f8fafc', cursor: 'pointer', textAlign: 'left' }}>
            <span style={{ fontSize: 12, color: UI.muted }}>{isOpen(day.date) ? '▼' : '▶'}</span>
            <span style={{ fontSize: 14.5, fontWeight: 800, color: UI.text }}>{fmtDayLabel(day.date)}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: UI.muted }}>{day.count} event{day.count === 1 ? '' : 's'} · {day.groups.length} strateg{day.groups.length === 1 ? 'y' : 'ies'}</span>
            <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 800, color: actPnlColor(day.dayPnl) }}>{actMoney(day.dayPnl)}</span>
          </button>

          {isOpen(day.date) && day.groups.map(g => (
            <div key={String(g.strategyId || 'unassigned')} style={{ ...card(), padding: 0, marginBottom: 10, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 18px', borderBottom: `1px solid ${UI.border}` }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: UI.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.strategyName}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, whiteSpace: 'nowrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: UI.sub }}>P&L: <span style={{ color: actPnlColor(g.dayPnl) }}>{actMoney(g.dayPnl)}</span></span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: UI.muted }}>{g.events.length} event{g.events.length === 1 ? '' : 's'}</span>
                </div>
              </div>
              <ActivityEventTable events={g.events} />
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

function istToday() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
}
// IST date `days` away from today as YYYY-MM-DD (negative = past).
function istShift(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

// Opt-in card: turn on WhatsApp alerts and save the number the engine pings on
// every important event (entry / exit+P&L / rejection / blocked) of this user's
// deployed strategies. Self-contained — loads /auth/me, saves via /auth/notifications.
function WhatsAppAlertsCard() {
  const [on, setOn] = React.useState(false);
  const [num, setNum] = React.useState('');
  const [saved, setSaved] = React.useState({ on: false, num: '' });
  const [msg, setMsg] = React.useState(null); // { text, ok }
  const [busy, setBusy] = React.useState(false);
  const [pin, setPin] = React.useState('');       // new 4-digit PIN to set (blank = leave as-is)
  const [pinSet, setPinSet] = React.useState(false);

  React.useEffect(() => {
    fetch(API + '/auth/me', { headers: getHeaders() })
      .then(r => r.json())
      .then(u => { setOn(!!u.whatsappAlerts); setNum(u.whatsappNumber || ''); setSaved({ on: !!u.whatsappAlerts, num: u.whatsappNumber || '' }); setPinSet(!!u.whatsappPinSet); })
      .catch(() => {});
  }, []);

  const dirty = on !== saved.on || num.trim() !== saved.num;
  const flash = (text, ok) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); };

  const save = () => {
    setBusy(true);
    fetch(API + '/auth/notifications', { method: 'PUT', headers: { ...getHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ whatsappAlerts: on, whatsappNumber: num }) })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.message || 'Save failed'); return d; })
      .then(d => { setSaved({ on: !!d.whatsappAlerts, num: d.whatsappNumber || '' }); setNum(d.whatsappNumber || ''); flash('Saved ✓', true); })
      .catch(e => flash(e.message, false))
      .finally(() => setBusy(false));
  };

  const test = () => {
    setBusy(true);
    fetch(API + '/auth/notifications/test', { method: 'POST', headers: getHeaders() })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.message || 'Send failed'); return d; })
      .then(() => flash('Test message sent — check your WhatsApp 📲', true))
      .catch(e => flash(e.message, false))
      .finally(() => setBusy(false));
  };

  // Set / change / clear the 4-digit PIN that confirms a LIVE deploy from
  // WhatsApp. Sending an empty pin clears it.
  const savePin = (clear) => {
    if (!clear && !/^\d{4}$/.test(pin)) { flash('PIN must be exactly 4 digits', false); return; }
    setBusy(true);
    fetch(API + '/auth/notifications/pin', { method: 'PUT', headers: { ...getHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ pin: clear ? '' : pin }) })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.message || 'Save failed'); return d; })
      .then(d => { setPinSet(!!d.whatsappPinSet); setPin(''); flash(clear ? 'PIN cleared' : 'Deploy PIN saved ✓', true); })
      .catch(e => flash(e.message, false))
      .finally(() => setBusy(false));
  };

  return (
    <div style={{ ...card(), padding: '14px 18px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 22 }}>📲</div>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: UI.text }}>WhatsApp Alerts</div>
          <div style={{ fontSize: 11.5, color: UI.muted }}>Har entry / exit (P&L) / reject / block ka update apne WhatsApp pe pao</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: UI.sub }}>
          <input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
          {on ? 'On' : 'Off'}
        </label>
        <input value={num} onChange={e => setNum(e.target.value)} placeholder="WhatsApp number e.g. 9812345678"
          style={{ flex: '1 1 200px', minWidth: 160, padding: '9px 12px', fontSize: 13.5, fontWeight: 600, borderRadius: 10, border: `1px solid ${UI.border}`, color: UI.text, background: UI.card }} />
        <button onClick={save} disabled={busy || !dirty}
          style={{ padding: '9px 16px', fontSize: 13, fontWeight: 800, borderRadius: 10, border: 'none', cursor: (busy || !dirty) ? 'default' : 'pointer', color: '#fff', background: (busy || !dirty) ? '#9ca3af' : UI.blue }}>Save</button>
        <button onClick={test} disabled={busy || !saved.num}
          style={{ padding: '9px 14px', fontSize: 13, fontWeight: 800, borderRadius: 10, border: `1px solid ${UI.border}`, cursor: (busy || !saved.num) ? 'default' : 'pointer', color: UI.sub, background: UI.card }}>Send test</button>
        {msg && <span style={{ fontSize: 12.5, fontWeight: 700, color: msg.ok ? UI.green : UI.red }}>{msg.text}</span>}
      </div>

      {/* Deploy-via-WhatsApp PIN: confirms a LIVE deploy sent over chat. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', borderTop: `1px solid ${UI.border}`, paddingTop: 12 }}>
        <div style={{ fontSize: 22 }}>🔐</div>
        <div style={{ minWidth: 180 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: UI.text }}>Deploy PIN</div>
          <div style={{ fontSize: 11.5, color: UI.muted }}>WhatsApp se LIVE deploy confirm karne ke liye 4-digit PIN {pinSet ? '— set ✓' : '— not set'}</div>
        </div>
        <input value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" inputMode="numeric" placeholder={pinSet ? 'New 4-digit PIN' : 'Set 4-digit PIN'}
          style={{ flex: '0 1 160px', minWidth: 130, padding: '9px 12px', fontSize: 13.5, fontWeight: 600, letterSpacing: 3, borderRadius: 10, border: `1px solid ${UI.border}`, color: UI.text, background: UI.card }} />
        <button onClick={() => savePin(false)} disabled={busy || pin.length !== 4}
          style={{ padding: '9px 16px', fontSize: 13, fontWeight: 800, borderRadius: 10, border: 'none', cursor: (busy || pin.length !== 4) ? 'default' : 'pointer', color: '#fff', background: (busy || pin.length !== 4) ? '#9ca3af' : UI.blue }}>{pinSet ? 'Change PIN' : 'Set PIN'}</button>
        {pinSet && <button onClick={() => savePin(true)} disabled={busy}
          style={{ padding: '9px 14px', fontSize: 13, fontWeight: 800, borderRadius: 10, border: `1px solid ${UI.border}`, cursor: busy ? 'default' : 'pointer', color: UI.red, background: UI.card }}>Clear</button>}
      </div>
    </div>
  );
}

function Activity() {
  const [view, setView] = React.useState('today'); // 'today' | 'past' | 'range'
  const [date, setDate] = React.useState(istToday());
  const [data, setData] = React.useState({ groups: [], deployed: [] });
  const [loading, setLoading] = React.useState(true);

  // ── Range view: one strategy, many days ──────────────────────────────────
  // A carry / delta-adjust strategy's story spans a whole week, so the single-day
  // journal can't answer "is deployment mein hua kya". These drive the filter bar.
  const [rStrategy, setRStrategy] = React.useState('');       // '' = all strategies
  const [rFrom, setRFrom] = React.useState(istShift(-7));
  const [rTo, setRTo] = React.useState(istToday());
  const [rData, setRData] = React.useState(null);             // null = not searched yet
  const [rLoading, setRLoading] = React.useState(false);
  const [rError, setRError] = React.useState('');
  const [rOpen, setROpen] = React.useState({});               // date -> collapsed?

  const runRange = React.useCallback((opts = {}) => {
    const from = opts.from ?? rFrom, to = opts.to ?? rTo, sid = opts.strategyId ?? rStrategy;
    setRLoading(true); setRError(''); setROpen({});   // fresh search → every day collapsed again
    const qs = new URLSearchParams({ from, to, ...(sid ? { strategyId: sid } : {}) }).toString();
    fetch(API + '/strategies/activity/range?' + qs, { headers: getHeaders() })
      .then(r => r.json())
      .then(res => {
        if (!res || !Array.isArray(res.days)) throw new Error(res?.message || 'Could not load activity');
        setRData(res);
        // Server may clamp `from` to the 60-day retention floor — reflect that back
        // so the input never shows a window that was silently narrowed.
        if (res.from && res.from !== from) setRFrom(res.from);
      })
      .catch(e => { setRError(e.message || 'Could not load activity'); setRData(null); })
      .finally(() => setRLoading(false));
  }, [rFrom, rTo, rStrategy]);

  // Switch sub-tab: Today snaps to the live current day; Past opens on the most
  // recent prior trading-day view (defaults to yesterday) with a date filter.
  // Range loads its default (last 7 days, all strategies) the first time it opens.
  const switchView = (v) => {
    setView(v);
    if (v === 'range') { if (!rData && !rLoading) runRange(); return; }
    setDate(v === 'today' ? istToday() : istShift(-1));
  };

  const load = React.useCallback((d) => fetch(API + '/strategies/activity?date=' + d, { headers: getHeaders() })
    .then(r => r.json())
    .then(res => { if (res && Array.isArray(res.groups)) setData(res); })
    .catch(() => {})
    .finally(() => setLoading(false)), []);

  React.useEffect(() => {
    // The range view has its own explicit fetch — don't keep polling the day
    // endpoint (and its 8s timer) behind a tab that isn't showing it.
    if (view === 'range') return;
    setLoading(true);
    load(date);
    // Live-refresh only while viewing today — past days never change.
    if (date !== istToday()) return;
    const id = setInterval(() => load(date), 8000);
    return () => clearInterval(id);
  }, [date, load, view]);

  // Strategies that had activity, keyed for lookup; plus any deployed strategy
  // with no events yet (shown empty so the user knows the engine is watching it).
  const groupById = new Map(data.groups.map(g => [g.strategyId ? String(g.strategyId) : 'unassigned', g]));
  const rows = [];
  for (const s of (data.deployed || [])) {
    const g = groupById.get(String(s._id));
    rows.push({ id: String(s._id), name: s.name, mode: s.mode, events: g ? g.events : [], dayPnl: g ? g.dayPnl : 0 });
    groupById.delete(String(s._id));
  }
  // Any leftover groups (e.g. a since-undeployed strategy, or unassigned events).
  for (const [, g] of groupById) rows.push({ id: g.strategyId || 'unassigned', name: g.strategyName, mode: null, events: g.events, dayPnl: g.dayPnl });

  const totalEvents = data.groups.reduce((n, g) => n + g.events.length, 0);
  const dayTotal = data.groups.reduce((n, g) => n + (g.dayPnl || 0), 0);
  const money = actMoney, pnlColor = actPnlColor;

  return (
    <Layout>
      <PageHead title="Activity" subtitle="Har deployed strategy ki poori din ki activity — order kab/kyu laga, exit kyu hua, reject kyu hua" icon="📜"
        right={<span style={{ fontSize: 12.5, fontWeight: 700, background: 'rgba(255,255,255,0.18)', padding: '6px 14px', borderRadius: 20 }}>{view === 'range' ? 'date range' : date === istToday() ? '● live · auto-refresh 8s' : 'past day'}</span>} />

      <WhatsAppAlertsCard />

      {/* Sub-tabs: Today (live) · Past activity (date filter) */}
      <div style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${UI.border}`, marginBottom: 16 }}>
        {[['today', '📅 Today'], ['past', '🗄️ Past Activity'], ['range', '🔎 Strategy-wise (date range)']].map(([k, label]) => (
          <button key={k} onClick={() => switchView(k)}
            style={{ background: 'transparent', border: 'none', borderBottom: view === k ? `2.5px solid ${UI.blue}` : '2.5px solid transparent', padding: '10px 16px', marginBottom: -1, cursor: 'pointer', fontSize: 13.5, fontWeight: 800, color: view === k ? UI.blue : UI.sub }}>
            {label}
          </button>
        ))}
      </div>

      {/* Date filter (Past only) + summary */}
      {view !== 'range' && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        {view === 'past' ? (
          <>
            <label style={{ fontSize: 13, fontWeight: 700, color: UI.sub }}>Pick a date:</label>
            <input type="date" value={date} min={istShift(-60)} max={istShift(-1)} onChange={e => setDate(e.target.value)}
              style={{ padding: '9px 12px', fontSize: 14, fontWeight: 700, borderRadius: 10, border: `1px solid ${UI.border}`, color: UI.text, background: UI.card, cursor: 'pointer' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: UI.muted }}>(saved up to 60 days)</span>
          </>
        ) : (
          <span style={{ fontSize: 14, fontWeight: 800, color: UI.text }}>Today · {date}</span>
        )}
        <span style={{ fontSize: 13, fontWeight: 700, color: UI.muted }}>{totalEvents} event{totalEvents === 1 ? '' : 's'} · {(data.deployed || []).length} deployed</span>
        <span style={{ marginLeft: 'auto', fontSize: 13.5, fontWeight: 800, color: UI.sub }}>Day realized P&L: <span style={{ color: pnlColor(dayTotal) }}>{money(dayTotal)}</span></span>
      </div>
      )}

      {view === 'range' && (
        <ActivityRange
          strategies={rData?.strategies || []} days={rData?.days || []} totals={rData?.totals}
          strategyId={rStrategy} setStrategyId={setRStrategy}
          from={rFrom} setFrom={setRFrom} to={rTo} setTo={setRTo}
          earliest={rData?.earliest || istShift(-60)} searched={!!rData}
          loading={rLoading} error={rError} onSearch={runRange}
          open={rOpen} setOpen={setROpen} />
      )}

      {view !== 'range' && loading && <div style={{ ...card(), padding: 40, textAlign: 'center', color: UI.muted, fontSize: 14 }}>Loading activity…</div>}

      {view !== 'range' && !loading && rows.length === 0 && (
        <div style={{ ...card(), padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>📭</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151', marginBottom: 6 }}>No deployed strategies</div>
          <div style={{ fontSize: 13.5, color: UI.muted }}>Deploy a strategy to see its live activity here.</div>
        </div>
      )}

      {view !== 'range' && !loading && rows.map(row => (
        <div key={row.id} style={{ ...card(), padding: 0, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '14px 18px', borderBottom: `1px solid ${UI.border}`, background: IS_CRYPTO_PORTAL ? '#1e2329' : '#fbfcfe' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: UI.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
              {row.mode && <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 9px', borderRadius: 20, background: row.mode === 'Live' ? '#d1fae5' : '#dbeafe', color: row.mode === 'Live' ? '#065f46' : UI.blue }}>{row.mode === 'Live' ? '● LIVE' : '📝 Paper'}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: UI.sub }}>Total P&L: <span style={{ color: pnlColor(row.dayPnl) }}>{money(row.dayPnl)}</span></span>
              <span style={{ fontSize: 12, fontWeight: 700, color: UI.muted }}>{row.events.length} event{row.events.length === 1 ? '' : 's'}</span>
            </div>
          </div>

          {row.events.length === 0 ? (
            <div style={{ padding: '20px 18px', fontSize: 13, color: UI.muted }}>No activity {date === istToday() ? 'yet today' : 'on this day'} — engine is watching for entry conditions.</div>
          ) : (
            <ActivityEventTable events={row.events} />
          )}
        </div>
      ))}
    </Layout>
  );
}

// ── POSITIONS — Paper / Live tabs, live P&L, manual exit ──────────────
// Open-positions panel (Paper / Live tabs + live P&L + per-leg cards). Rendered
// standalone on the Positions page and `embedded` on the Dashboard.
function PositionsPanel({ embedded }) {
  const [mode, setMode] = React.useState('live'); // 'live' | 'paper' — Live tab first + default
  const [data, setData] = React.useState({ totalPnl: 0, count: 0, strategies: [] });
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(null); // key of the row currently exiting
  // Traded-leg history (closed trades) for the current tab, date-filtered.
  const [histOpen, setHistOpen] = React.useState(false);
  const [hist, setHist] = React.useState([]);
  const [histLoading, setHistLoading] = React.useState(false);
  const _today = new Date().toISOString().slice(0, 10);
  const _weekAgo = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
  const [histFrom, setHistFrom] = React.useState(_weekAgo);
  const [histTo, setHistTo] = React.useState(_today);

  const loadHist = React.useCallback((m, from, to) => {
    setHistLoading(true);
    fetch(API + '/positions/history?mode=' + m + '&from=' + from + '&to=' + to, { headers: getHeaders() })
      .then(r => r.json())
      // Show only THIS portal's trades (crypto vs NSE), matching the open list.
      .then(d => { if (d && Array.isArray(d.trades)) setHist(d.trades.filter(t => !!t.isCrypto === !!IS_CRYPTO_PORTAL)); })
      .catch(() => {})
      .finally(() => setHistLoading(false));
  }, []);
  React.useEffect(() => { if (histOpen) loadHist(mode, histFrom, histTo); }, [histOpen, mode, histFrom, histTo, loadHist]);

  const load = React.useCallback((m) => fetch(API + '/positions?mode=' + m, { headers: getHeaders() })
    .then(r => r.json())
    .then(d => { if (d && Array.isArray(d.strategies)) setData({ ...d, strategies: portalStrategies(d.strategies) }); })
    .catch(() => {})
    .finally(() => setLoading(false)), []);

  React.useEffect(() => {
    setLoading(true);
    load(mode);
    // Poll so P&L and the open-leg list stay live without a manual refresh.
    // Crypto runs 24/7 on the 1s fast loop (Delta's higher rate limits), so the
    // crypto portal polls every 1s; NSE keeps the gentler 4s cadence.
    const id = setInterval(() => load(mode), POLL_MS);
    return () => clearInterval(id);
  }, [mode, load]);

  const doExit = async (key, body, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    setBusy(key);
    try {
      const res = await fetch(API + '/positions/exit', { method: 'POST', headers: getHeaders(), body: JSON.stringify(body) });
      const d = await res.json();
      if (!res.ok) alert(d.message || 'Exit failed');
      await load(mode);
    } catch (e) { alert('Exit failed: ' + e.message); }
    finally { setBusy(null); }
  };
  const exitStrategy = (s) => doExit('strat:' + s.strategyId, { strategyId: s.strategyId },
    `Exit ALL legs of "${s.strategyName}"?` + (mode === 'live' ? '\n\nThis places real square-off orders with your broker.' : ''));
  const exitLeg = (s, leg) => doExit('leg:' + leg.tradeId, { strategyId: s.strategyId, instrument: leg.instrument },
    `Exit ${leg.instrument}?` + (mode === 'live' ? '\n\nThis places a real square-off order.' : ''));
  // Manual (broker-only) position — squared off directly via the broker, no strategy.
  const exitManual = (leg) => doExit('leg:' + leg.tradeId, { manual: true, instrument: leg.instrument, product: leg.product },
    `Exit manual position ${leg.instrument} (${leg.product || ''})?\n\nThis places a real square-off order in Zerodha.`);

  // Crypto P&L is USD; NSE is ₹. Crypto USD amounts can be fractional, so show 2dp.
  const money = n => (n >= 0 ? '+' : '-') + (IS_CRYPTO_PORTAL ? '$' : '₹')
    + Math.abs(n || 0).toFixed(IS_CRYPTO_PORTAL ? 2 : 0);
  const pnlColor = n => (n > 0 ? UI.green : n < 0 ? UI.red : UI.sub);
  // Spot/price level in USD (no +/- sign), e.g. "$64,250.00".
  const spotUsd = n => '$' + Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
  // Sum P&L over the portal-filtered strategies (the backend's totalPnl spans both
  // portals' trades and mixes currencies), so the header total stays portal-correct.
  const totalPnl = data.strategies.reduce((a, s) => a + (s.pnl || 0), 0);

  return (
    <>
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: UI.text, margin: 0 }}>📌 Open Positions</h2>
          <a href="/positions" style={{ fontSize: 13, fontWeight: 700, color: UI.blue, textDecoration: 'none' }}>View all →</a>
        </div>
      )}

      {/* Paper / Live tabs + total P&L */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {[['live', '🚀 Live Trade', UI.green], ['paper', '📝 Paper Trade', UI.blue]].map(([m, label, col]) => (
          <button key={m} onClick={() => setMode(m)}
            style={{ padding: '11px 22px', fontSize: 14, fontWeight: 800, borderRadius: 12, border: mode === m ? `2px solid ${col}` : `1px solid ${UI.border}`, background: mode === m ? '#fff' : '#f8fafc', color: mode === m ? col : UI.sub, cursor: 'pointer', boxShadow: mode === m ? '0 2px 10px rgba(15,23,42,0.08)' : 'none' }}>
            {label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, ...card({ padding: '9px 18px' }) }}>
          <span style={{ fontSize: 11.5, color: UI.muted, fontWeight: 700, letterSpacing: 0.5 }}>{mode === 'paper' ? 'PAPER' : 'LIVE'} P&L</span>
          <span style={{ fontSize: 19, fontWeight: 900, color: pnlColor(totalPnl) }}>{money(totalPnl)}</span>
        </div>
      </div>

      {loading ? (
        <div style={{ ...card({ padding: 60 }), textAlign: 'center', color: UI.muted }}>Loading positions…</div>
      ) : data.strategies.length === 0 ? (
        <div style={{ ...card({ padding: 70 }), textAlign: 'center', color: UI.muted }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151', marginBottom: 6 }}>No open {mode} positions</div>
          <div style={{ fontSize: 13 }}>Deploy a strategy in {mode === 'paper' ? 'Paper' : 'Live'} mode to see its positions here.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {data.strategies.map(s => (
            <div key={s.strategyId} style={card({ padding: 0, overflow: 'hidden' })}>
              {/* strategy header: name + rolled-up P&L + Exit All */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 20px', borderBottom: `1px solid ${UI.border}`, background: IS_CRYPTO_PORTAL ? '#1e2329' : '#fbfcfe' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, fontSize: 15.5, color: UI.text }}>{s.strategyName}</span>
                    <span style={{ padding: '2px 9px', borderRadius: 6, fontSize: 10, fontWeight: 900, letterSpacing: 0.5, background: s.source === 'manual' ? '#fef3c7' : '#e0e7ff', color: s.source === 'manual' ? '#92400e' : '#3730a3' }}>
                      {s.source === 'manual' ? '✋ MANUAL (ZERODHA)' : '🤖 ALGO'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: UI.muted, fontWeight: 600, marginTop: 4 }}>
                    {s.legs.length} open leg{s.legs.length > 1 ? 's' : ''}{s.source !== 'manual' && s.exitCondition ? ' · exit on: ' + s.exitCondition : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10.5, color: UI.muted, fontWeight: 700 }}>P&L</div>
                    <div style={{ fontSize: 17, fontWeight: 900, color: pnlColor(s.pnl) }}>{money(s.pnl)}</div>
                  </div>
                  {s.source !== 'manual' && s.strategyId && (
                    <button disabled={busy === 'strat:' + s.strategyId} onClick={() => exitStrategy(s)}
                      style={{ padding: '10px 16px', background: UI.red, color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: 'pointer', opacity: busy === 'strat:' + s.strategyId ? 0.6 : 1 }}>
                      {busy === 'strat:' + s.strategyId ? 'Exiting…' : '✕ Exit All'}
                    </button>
                  )}
                </div>
              </div>
              {/* SL / delta-adjust summary — live-computed spot levels for when this
                  strategy will roll a leg or fully exit (only set for live NSE algo
                  strategies with adjust/SL logic configured). */}
              {s.slSummary && s.slSummary.length > 0 && (
                <div style={{ margin: '12px 20px 0', padding: '11px 14px', borderRadius: 10, background: IS_CRYPTO_PORTAL ? '#1a2230' : '#f0f5ff', border: `1px solid ${IS_CRYPTO_PORTAL ? '#2b3646' : '#dbe6ff'}` }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: UI.blue, letterSpacing: 0.4, marginBottom: 6 }}>📐 SL / ADJUST SUMMARY (live-computed)</div>
                  {s.slSummary.map((line, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: UI.sub, lineHeight: 1.6 }}>• {line}</div>
                  ))}
                </div>
              )}
              {/* legs */}
              <div style={{ padding: '6px 10px' }}>
                {s.legs.map(leg => (
                  <div key={leg.tradeId} style={{ padding: '10px 10px', borderRadius: 10, borderBottom: `1px solid ${UI.bg}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.7fr 0.8fr 1fr 1fr 1fr auto', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <div style={{ fontWeight: 700, color: UI.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={leg.instrument}>{leg.instrument}</div>
                      <div><span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 800, background: leg.action === 'SELL' ? '#fee2e2' : '#dcfce7', color: leg.action === 'SELL' ? '#991b1b' : '#15803d' }}>{leg.action}</span></div>
                      <div style={{ color: UI.sub }}>Qty {leg.qty}</div>
                      <div style={{ color: UI.sub }}>Entry {Number(leg.entryPrice || 0).toFixed(2)}</div>
                      <div style={{ color: UI.text, fontWeight: 700 }}>LTP {leg.ltp ? Number(leg.ltp).toFixed(2) : '—'}</div>
                      <div style={{ fontWeight: 800, color: pnlColor(leg.pnl) }}>{money(leg.pnl)}</div>
                      {leg.source === 'manual' ? (
                        <button disabled={busy === 'leg:' + leg.tradeId} onClick={() => exitManual(leg)}
                          style={{ padding: '7px 14px', background: UI.card, color: '#b45309', border: '1.5px solid #fcd34d', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer', opacity: busy === 'leg:' + leg.tradeId ? 0.6 : 1 }}>
                          {busy === 'leg:' + leg.tradeId ? '…' : 'Exit'}
                        </button>
                      ) : (
                        <button disabled={busy === 'leg:' + leg.tradeId} onClick={() => exitLeg(s, leg)}
                          style={{ padding: '7px 14px', background: UI.card, color: UI.red, border: '1.5px solid #fecaca', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer', opacity: busy === 'leg:' + leg.tradeId ? 0.6 : 1 }}>
                          {busy === 'leg:' + leg.tradeId ? '…' : 'Exit'}
                        </button>
                      )}
                    </div>
                    {leg.source !== 'manual' && (leg.isCrypto || leg.hasDetails) && (() => {
                      const C = leg.isCrypto;
                      // spot label: crypto → BTC/ETH; NSE → underlying index (NIFTY/BANKNIFTY)
                      const asset = C ? (/ETH/i.test(leg.instrument) ? 'ETH' : 'BTC') : (leg.underlying || 'NIFTY');
                      // crypto prices in $ (en-US); NSE premium prices in ₹ (en-IN). The index
                      // spot is shown as plain points (no currency symbol).
                      const px = C ? spotUsd : (n => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }));
                      const spotTxt = !leg.spot ? '—' : C ? spotUsd(leg.spot) : Number(leg.spot).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                      // Light pastel chips on the NSE portal (light theme), dark on crypto.
                      const P = C
                        ? { spot: ['#0b1f33', '#7dd3fc'], tgt: ['#0f2418', '#86efac'], sl: ['#2a1316', '#fca5a5'], cost: ['#2a2410', '#fcd34d'] }
                        : { spot: ['#eff6ff', '#1d4ed8'], tgt: ['#ecfdf5', '#15803d'], sl: ['#fef2f2', '#b91c1c'], cost: ['#fffbeb', '#b45309'] };
                      const chip = ([bg, col], label, val) => (
                        <span style={{ background: bg, color: col, padding: '3px 10px', borderRadius: 7, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap', border: `1px solid ${col}33` }}>
                          {label} <b style={{ fontWeight: 900 }}>{val}</b>
                        </span>
                      );
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 9, alignItems: 'center' }}>
                          {chip(P.spot, `📈 ${asset} spot`, spotTxt)}
                          {chip(P.tgt, '🎯 Target', leg.targetPrice ? `${px(leg.targetPrice)} → ${money(leg.targetPnl)}` : '—')}
                          {chip(P.sl, '🛑 SL', leg.slPrice ? `${px(leg.slPrice)} → ${money(leg.slPnl)}` : '—')}
                          {leg.costArmSpot ? chip(P.cost, '🛡️ SL→Cost @', `${px(leg.costArmSpot)} → BE ${px(leg.costLevel)}`) : null}
                          {chip(P.tgt, '▲ Peak', money(leg.pnlHigh))}
                          {chip(P.sl, '▼ Low', money(leg.pnlLow))}
                        </div>
                      );
                    })()}
                    {leg.remark && (
                      <div style={{ fontSize: 11.5, color: leg.source === 'manual' ? '#92400e' : UI.muted, marginTop: 7, fontWeight: 600, lineHeight: 1.4 }}>
                        {leg.source === 'manual' ? '✋ ' : '🤖 '}{leg.remark}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Traded history (closed legs) — this tab (live/paper), date-filtered ── */}
      <div style={{ marginTop: 24 }}>
        <button onClick={() => setHistOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: histOpen ? '#fff' : UI.card, color: UI.text, border: `1px solid ${UI.border}`, borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
          📜 {mode === 'paper' ? 'Paper' : 'Live'} Trade History <span style={{ fontSize: 12, color: UI.muted }}>{histOpen ? '▲' : '▼'}</span>
        </button>
        {histOpen && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
              <label style={{ fontSize: 12.5, color: UI.sub, fontWeight: 700 }}>From</label>
              <input type="date" value={histFrom} max={histTo} onChange={e => setHistFrom(e.target.value)} style={{ padding: '8px 10px', border: `1px solid ${UI.border}`, borderRadius: 8, fontSize: 13, background: UI.card, color: UI.text }} />
              <label style={{ fontSize: 12.5, color: UI.sub, fontWeight: 700 }}>To</label>
              <input type="date" value={histTo} min={histFrom} onChange={e => setHistTo(e.target.value)} style={{ padding: '8px 10px', border: `1px solid ${UI.border}`, borderRadius: 8, fontSize: 13, background: UI.card, color: UI.text }} />
              <button onClick={() => loadHist(mode, histFrom, histTo)} style={{ padding: '8px 14px', background: UI.blue, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>Refresh</button>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: UI.muted, fontWeight: 700 }}>{hist.length} closed leg{hist.length !== 1 ? 's' : ''}{hist.length ? ` · P&L ${money(hist.reduce((a, t) => a + (t.pnl || 0), 0))}` : ''}</span>
            </div>
            {histLoading ? (
              <div style={{ ...card({ padding: 40 }), textAlign: 'center', color: UI.muted }}>Loading history…</div>
            ) : hist.length === 0 ? (
              <div style={{ ...card({ padding: 40 }), textAlign: 'center', color: UI.muted }}>No closed {mode} trades in this range.</div>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                {Object.entries(hist.reduce((g, t) => { const d = String(t.closedAt || t.tradeDate || '').slice(0, 10); (g[d] = g[d] || []).push(t); return g; }, {})).sort((a, b) => a[0] < b[0] ? 1 : -1).map(([day, legs]) => {
                  const dayPnl = legs.reduce((a, t) => a + (t.pnl || 0), 0);
                  return (
                    <div key={day} style={card({ padding: 0, overflow: 'hidden' })}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: `1px solid ${UI.border}`, background: IS_CRYPTO_PORTAL ? '#1e2329' : '#fbfcfe' }}>
                        <span style={{ fontWeight: 800, fontSize: 14, color: UI.text }}>{day} <span style={{ fontSize: 11.5, color: UI.muted, fontWeight: 600 }}>· {legs.length} leg{legs.length > 1 ? 's' : ''}</span></span>
                        <span style={{ fontSize: 14, fontWeight: 900, color: pnlColor(dayPnl) }}>{money(dayPnl)}</span>
                      </div>
                      <div style={{ padding: '4px 10px' }}>
                        {legs.map(leg => (
                          <div key={leg.tradeId} style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.7fr 0.7fr 1.4fr 1fr 1.7fr', alignItems: 'center', gap: 8, padding: '9px 8px', borderBottom: `1px solid ${UI.bg}`, fontSize: 12.5 }}>
                            <div style={{ fontWeight: 700, color: UI.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={leg.instrument}>{leg.instrument}</div>
                            <div><span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 800, background: leg.action === 'SELL' ? '#fee2e2' : '#dcfce7', color: leg.action === 'SELL' ? '#991b1b' : '#15803d' }}>{leg.action}</span></div>
                            <div style={{ color: UI.sub }}>Qty {leg.qty}</div>
                            <div style={{ color: UI.sub }} title="Entry → Exit">{Number(leg.entryPrice || 0).toFixed(2)} → {leg.exitPrice != null ? Number(leg.exitPrice).toFixed(2) : '—'}</div>
                            <div style={{ fontWeight: 800, color: pnlColor(leg.pnl) }}>{money(leg.pnl)}</div>
                            <div style={{ color: UI.muted, fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={leg.exitNote || leg.exitReason}>{leg.exitNote || leg.exitReason || '—'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Positions() {
  return (
    <Layout>
      <PageHead title="Positions" subtitle="Open positions, live P&L and one-click manual exit" icon="📌"
        right={<span style={{ fontSize: 12.5, fontWeight: 700, background: 'rgba(255,255,255,0.18)', padding: '6px 14px', borderRadius: 20 }}>● auto-refresh {POLL_MS / 1000}s</span>} />
      <PositionsPanel />
    </Layout>
  );
}

// ── Strategy-card summary helpers (show full config at a glance) ───────────
// One leg's strike, mirroring the engine's selection criteria.
const legStrikeLabel = (leg) => {
  const crit = String(leg.strikeCriteria || "").toUpperCase();
  const v = leg.strikeValue;
  if (crit === "DELTA_NEAR" || leg.strikeBy === "delta") return "Δ" + (v != null ? v : leg.deltaValue);
  if (crit === "CP") return "≈₹" + v;
  if (crit === "CP_GTE") return "≥₹" + v;
  if (crit === "CP_LTE") return "≤₹" + v;
  if (crit === "ATM_PT") return "ATM" + (v >= 0 ? "+" : "") + v + "pt";
  if (crit === "ATM_PERCENT") return "ATM" + (v >= 0 ? "+" : "") + v + "%";
  if (leg.strikeBy === "points" && leg.strikeOffset) return "ATM" + (leg.strikeOffset >= 0 ? "+" : "") + leg.strikeOffset;
  return String(leg.strikeType || "ATM").replace("_", " ");
};
// Compact description of an indicator operand / condition.
const operandLabel = (o) => {
  if (!o) return "?";
  const t = o.type || "";
  if (t === "Number") return String(o.value);
  if (t === "Formula") return o.expr || "formula";
  if (t === "Candle") return o.price || "close";
  if (t === "MACD") return `MACD(${o.fast || 12},${o.slow || 26},${o.signal || 9})`;
  if (t === "RSI") return `RSI(${o.period || 14})`;
  if (/Moving Average|EMA|SMA/i.test(t)) return `${t}(${o.period || ""})`;
  return o.period ? `${t}(${o.period})` : t;
};
const condLabel = (c) => `${operandLabel(c.left)} ${(c.comparator || "").replace(/_/g, " ")} ${operandLabel(c.right)}`;
const entryLogicSummary = (s) => {
  const ic = s.indicatorConfig;
  if (ic?.enabled && ic.entryConditions?.length) return ic.entryConditions.map(condLabel).join(`  ${ic.entryLogic || "AND"}  `);
  // Inside-bar / mother-candle breakout — entry is on the breakout, NOT a fixed
  // clock time, so never show "Time-based @ 09:20" for these.
  if (s.insideBar?.enabled) {
    const tf = s.insideBar.timeframe || s.insideBar.tf || s.timeframe || s.candleInterval || "";
    const kind = s.insideBar.motherBreakout ? "Mother-candle breakout" : "Inside-bar breakout";
    return kind + (tf ? ` (${tf})` : "");
  }
  const t = s.entryTime || s.startTime;
  if (t) return "Time-based @ " + t;
  // Crypto strategies have no NSE 09:20 default — don't fabricate one.
  return /^crypto/.test(s.assetClass || "") ? "On-signal entry" : "Time-based @ 09:20";
};
const fmtRisk = (type, val) => {
  if (!val) return null;
  const t = String(type || "amount").toLowerCase();
  if (t.includes("percent")) return val + "%";
  if (t.includes("point")) return val + "pt";
  return "₹" + Number(val).toLocaleString("en-IN");
};
// Enabled advanced features → short chips.
const strategyFeatures = (s) => {
  const f = [];
  if (s.rollAdjust?.enabled) f.push("Roll adjust");
  if (s.deltaAdjust?.enabled) f.push("Δ-Neutral");
  if (s.reEntryOnSL?.enabled) f.push("Re-entry SL");
  if (s.reExecuteOnTarget?.enabled) f.push("Re-exec target");
  if (s.waitAndTrade?.enabled) f.push("Wait & Trade");
  if (s.trailSlType && s.trailSlType !== "disabled") f.push("Trail SL");
  if (s.moveSLtoCost) f.push("SL→Cost");
  if (s.prePunchSL) f.push("Pre-punch SL");
  if (s.lockFixProfit?.enabled) f.push("Lock profit");
  if (s.trailProfit?.enabled) f.push("Trail profit");
  if (s.lockAndTrail?.enabled) f.push("Lock & Trail");
  if (s.mtmLossLimit?.enabled) f.push("MTM loss");
  if (s.mtmProfitLock?.enabled) f.push("MTM lock");
  if (s.premiumDiff?.enabled) f.push("Premium diff");
  if (s.exitAllOnSL) f.push("Exit-all on SL");
  if (s.exitAllOnTarget) f.push("Exit-all on target");
  return f;
};

// Auto-generated plain-language (Hinglish) explanation of HOW a strategy works — entry
// kab/kaise, legs, exit kab/kaise, aur poora logic — built from the SAME config fields the
// engine/backtest read. Returns [{ h, items: [] }] sections for the card's info panel.
const explainStrategy = (s) => {
  const sec = [];
  const tf = (t) => ({ '1min': '1-min', '5min': '5-min', '15min': '15-min', '30min': '30-min', '60min': '1-hour', '1hour': '1-hour', '2hour': '2-hour', '4hour': '4-hour', '1day': 'daily' }[String(t)] || t || '5-min');
  const ac = String(s.assetClass || 'option');
  const isCrypto = ac.startsWith('crypto'), isPerp = ac === 'crypto_perp';
  const cur = isCrypto ? '$' : '₹';
  const tz = isCrypto ? ' UTC' : '';
  const und = s.instrument || s.underlying || 'NIFTY';
  const legs = (s.legs || []).filter(Boolean);
  const lt = (l) => (String(l.callPut || l.optionType || 'CE').toUpperCase().includes('P') ? 'PE' : 'CE');
  const act = (l) => (String(l.buySell || l.action || 'sell').toUpperCase().startsWith('S') ? 'SELL' : 'BUY');
  const ib = s.insideBar, cb = s.candleBreakout, ic = s.indicatorConfig, eb = s.emaBreakout;
  const ru = (cfg) => cfg && cfg.type === 'rMultiple' ? `${cfg.value}× R (candle range)` : cfg && cfg.type === 'percent' ? `${cfg.value}%` : `${cfg ? cfg.value : 0} pt`;

  // Overview
  const kind = isPerp ? `${und} perpetual (futures)` : isCrypto ? `${und} options (Delta)` : ac === 'equity' ? `${und} cash/equity` : `${und} options`;
  sec.push({ h: '📌 Overview', items: [
    `Ye ${kind} pe trade karti hai. Product: ${s.productType || s.orderType || 'MIS'}.` + (s.days && s.days.length && s.days.length < 7 ? ` Sirf in dino: ${s.days.map(d => d.slice(0, 3)).join(', ')}.` : ''),
  ] });

  // Entry
  const entry = [];
  if (ib?.enabled) {
    const ref = ib.motherBreakout ? 'MOTHER (bahar/outer)' : 'BABY (andar/inside)';
    entry.push(`Inside-bar setup (${tf(ib.timeframe)} candles): pehle ek "mother" candle, phir uske ANDAR ek "baby" (inside) candle banti hai${ib.minConsecutive > 1 ? ` (${ib.minConsecutive} consecutive inside bars chahiye)` : ''}.`);
    if (ib.directional) {
      const buf = ib.breakoutBuffer ? ` (buffer ${ib.breakoutBuffer}${ib.breakoutBufferType === 'percent' ? '%' : ' pt'})` : '';
      entry.push(`Entry tab jab spot us ${ref} candle ka High/Low tode${buf}, ${ib.triggerMode === 'touch' ? 'touch pe instant' : 'candle CLOSE pe'}. High toote → ${isPerp ? 'BUY (long)' : 'CE side'}, Low toote → ${isPerp ? 'SELL (short)' : 'PE side'} — sirf breakout-side ki EK leg lagti hai.`);
    } else {
      entry.push(`Non-directional: inside bar bante hi dono legs ek saath lagti hain (short-straddle type).`);
    }
    if (ib.maxBabyPct) entry.push(`Quality filter: baby candle range mother range ke ${ib.maxBabyPct}% se choti honi chahiye (tight contraction).`);
  } else if (cb?.enabled) {
    entry.push(`Candle-breakout (${tf(cb.timeframe)}): ${cb.reference === 'prev' ? 'pichhli poori-hui candle' : 'din ki PEHLI candle (ORB)'} ka High/Low tootne pe entry${cb.directional ? ' (High→Buy, Low→Sell)' : ''}.`);
  } else if (eb?.enabled) {
    entry.push(`10-EMA breakout (${tf(eb.timeframe)}, EMA ${eb.period}): EMA trend ke direction me candle breakout pe entry.`);
  } else if (ic?.enabled && ic.entryConditions?.length) {
    entry.push(`Indicator conditions (${ic.timeframe || '5min'}) poori hone pe entry: ${ic.entryConditions.map(condLabel).join(`  ${ic.entryLogic || 'AND'}  `)}.`);
  } else {
    entry.push(`Time-based: ${s.entryTime || s.startTime || '09:20'}${tz} baje entry (jaise hi market/time aaye).`);
  }
  if ((ib?.enabled || cb?.enabled || ic?.enabled) && (s.entryTime || s.startTime)) entry.push(`Entry window ${s.entryTime || s.startTime} se shuru${s.noTradeAfter ? ` · ${s.noTradeAfter} ke baad nayi entry nahi` : ''}.`);
  if (s.waitAndTrade?.enabled) entry.push(`Wait & Trade: signal ke baad ${s.waitAndTrade.type === 'time' ? `${s.waitAndTrade.minutes} min ruk ke` : `${s.waitAndTrade.value} ${s.waitAndTrade.type} move pe`} entry.`);
  if (s.premiumDiff?.enabled) entry.push(`Premium-diff: dono legs ka premium ${s.premiumDiff.points} pt ke andar aaye tab entry.`);
  sec.push({ h: '🟢 Entry — kab & kaise', items: entry });

  // Legs
  if (legs.length) {
    const items = legs.map((l) => {
      const inst = isPerp ? und : lt(l);
      const strike = isPerp ? '' : ' ' + legStrikeLabel(l);
      const sl = fmtRisk(l.slType, l.slValue ?? l.sl), tp = fmtRisk(l.tpType, l.targetValue ?? l.tp);
      const risk = [sl ? `SL ${sl}` : null, tp ? `Target ${tp}` : null].filter(Boolean).join(' · ');
      return `${act(l)} ${inst}${strike} × ${l.qty || 1}${risk ? ` — ${risk}` : ''}`;
    });
    if (ib?.enabled && ib.directional) items.unshift('Directional: har trade me breakout-side ki SIRF ek leg lagti hai (dono saath nahi).');
    sec.push({ h: '📊 Legs — kya trade hota hai', items });
  }

  // Exit
  const exit = [];
  if (ib?.enabled) {
    if (ib.babyCandleSL) exit.push(`SL: ${ib.motherBreakout ? 'mother' : 'baby'} candle ke opposite extreme pe (spot-based) — hit hote hi poori strategy square-off.`);
    if (ib.motherCandleSL) exit.push(`Mother-candle SL: outer candle ke opposite extreme pe (wider structural stop).`);
    if (ib.target?.enabled) exit.push(`Target: breakout level se ${ru(ib.target)} favourable move pe profit book.`);
    if (ib.trail?.enabled) exit.push(`Trailing stop: best price ke ${ru({ type: ib.trail.type, value: ib.trail.gap })} peeche; ${ru({ type: ib.trail.type, value: ib.trail.arm })} move ke baad arm.`);
    if (ib.invalidateOnEntry) exit.push(`Whipsaw guard: entry ke ${ib.invalidateCandles} candle me opposite side bhi toote → trade invalid → exit.`);
    if (ib.dailyGuard?.enabled) exit.push(`Daily circuit-breaker: din me ${ib.dailyGuard.maxTrades || '∞'} trade / ${ib.dailyGuard.maxLoss ? cur + ib.dailyGuard.maxLoss : '∞'} loss ke baad us din band.`);
    if (ib.reEntryOnSL?.enabled) exit.push(`Re-entry: SL ke baad sirf ${ib.reEntryOnSL.times} aur trade, phir band.`);
  }
  const oSl = fmtRisk(s.overallSlType, s.overallSlValue), oTgt = fmtRisk(s.overallTargetType, s.overallTargetValue);
  if (oSl) exit.push(`Overall SL: total loss ${oSl} pe sab exit.`);
  if (oTgt) exit.push(`Overall target: total profit ${oTgt} pe sab exit.`);
  if (s.moveSLtoCost) exit.push(`Move-SL-to-cost: ${s.moveSLtoCostAt || 50}${String(s.moveSLtoCostAtUnit || 'percent') === 'points' ? ' pts' : '%'} profit pe leg SL ko break-even pe shift.`);
  if (s.exitAllOnSL) exit.push(`Koi ek leg SL hit → saari legs exit.`);
  if (s.exitAllOnTarget) exit.push(`Koi ek leg target hit → saari legs exit.`);
  if (s.trailSlType && s.trailSlType !== 'disabled') exit.push(`Overall trail: ${cur}${s.lockProfitAt} profit lock, phir ${cur}${s.trailBy} se trail (step ${cur}${s.trailStep}).`);
  if (s.reEntryOnSL?.enabled) exit.push(`Re-entry on SL: SL ke baad ${s.reEntryOnSL.times}× dobara entry.`);
  if (s.reExecuteOnTarget?.enabled) exit.push(`Re-execute on target: target ke baad ${s.reExecuteOnTarget.times}× dobara.`);
  if (ic?.enabled && ic.exitConditions?.length) exit.push(`Indicator exit: ${ic.exitConditions.map(condLabel).join(`  ${ic.exitLogic || 'OR'}  `)}.`);
  const sq = s.exitTime || s.squareOff;
  if (sq) exit.push(`Square-off: ${sq}${tz} baje sab positions band.`);
  else if (isCrypto) exit.push(`Koi fixed square-off time nahi (24/7) — position SL/Target/signal tak chalti hai, UTC days ke paar bhi.`);
  if (!exit.length) exit.push('Koi explicit exit set nahi — sirf square-off time / signal pe exit hoga.');
  sec.push({ h: '🔴 Exit — kab & kaise', items: exit });

  // Extra adjustments
  const extra = [];
  if (s.rollAdjust?.enabled) extra.push(`Roll-adjust: short leg ke paas spot aaye to strangle re-center.`);
  if (s.eodRoll?.enabled) extra.push(`EOD roll: ${s.eodRoll.time || '15:20'} pe strangle re-center.`);
  if (s.deltaAdjust?.enabled) extra.push(`Delta-neutral: |Δ| imbalance pe legs rebalance.`);
  if (s.nightHedge?.enabled) extra.push(`Night-hedge: overnight protective wings.`);
  if (s.slTgtReenter?.enabled) extra.push(`SL/Target ke baad exit-all & re-enter (cycles).`);
  if (s.srAutoLevels?.enabled) extra.push(`Auto SL/Target agle Pivot (S/R) level pe.`);
  if (extra.length) sec.push({ h: '⚙️ Extra logic', items: extra });

  return sec;
};

// POST a deploy with the chosen broker (live routing). brokerId may be undefined
// (Paper, or a single-broker setup) — the engine then uses the connected broker.
async function deployStrategyReq(id, mode, brokerId) {
  const res = await fetch(API + '/strategies/' + id + '/deploy', {
    method: 'POST', headers: getHeaders(),
    body: JSON.stringify({ mode, brokerId: brokerId || null }),
  });
  return res.json();
}

function Strategies() {
  const [strategies, setStrategies] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [menuOpen, setMenuOpen] = React.useState(null); // strategy id whose ⋮ menu is open
  const [infoOpen, setInfoOpen] = React.useState(null); // strategy id whose "how it works" panel is open
  const brokers = useBrokers();
  const [pickFor, setPickFor] = React.useState(null); // strategy id awaiting a broker choice
  const [tab, setTab] = React.useState('created'); // created | deployed | template
  const [templates, setTemplates] = React.useState([]);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [exiting, setExiting] = React.useState(null); // strategy id being exited + undeployed

  const loadTemplates = React.useCallback(() => {
    fetch(API + '/strategies/templates', { headers: getHeaders() })
      .then(r => r.json()).then(d => Array.isArray(d) && setTemplates(portalStrategies(d))).catch(() => {});
  }, []);

  React.useEffect(() => {
    const load = () => fetch(API + '/strategies', { headers: getHeaders() })
      .then(r => r.json()).then(d => Array.isArray(d) && setStrategies(portalStrategies(d))).catch(() => {})
      .finally(() => setLoading(false));
    load();
    loadTemplates();
    authAPI.me().then(r => setIsAdmin(!!r.data.isAdmin)).catch(() => {});
    // Poll so a strategy auto-undeployed after a manual Zerodha exit flips back
    // to a redeployable state here without needing a manual refresh.
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  // Split into the three tabs: not-yet-deployed (drafts), currently deployed.
  const created = strategies.filter(s => !s.isDeployed);
  const deployed = strategies.filter(s => s.isDeployed);
  const visible = tab === 'deployed' ? deployed : tab === 'created' ? created : [];

  const deleteStrategy = async (id) => {
    if (!window.confirm('Delete this strategy?')) return;
    await fetch(API + '/strategies/' + id, { method: 'DELETE', headers: getHeaders() });
    setStrategies(p => p.filter(s => s._id !== id));
  };

  const doDeploy = async (id, mode, brokerId) => {
    const d = await deployStrategyReq(id, mode, brokerId);
    if (d._id) {
      setStrategies(p => p.map(s => s._id === id ? d : s));
      const bname = brokerId ? (brokers.find(b => b._id === brokerId)?.name || 'broker') : '';
      alert('Strategy deployed (' + mode + (bname ? ' · ' + bname : '') + ')!');
    } else alert(d.message || 'Deploy failed');
  };

  const deployStrategy = async (id, mode) => {
    if (mode !== 'Live') return doDeploy(id, mode);
    // Always open the Go Live modal — broker choice + Terms & Conditions accept.
    setPickFor(id);
  };

  // Exit & undeploy a deployed strategy: squares off ALL its open positions at the
  // broker (real orders for Live, simulated for Paper) and flips it back to a
  // redeployable draft. Same backend path as the Positions page "Exit All".
  const exitStrategy = async (s) => {
    const live = s.mode !== 'Paper';
    if (!window.confirm(`Exit & undeploy "${s.name || 'strategy'}"?\n\nSaari open positions square off hongi` + (live ? ' (real broker orders)' : ' (paper)') + ' aur strategy undeploy ho jayegi.')) return;
    setExiting(s._id);
    try {
      const res = await fetch(API + '/positions/exit', { method: 'POST', headers: getHeaders(), body: JSON.stringify({ strategyId: s._id }) });
      const d = await res.json();
      if (!res.ok) { alert(d.message || 'Exit failed'); return; }
      setStrategies(p => p.map(x => x._id === s._id ? { ...x, isDeployed: false, status: 'Stopped' } : x));
    } catch (e) { alert('Exit failed: ' + e.message); }
    finally { setExiting(null); }
  };

  const duplicateStrategy = async (id) => {
    setMenuOpen(null);
    const res = await fetch(API + '/strategies/' + id + '/duplicate', { method: 'POST', headers: getHeaders() });
    const d = await res.json();
    if (d._id) setStrategies(p => [d, ...p]); else alert(d.message || 'Could not duplicate');
  };

  // Admin: publish/unpublish a created strategy as a public template (Template tab,
  // visible to all users). On publish, prompt for the short blurb shown on the card.
  const publishTemplate = async (s) => {
    setMenuOpen(null);
    const publish = !s.isTemplate;
    let description = '';
    if (publish) {
      description = window.prompt('Short description for this template (shown to all users):', s.templateDescription || '');
      if (description === null) return; // cancelled
    }
    const res = await fetch(API + '/strategies/' + s._id + '/publish-template', {
      method: 'POST', headers: getHeaders(), body: JSON.stringify({ publish, description }),
    });
    const d = await res.json();
    if (d._id) {
      setStrategies(p => p.map(x => x._id === s._id ? d : x));
      loadTemplates();
      alert(publish ? 'Published to the Template tab — visible to all users.' : 'Removed from templates.');
    } else alert(d.message || 'Could not update template');
  };

  // Admin: remove a template straight from the Template tab (unpublish its source
  // strategy). Same endpoint as publishTemplate, but driven off the template card.
  const removeTemplate = async (t) => {
    const id = t._id || t.id;
    if (!window.confirm('Remove "' + t.name + '" from templates? It will no longer be visible to users.')) return;
    const res = await fetch(API + '/strategies/' + id + '/publish-template', {
      method: 'POST', headers: getHeaders(), body: JSON.stringify({ publish: false }),
    });
    const d = await res.json();
    if (d._id) {
      setStrategies(p => p.map(x => x._id === id ? d : x));
      loadTemplates();
    } else alert(d.message || 'Could not remove template');
  };

  // Any user: clone a published template into their own Created strategies, then
  // open it in the builder to review/tweak before deploying.
  const useTemplate = async (t) => {
    const res = await fetch(API + '/strategies/templates/' + (t._id || t.id) + '/use', { method: 'POST', headers: getHeaders() });
    const d = await res.json();
    if (d._id) { window.location.href = '/strategy-builder?id=' + d._id; }
    else alert(d.message || 'Could not use template');
  };

  if (loading) return <Layout><div style={{textAlign:'center',padding:60,color:'#9ca3af'}}>Loading strategies...</div></Layout>;

  return (
    <Layout>
      <PageHead title="My Strategies" subtitle="Build, edit, backtest and deploy your strategies" icon="📈"
        right={<a href="/strategy-builder" style={{ background: UI.card, color: UI.blue, borderRadius: 12, padding: '12px 20px', fontSize: 14, fontWeight: 800, textDecoration: 'none' }}>+ Create Strategy</a>} />

      {/* tabs: Created · Deployed · Template */}
      <div style={{display:'flex',gap:4,marginBottom:18,borderBottom:`1px solid ${UI.border}`,flexWrap:'wrap'}}>
        {[['created','Created Strategy',created.length],['deployed','Deployed Strategy',deployed.length],['template','Template',templates.length]].map(([k,label,n]) => (
          <button key={k} onClick={() => setTab(k)} style={{background:'transparent',border:'none',borderBottom:tab===k?`2.5px solid ${UI.blue}`:'2.5px solid transparent',padding:'11px 14px',marginBottom:-1,cursor:'pointer',fontSize:13.5,fontWeight:800,color:tab===k?UI.blue:UI.sub,display:'flex',alignItems:'center',gap:7}}>
            {label}
            <span style={{fontSize:11,fontWeight:800,background:tab===k?'#dbeafe':'#f1f5f9',color:tab===k?UI.blue:UI.muted,borderRadius:20,padding:'1px 8px'}}>{n}</span>
          </button>
        ))}
      </div>

      {/* Template tab — predefined starting points */}
      {tab === 'template' && (
        templates.length === 0 ? (
          <div style={{textAlign:'center',padding:60,color:UI.muted,fontSize:14}}>No templates available.</div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))',gap:16}}>
            {templates.map(t => (
              <div key={t.id} style={card({padding:18,display:'flex',flexDirection:'column'})}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                  <div style={{width:38,height:38,borderRadius:10,background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.14)' : 'linear-gradient(135deg,#eef2ff,#dbeafe)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>📋</div>
                  <div style={{fontWeight:800,fontSize:14.5,color:UI.text,lineHeight:1.3}}>{t.name}</div>
                </div>
                <div style={{fontSize:12.5,color:UI.sub,lineHeight:1.6,flex:1,marginBottom:16}}>{t.description}</div>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={() => useTemplate(t)} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'10px',flex:1,background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.10)' : '#eff6ff',color:UI.blue,border:'1.5px solid #bfdbfe',borderRadius:10,fontSize:13,fontWeight:800,cursor:'pointer'}}>Use Template →</button>
                  {isAdmin && <button onClick={() => removeTemplate(t)} title="Remove this template (admin)" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:5,padding:'10px 14px',background:'#fef2f2',color:'#dc2626',border:'1.5px solid #fecaca',borderRadius:10,fontSize:13,fontWeight:800,cursor:'pointer'}}>🗑 Remove</button>}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Created / Deployed tab — empty state */}
      {tab !== 'template' && visible.length === 0 && (
        <div style={{background:UI.card,borderRadius:16,padding:64,border:`1px solid ${UI.border}`,textAlign:'center',color:'#9ca3af'}}>
          <div style={{fontSize:48,marginBottom:14}}>{tab==='deployed'?'🚀':'📊'}</div>
          <div style={{fontSize:16,fontWeight:800,marginBottom:6,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>{tab==='deployed'?'No deployed strategies':'No created strategies yet'}</div>
          <div style={{fontSize:13.5,marginBottom:22}}>{tab==='deployed'?'Deploy a strategy from the Created tab to see it here.':'Build your first automated trading strategy.'}</div>
          {tab!=='deployed' && <a href="/strategy-builder" style={{background:UI.blue,color:'#fff',padding:'11px 26px',borderRadius:10,textDecoration:'none',fontSize:14,fontWeight:700}}>+ Create Strategy</a>}
        </div>
      )}

      {tab !== 'template' && visible.length > 0 && (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <style>{`
            .strat-card{transition:box-shadow .18s var(--ar-ease), border-color .18s var(--ar-ease);}
            .strat-card:hover{box-shadow:0 8px 22px rgba(15,23,42,0.07) !important;border-color:#cbd5e1 !important;}
            .strat-menu-item{transition:background .12s ease;}
            .strat-menu-item:hover{background:#f1f5f9;}
            .strat-menu-item.danger:hover{background:#fef2f2;}
            .strat-deploy:hover{filter:brightness(1.06);}
            .strat-bt:hover{background:#eef2ff !important;}
          `}</style>
          {visible.map(s => {
            // Risk summary bits (leg + overall SL/TGT + MTM) shown inline on the row.
            const riskBits = (() => {
              const leg0 = s.legs?.[0]; const bits = [];
              if (leg0) { const sl = fmtRisk(leg0.slType, leg0.slValue), tp = fmtRisk(leg0.tpType, leg0.targetValue); if (sl) bits.push(`Leg SL ${sl}`); if (tp) bits.push(`TGT ${tp}`); }
              const oSl = fmtRisk(s.overallSlType, s.overallSlValue), oTp = fmtRisk(s.overallTargetType, s.overallTargetValue);
              if (oSl) bits.push(`Overall SL ${oSl}`); if (oTp) bits.push(`Overall TGT ${oTp}`);
              if (s.mtmLossLimit?.enabled && s.mtmLossLimit.amount) bits.push(`MTM loss ₹${Number(s.mtmLossLimit.amount).toLocaleString('en-IN')}`);
              if (s.mtmProfitLock?.enabled && s.mtmProfitLock.amount) bits.push(`MTM lock ₹${Number(s.mtmProfitLock.amount).toLocaleString('en-IN')}`);
              return bits;
            })();
            const feats = strategyFeatures(s);
            // Crypto-aware meta line — on a crypto strategy, don't leak NSE defaults
            // (09:20→15:15 times, "current week" expiry, MIS/NRML product). Perps have
            // NO expiry; crypto options use DAILY/NEXT DAILY/MONTHLY labels.
            const isCrypto = /^crypto/.test(s.assetClass || "");
            const isPerp = s.assetClass === "crypto_perp";
            // Quantity lives PER-LEG (leg.qty = 400/600/…); s.lots is a stale unused
            // "1" on most strategies, so never trust it for the count. Show the first
            // real leg qty (they're usually uniform), falling back to s.lots.
            const legQty = (s.legs || []).map(l => Number(l.qty) || 0).find(x => x > 0) || (s.lots || 1);
            const metaParts = [ (s.instrument || s.underlying || (isCrypto ? "BTCUSD" : "NIFTY")), `${legQty} lot` ];
            const eT = s.entryTime || s.startTime, xT = s.exitTime || s.squareOff;
            if (isCrypto) { if (eT || xT) metaParts.push(`${eT || "—"}→${xT || "—"}`); }
            else metaParts.push(`${eT || "09:20"}→${xT || "15:15"}`);
            if (!isPerp) {
              const exp = String(s.expiry || (isCrypto ? "current_week" : "current_week"));
              metaParts.push(isCrypto ? ({ current_week: "DAILY", next_week: "NEXT DAILY", monthly: "MONTHLY" }[exp] || exp.replace(/_/g, " ")) : exp.replace(/_/g, " "));
            }
            if (!isCrypto) metaParts.push(s.productType || s.orderType || "MIS");
            const metaLine = metaParts.join(" · ");
            return (
            <div className="strat-card" key={s._id} style={{position:'relative',background:UI.card,border:`1px solid ${UI.border}`,borderRadius:12,overflow: menuOpen === s._id ? 'visible' : 'hidden',boxShadow:'0 1px 3px rgba(15,23,42,0.05)',display:'flex'}}>
              {/* status accent bar (left, so each item reads as a row) */}
              <div style={{width:4,flexShrink:0,background:s.isDeployed?`linear-gradient(180deg,${UI.green},#34d399)`:'#e2e8f0'}} />
              <div style={{flex:1,minWidth:0,padding:'12px 14px'}}>
                {/* ── top line: identity + legs + actions, all in one horizontal row ── */}
                <div style={{display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
                  {/* identity: name + status + tags + compact meta */}
                  <div style={{flex:'1 1 240px',minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                      <span title={s.name} style={{fontWeight:800,fontSize:14.5,color:UI.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',letterSpacing:-0.2,maxWidth:260}}>{s.name}</span>
                      <span style={{display:'inline-flex',alignItems:'center',gap:5,padding:'3px 9px',borderRadius:20,background:s.isDeployed?'#dcfce7':'#f1f5f9',color:s.isDeployed?'#15803d':UI.sub,fontSize:10.5,fontWeight:800,letterSpacing:0.4}}>
                        <span style={{width:6,height:6,borderRadius:'50%',background:s.isDeployed?UI.green:UI.muted}} />
                        {s.isDeployed ? (s.mode === 'Paper' ? 'PAPER' : 'LIVE') : 'NOT DEPLOYED'}
                      </span>
                      {s.aiGenerated && <span title={s.aiApproved ? 'AI-generated · backtest approved' : 'AI-generated · not yet approved for live'} style={{padding:'2px 7px',borderRadius:20,background:s.aiApproved?'#ecfdf5':'#fef9c3',color:s.aiApproved?'#047857':'#a16207',fontSize:10,fontWeight:800}}>🤖 AI{s.aiApproved?' ✓':''}</span>}
                      {(s.indicatorConfig?.enabled) && <span style={{padding:'2px 7px',borderRadius:20,background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.10)' : '#f0f9ff',color:'#0369a1',fontSize:10,fontWeight:800}}>📐 Indicator</span>}
                      {s.isTemplate && <span title="Published as a public template (visible to all users)" style={{padding:'2px 7px',borderRadius:20,background: IS_CRYPTO_PORTAL ? 'rgba(14,165,233,0.14)' : '#ecfeff',color:'#0e7490',fontSize:10,fontWeight:800}}>🌐 Template</span>}
                    </div>
                    <div style={{fontSize:11.5,color:UI.sub,fontWeight:600,marginTop:5,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                      {metaLine}
                    </div>
                  </div>
                  {/* leg chips */}
                  {(s.legs?.length > 0) && (
                    <div style={{display:'flex',flexWrap:'wrap',gap:5,flex:'1 1 180px',minWidth:0}}>
                      {s.legs.map((leg,i) => {
                        const sell = String(leg.buySell||leg.action||'sell').toUpperCase().startsWith('S');
                        const cp = (leg.callPut||leg.optionType||'CE').toUpperCase().includes('P') ? 'PE' : 'CE';
                        return (
                          <span key={i} title={`${sell?'SELL':'BUY'} ${cp} ${legStrikeLabel(leg)} × ${leg.qty||1}`}
                            style={{display:'inline-flex',alignItems:'center',gap:4,padding:'3px 8px',borderRadius:7,fontSize:11,fontWeight:800,background:sell?'#fef2f2':'#ecfdf5',color:sell?'#b91c1c':'#047857',border:`1px solid ${sell?'#fecaca':'#a7f3d0'}`}}>
                            {sell?'S':'B'} {cp} {legStrikeLabel(leg)} <span style={{opacity:0.65,fontWeight:700}}>×{leg.qty||1}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {/* actions (right) */}
                  <div style={{display:'flex',alignItems:'center',gap:8,marginLeft:'auto',flexShrink:0}}>
                    <button onClick={() => setInfoOpen(infoOpen === s._id ? null : s._id)} title="Kaise kaam karti hai"
                      style={{display:'flex',alignItems:'center',gap:5,padding:'8px 11px',background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.08)' : '#eff6ff',color:UI.blue,border:`1px solid ${IS_CRYPTO_PORTAL ? '#2b3139' : '#dbeafe'}`,borderRadius:9,fontSize:12,fontWeight:800,cursor:'pointer'}}>ℹ️ Info <span style={{fontSize:10}}>{infoOpen === s._id ? '▲' : '▼'}</span></button>
                    <a className="strat-bt" href={'/backtesting?strategy=' + s._id}
                      style={{display:'flex',alignItems:'center',gap:6,padding:'8px 12px',background:UI.card,color:UI.indigo,border:'1.5px solid #e0e7ff',borderRadius:9,fontSize:12.5,fontWeight:800,textDecoration:'none',whiteSpace:'nowrap'}}>🔬 Backtest</a>
                    {s.isDeployed ? (
                      <>
                        <div style={{display:'flex',alignItems:'center',gap:6,padding:'8px 12px',background: IS_CRYPTO_PORTAL ? 'rgba(14,203,129,0.12)' : '#dcfce7',color:'#15803d',borderRadius:9,fontSize:12.5,fontWeight:800,whiteSpace:'nowrap'}}>✅ {s.mode === 'Paper' ? 'Paper' : 'Live'}</div>
                        <button className="strat-deploy" disabled={exiting === s._id} onClick={() => exitStrategy(s)}
                          title="Undeploy + saari open positions square off"
                          style={{display:'flex',alignItems:'center',gap:4,padding:'8px 12px',background:UI.red,color:'#fff',border:'none',borderRadius:9,fontSize:12,fontWeight:800,cursor:'pointer',whiteSpace:'nowrap',opacity: exiting === s._id ? 0.6 : 1}}>{exiting === s._id ? 'Exiting…' : '🛑 Exit'}</button>
                      </>
                    ) : (
                      <>
                        <button className="strat-deploy" onClick={() => deployStrategy(s._id, 'Paper')}
                          style={{display:'flex',alignItems:'center',gap:4,padding:'8px 12px',background:UI.card,color:UI.blue,border:'1.5px solid #bfdbfe',borderRadius:9,fontSize:12,fontWeight:800,cursor:'pointer',whiteSpace:'nowrap'}}>📝 Paper</button>
                        <button className="strat-deploy" onClick={() => deployStrategy(s._id, 'Live')}
                          style={{display:'flex',alignItems:'center',gap:4,padding:'8px 12px',background:`linear-gradient(135deg,${UI.green},#16a34a)`,color:'#fff',border:'none',borderRadius:9,fontSize:12,fontWeight:800,cursor:'pointer',boxShadow:'0 2px 10px rgba(22,163,74,0.24)',whiteSpace:'nowrap'}}>🚀 Go Live</button>
                      </>
                    )}
                    {/* ⋮ menu */}
                    <div style={{position:'relative',flexShrink:0}}>
                      <button onClick={() => setMenuOpen(menuOpen === s._id ? null : s._id)} aria-label="More actions"
                        style={{width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',background:menuOpen===s._id?'#f1f5f9':'transparent',border:'none',borderRadius:9,cursor:'pointer',color:UI.sub,fontSize:20,lineHeight:1}}>⋮</button>
                      {menuOpen === s._id && (
                        <>
                          <div onClick={() => setMenuOpen(null)} style={{position:'fixed',inset:0,zIndex:40}} />
                          <div style={{position:'absolute',top:36,right:0,minWidth:158,background:UI.card,border:`1px solid ${UI.border}`,borderRadius:12,boxShadow:'0 10px 28px rgba(15,23,42,0.16)',zIndex:50,padding:5}}>
                            {[
                              {label:'Edit', icon:'✏️', onClick:() => { setMenuOpen(null); window.location.href = '/strategy-builder?id=' + s._id; }},
                              {label:'Duplicate', icon:'📋', onClick:() => duplicateStrategy(s._id)},
                              ...(isAdmin ? [{label: s.isTemplate ? 'Unpublish Template' : 'Publish as Template', icon: s.isTemplate ? '🚫' : '🌐', onClick:() => publishTemplate(s)}] : []),
                              {label:'Delete', icon:'🗑️', danger:true, onClick:() => { setMenuOpen(null); deleteStrategy(s._id); }},
                            ].map(m => (
                              <button key={m.label} className={'strat-menu-item' + (m.danger ? ' danger' : '')} onClick={m.onClick}
                                style={{display:'flex',alignItems:'center',gap:10,width:'100%',padding:'9px 10px',background:'transparent',border:'none',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,color:m.danger?UI.red:UI.text,textAlign:'left'}}>
                                <span style={{fontSize:14,width:16,textAlign:'center'}}>{m.icon}</span>{m.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {/* ── secondary line: entry logic + risk + feature chips ── */}
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginTop:9,fontSize:11.5,color:UI.sub,fontWeight:600,lineHeight:1.5}}>
                  <span style={{color:UI.text,fontWeight:700}}>⚡ {entryLogicSummary(s)}{s.indicatorConfig?.enabled ? ` · ${s.indicatorConfig.timeframe||'5min'}` : ''}</span>
                  {riskBits.length > 0 && <span>🛡️ {riskBits.join('  ·  ')}</span>}
                  {feats.map(f => (
                    <span key={f} style={{padding:'2px 8px',borderRadius:20,background: IS_CRYPTO_PORTAL ? 'rgba(44,140,255,0.10)' : '#eef2ff',color:'#4338ca',fontSize:10,fontWeight:700,border:'1px solid #e0e7ff'}}>{f}</span>
                  ))}
                </div>
                {/* auto "how it works" info panel — expands full-width below the row */}
                {infoOpen === s._id && (
                  <div style={{ marginTop: 10, padding: '12px 13px', background: UI.bg, border: `1px solid ${UI.border}`, borderRadius: 10, fontSize: 12, lineHeight: 1.55, color: UI.text }}>
                    {explainStrategy(s).map((sx, si) => (
                      <div key={si} style={{ marginBottom: si === explainStrategy(s).length - 1 ? 0 : 11 }}>
                        <div style={{ fontWeight: 800, fontSize: 11.5, color: UI.text, marginBottom: 4 }}>{sx.h}</div>
                        <ul style={{ margin: 0, paddingLeft: 16, color: UI.sub }}>
                          {sx.items.map((it, ii) => <li key={ii} style={{ marginBottom: 3 }}>{it}</li>)}
                        </ul>
                      </div>
                    ))}
                    <div style={{ marginTop: 10, fontSize: 10, color: UI.muted }}>Auto-generated from this strategy's settings.</div>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
      {pickFor && (
        <BrokerPickModal brokers={brokers} strategyId={pickFor}
          onConfirm={(brokerId) => { const id = pickFor; setPickFor(null); doDeploy(id, 'Live', brokerId); }}
          onClose={() => setPickFor(null)} />
      )}
    </Layout>
  );
}

// ── REPORTS ─────────────────────────────────────────
function Reports() {
  return <Layout><ReportsView /></Layout>;
}

function _ReportsLegacy() {
  const [report, setReport] = React.useState(null);
  const [from, setFrom] = React.useState('2025-01-01');
  const [to, setTo] = React.useState(new Date().toISOString().split('T')[0]);
  const [mode, setMode] = React.useState('all'); // all | live | paper
  const [loading, setLoading] = React.useState(false);

  const fetchReport = (m = mode) => {
    setLoading(true);
    fetch(API + '/reports?from=' + from + '&to=' + to + '&mode=' + m, { headers: getHeaders() })
      .then(r => r.json()).then(d => setReport(d)).catch(() => {})
      .finally(() => setLoading(false));
  };

  React.useEffect(() => { fetchReport(); }, []);

  return (
    <Layout>
      <PageHead title="Reports" subtitle="Track your trading performance over time" icon="📋" />
      <div style={card({ padding: 24 })}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ color: UI.muted, fontWeight: 700, fontSize: 12 }}>FROM</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ padding: '9px 12px', border: `1px solid ${UI.border}`, borderRadius: 9, fontSize: 13, outline: 'none', background: UI.bg }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ color: UI.muted, fontWeight: 700, fontSize: 12 }}>TO</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ padding: '9px 12px', border: `1px solid ${UI.border}`, borderRadius: 9, fontSize: 13, outline: 'none', background: UI.bg }} />
          </div>
          <button onClick={() => fetchReport()} disabled={loading} style={{ padding: '10px 24px', background: 'linear-gradient(135deg,#4f46e5,#2563eb)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(37,99,235,0.28)' }}>
            {loading ? 'Loading...' : 'Get Reports'}
          </button>
          {/* Paper / Live filter */}
          <div style={{ display: 'inline-flex', background: IS_CRYPTO_PORTAL ? '#1e2329' : '#eef1f6', borderRadius: 11, padding: 3, gap: 3, marginLeft: 'auto' }}>
            {[['all', 'All'], ['live', 'Live'], ['paper', 'Paper']].map(([m, label]) => (
              <button key={m} onClick={() => { setMode(m); fetchReport(m); }} style={{ padding: '7px 16px', fontSize: 12.5, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', background: mode === m ? '#fff' : 'transparent', color: mode === m ? (m === 'paper' ? '#7c3aed' : m === 'live' ? UI.green : UI.blue) : UI.sub, boxShadow: mode === m ? '0 1px 3px rgba(15,23,42,0.14)' : 'none' }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Paper/Live PnL split (shown in All view) */}
        {report && mode === 'all' && (report.paperPnl || report.livePnl) ? (
          <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, padding: '6px 12px', borderRadius: 9, background: IS_CRYPTO_PORTAL ? 'rgba(14,203,129,0.12)' : '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>Live P&L: ₹{(report.livePnl || 0).toLocaleString()}</span>
            <span style={{ fontSize: 12.5, fontWeight: 700, padding: '6px 12px', borderRadius: 9, background: IS_CRYPTO_PORTAL ? 'rgba(122,92,255,0.12)' : '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe' }}>Paper P&L: ₹{(report.paperPnl || 0).toLocaleString()}</span>
          </div>
        ) : null}

        {report ? (
          <>
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:16,marginBottom:24}}>
              {[
                ['Total P&L', '₹' + (report.totalPnl||0).toLocaleString(), report.totalPnl>=0?'#10b981':'#ef4444', '💰'],
                ['Total Trades', report.breakdown?.reduce((s,b)=>s+b.totalTrades,0)||0, '#374151', '📊'],
                ['Wins', report.breakdown?.reduce((s,b)=>s+b.wins,0)||0, '#10b981', '✅'],
                ['Losses', report.breakdown?.reduce((s,b)=>s+b.losses,0)||0, '#ef4444', '❌'],
                ['Open', report.breakdown?.reduce((s,b)=>s+(b.open||0),0)||0, '#2563eb', '⏳']
              ].map(([l,v,c,icon]) => (
                <div key={l} style={{border:'1px solid #e8eaf0',borderRadius:12,padding:20,textAlign:'center'}}>
                  <div style={{fontSize:24,marginBottom:8}}>{icon}</div>
                  <div style={{fontSize:11,color:'#9ca3af',marginBottom:6,fontWeight:700}}>{l}</div>
                  <div style={{fontSize:24,fontWeight:900,color:c}}>{v}</div>
                </div>
              ))}
            </div>
            {report.breakdown?.length > 0 ? (
              <div className="ar-tablewrap">
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:'2px solid #e8eaf0',background: IS_CRYPTO_PORTAL ? '#1e2329' : '#f8faff'}}>
                    {['Strategy','Trades','Wins','Losses','Open','Win Rate','P&L'].map(h => <th key={h} style={{padding:'12px 16px',textAlign:'left',fontSize:12,color:'#9ca3af',fontWeight:700}}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {report.breakdown.map((b,i) => (
                    <tr key={i} style={{borderBottom:'1px solid #f1f3f9'}}>
                      <td style={{padding:'14px 16px',fontWeight:700}}>{b.strategy}{b.paper && <span style={{marginLeft:8,padding:'2px 8px',borderRadius:20,background: IS_CRYPTO_PORTAL ? 'rgba(122,92,255,0.12)' : '#f5f3ff',color:'#7c3aed',fontSize:10,fontWeight:800,verticalAlign:'middle'}}>PAPER</span>}</td>
                      <td style={{padding:'14px 16px'}}>{b.totalTrades}</td>
                      <td style={{padding:'14px 16px',color:'#10b981',fontWeight:700}}>{b.wins}</td>
                      <td style={{padding:'14px 16px',color:'#ef4444',fontWeight:700}}>{b.losses}</td>
                      <td style={{padding:'14px 16px',color: IS_CRYPTO_PORTAL ? '#2c8cff' : '#2563eb',fontWeight:700}}>{b.open||0}</td>
                      <td style={{padding:'14px 16px'}}>{(b.wins+b.losses)>0?Math.round(b.wins/(b.wins+b.losses)*100)+'%':'—'}</td>
                      <td style={{padding:'14px 16px',fontWeight:800,fontSize:15,color:b.pnl>=0?'#10b981':'#ef4444'}}>₹{b.pnl?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            ) : <div style={{textAlign:'center',padding:60,color:'#9ca3af'}}><div style={{fontSize:48,marginBottom:12}}>📭</div><div style={{fontSize:16,fontWeight:600}}>No trade data yet</div><div style={{fontSize:13,marginTop:4}}>Start trading to see reports here</div></div>}

            {report.tradeLogs?.length > 0 && (
              <div style={{marginTop:28}}>
                <h2 style={{fontSize:15,fontWeight:800,marginBottom:14,color:UI.text}}>Trade Log</h2>
                <div className="ar-tablewrap">
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead>
                    <tr style={{borderBottom:'2px solid #e8eaf0',background: IS_CRYPTO_PORTAL ? '#1e2329' : '#f8faff'}}>
                      {['Date','Strategy','Instrument','Action','Qty','Entry Reason','P&L','Exit Time','Exit'].map(h => <th key={h} style={{padding:'12px 16px',textAlign:'left',fontSize:12,color:'#9ca3af',fontWeight:700}}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {report.tradeLogs.slice().reverse().map(t => {
                      const manual = t.exitReason === 'MANUAL';
                      const open = t.status === 'OPEN';
                      const badge = open
                        ? { label: t.live ? '🟢 Open (live)' : 'Open', bg: '#dbeafe', color: '#1e40af' }
                        : manual
                          ? { label: '🖐 Exited Manually', bg: '#fef3c7', color: '#92400e' }
                          : { label: 'Auto', bg: '#f3f4f6', color: IS_CRYPTO_PORTAL ? '#a7afba' : '#6b7280' };
                      return (
                        <tr key={t.id} style={{borderBottom:'1px solid #f1f3f9'}}>
                          <td style={{padding:'12px 16px',color: IS_CRYPTO_PORTAL ? '#a7afba' : '#6b7280'}}>{fmtDate(t.date)}</td>
                          <td style={{padding:'12px 16px',fontWeight:700}}>{t.strategy}{t.paper && <span style={{marginLeft:6,padding:'2px 7px',borderRadius:20,background: IS_CRYPTO_PORTAL ? 'rgba(122,92,255,0.12)' : '#f5f3ff',color:'#7c3aed',fontSize:9.5,fontWeight:800,verticalAlign:'middle'}}>PAPER</span>}</td>
                          <td style={{padding:'12px 16px'}}>{t.instrument}</td>
                          <td style={{padding:'12px 16px',fontWeight:700,color:t.action==='BUY'?'#10b981':'#ef4444'}}>{t.action}</td>
                          <td style={{padding:'12px 16px'}}>{t.qty}</td>
                          <td style={{padding:'12px 16px',color: IS_CRYPTO_PORTAL ? '#b7bdc6' : '#4b5563',fontSize:12,maxWidth:240}}>{t.entryReason || '—'}</td>
                          <td style={{padding:'12px 16px',fontWeight:700,color:(t.pnl||0)>=0?'#10b981':'#ef4444'}}>₹{(t.pnl||0).toLocaleString()}</td>
                          <td style={{padding:'12px 16px',color: IS_CRYPTO_PORTAL ? '#a7afba' : '#6b7280',whiteSpace:'nowrap'}}>{t.exitTime ? new Date(t.exitTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '—'}</td>
                          <td style={{padding:'12px 16px'}}>
                            <span style={{padding:'3px 10px',borderRadius:20,background:badge.bg,color:badge.color,fontSize:11,fontWeight:700,whiteSpace:'nowrap'}}>{badge.label}</span>
                            {!open && t.exitNote ? <div style={{marginTop:4,fontSize:11,color: IS_CRYPTO_PORTAL ? '#a7afba' : '#6b7280',maxWidth:220}}>{t.exitNote}</div> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </>
        ) : <div style={{textAlign:'center',padding:60,color:'#9ca3af'}}>Loading reports...</div>}
      </div>
    </Layout>
  );
}

// ── SUBSCRIPTION ────────────────────────────────────
function Subscription() {
  const [plans, setPlans] = React.useState(null);
  React.useEffect(() => {
    fetch(API + '/subscriptions/plans', { headers: getHeaders() })
      .then(r => r.json()).then(d => setPlans(d)).catch(() => {});
  }, []);
  return (
    <Layout>
      <PageHead title="Subscriptions" subtitle="Choose the plan that works best for you" icon="💳" />
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:20}}>
        {(plans?.plans||[]).map((plan,i) => (
          <div key={plan.id} style={{background:UI.card,borderRadius:16,padding:28,border:plan.id==='unlimited'?'2px solid #2563eb':'1px solid #e8eaf0',position:'relative',boxShadow:plan.id==='unlimited'?'0 4px 20px rgba(37,99,235,0.12)':'none'}}>
            {plan.id==='unlimited' && <div style={{position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',background: IS_CRYPTO_PORTAL ? '#2c8cff' : '#2563eb',color:'#fff',padding:'4px 16px',borderRadius:20,fontSize:11,fontWeight:700,whiteSpace:'nowrap'}}>MOST POPULAR</div>}
            <div style={{fontWeight:800,fontSize:18,marginBottom:4,color: IS_CRYPTO_PORTAL ? '#eaecef' : '#1a1a2e'}}>{plan.name}</div>
            <div style={{fontSize:36,fontWeight:900,color: IS_CRYPTO_PORTAL ? '#eaecef' : '#1a1a2e',marginBottom:4}}>
              ₹{(plan.price?.quarterly||0).toLocaleString()}
              <span style={{fontSize:13,fontWeight:400,color:'#9ca3af'}}>/quarter</span>
            </div>
            <div style={{fontSize:12,color:'#9ca3af',marginBottom:20}}>+GST</div>
            <button style={{width:'100%',padding:'12px',borderRadius:10,border:'none',fontSize:14,fontWeight:700,cursor:'pointer',marginBottom:20,background:plans?.current===plan.id?'none':plan.id==='unlimited'?'#2563eb':'#f1f5f9',color:plans?.current===plan.id?'#10b981':plan.id==='unlimited'?'#fff':'#374151',border:plans?.current===plan.id?'2px solid #a7f3d0':'none'}}>
              {plans?.current===plan.id?'✅ Current Plan':'Get Started'}
            </button>
            <ul style={{listStyle:'none',padding:0,margin:0}}>
              {plan.features?.map(f => (
                <li key={f} style={{display:'flex',alignItems:'flex-start',gap:8,marginBottom:10,fontSize:13,color: IS_CRYPTO_PORTAL ? '#b7bdc6' : '#4b5563'}}>
                  <span style={{color:'#10b981',fontWeight:700,flexShrink:0}}>✓</span>{f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Layout>
  );
}

// ── LOGIN ───────────────────────────────────────────
function Login() {
  const [form, setForm] = React.useState({ userId: '', password: '' });
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(API + '/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message);
      localStorage.setItem('token', d.token);
      window.location.href = '/';
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background: IS_CRYPTO_PORTAL ? 'radial-gradient(1200px 600px at 50% -10%, #11233f 0%, #0b0e11 60%)' : 'linear-gradient(135deg,#eff6ff 0%,#f0fdf4 100%)'}}>
      <div style={{width:440,background:UI.card,borderRadius:24,padding:48,boxShadow:'0 20px 80px rgba(37,99,235,0.12)',border:'1px solid #e8eaf0'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:36}}>
          <div style={{width:40,height:40,background:`linear-gradient(135deg,${BRAND.g1},${BRAND.g2})`,borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:900,fontSize:22}}>
            {BRAND.glyph || <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M3 17L9 11L13 15L21 7" stroke="white" strokeWidth="2.5" strokeLinecap="round"/></svg>}
          </div>
          <span style={{fontWeight:900,fontSize:24,color: IS_CRYPTO_PORTAL ? '#eaecef' : '#1a1a2e',letterSpacing:'-0.5px'}}>{BRAND.name}</span>
        </div>
        <h1 style={{fontSize:28,fontWeight:900,marginBottom:6,color: IS_CRYPTO_PORTAL ? '#eaecef' : '#1a1a2e'}}>Welcome back</h1>
        <p style={{color:'#9ca3af',fontSize:15,marginBottom:32}}>Sign in to your trading dashboard</p>
        {error && <div style={{background: IS_CRYPTO_PORTAL ? 'rgba(246,70,93,0.12)' : '#fef2f2',color:'#dc2626',padding:'12px 16px',borderRadius:10,fontSize:14,marginBottom:20,fontWeight:500,border:'1px solid #fecaca'}}>{error}</div>}
        <form onSubmit={handleLogin}>
          <div style={{marginBottom:20}}>
            <label style={{fontSize:13,fontWeight:700,display:'block',marginBottom:8,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Email or User ID</label>
            <input type="text" value={form.userId} onChange={e => setForm({...form,userId:e.target.value})} placeholder="sunil@example.com" required style={{width:'100%',padding:'13px 16px',border:'1.5px solid #e8eaf0',borderRadius:12,fontSize:15,outline:'none',boxSizing:'border-box',transition:'0.15s'}} onFocus={e=>e.target.style.borderColor='#2563eb'} onBlur={e=>e.target.style.borderColor='#e8eaf0'}/>
          </div>
          <div style={{marginBottom:28}}>
            <label style={{fontSize:13,fontWeight:700,display:'block',marginBottom:8,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Password</label>
            <input type="password" value={form.password} onChange={e => setForm({...form,password:e.target.value})} placeholder="Enter your password" required style={{width:'100%',padding:'13px 16px',border:'1.5px solid #e8eaf0',borderRadius:12,fontSize:15,outline:'none',boxSizing:'border-box'}} onFocus={e=>e.target.style.borderColor='#2563eb'} onBlur={e=>e.target.style.borderColor='#e8eaf0'}/>
          </div>
          <button type="submit" disabled={loading} style={{width:'100%',padding:'15px',background:loading?'#93c5fd':'linear-gradient(135deg,#2563eb,#1d4ed8)',color:'#fff',border:'none',borderRadius:12,fontSize:16,fontWeight:800,cursor:loading?'not-allowed':'pointer',boxShadow:'0 4px 16px rgba(37,99,235,0.3)'}}>
            {loading ? 'Signing in...' : 'Sign In →'}
          </button>
        </form>
        <div style={{textAlign:'center',marginTop:24,fontSize:14,color: IS_CRYPTO_PORTAL ? '#a7afba' : '#6b7280'}}>
          Don't have an account? <a href="/register" style={{color: IS_CRYPTO_PORTAL ? '#2c8cff' : '#2563eb',fontWeight:700,textDecoration:'none'}}>Create one</a>
        </div>
      </div>
    </div>
  );
}

// ── REGISTER ────────────────────────────────────────
function Register() {
  const [form, setForm] = React.useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    if (form.password !== form.confirm) return setError('Passwords do not match');
    setLoading(true);
    try {
      const res = await fetch(API + '/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone, password: form.password }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || 'Registration failed');
      localStorage.setItem('token', d.token);
      window.location.href = '/';
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const inputStyle = { width:'100%', padding:'13px 16px', border:'1.5px solid #e8eaf0', borderRadius:12, fontSize:15, outline:'none', boxSizing:'border-box' };

  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background: IS_CRYPTO_PORTAL ? 'radial-gradient(1200px 600px at 50% -10%, #11233f 0%, #0b0e11 60%)' : 'linear-gradient(135deg,#eff6ff 0%,#f0fdf4 100%)',padding:'24px 0'}}>
      <div style={{width:440,background:UI.card,borderRadius:24,padding:48,boxShadow:'0 20px 80px rgba(37,99,235,0.12)',border:'1px solid #e8eaf0'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:32}}>
          <div style={{width:40,height:40,background:`linear-gradient(135deg,${BRAND.g1},${BRAND.g2})`,borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:900,fontSize:22}}>
            {BRAND.glyph || <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M3 17L9 11L13 15L21 7" stroke="white" strokeWidth="2.5" strokeLinecap="round"/></svg>}
          </div>
          <span style={{fontWeight:900,fontSize:24,color: IS_CRYPTO_PORTAL ? '#eaecef' : '#1a1a2e',letterSpacing:'-0.5px'}}>{BRAND.name}</span>
        </div>
        <h1 style={{fontSize:28,fontWeight:900,marginBottom:6,color: IS_CRYPTO_PORTAL ? '#eaecef' : '#1a1a2e'}}>Create your account</h1>
        <p style={{color:'#9ca3af',fontSize:15,marginBottom:28}}>Start automating your trades in minutes</p>
        {error && <div style={{background: IS_CRYPTO_PORTAL ? 'rgba(246,70,93,0.12)' : '#fef2f2',color:'#dc2626',padding:'12px 16px',borderRadius:10,fontSize:14,marginBottom:20,fontWeight:500,border:'1px solid #fecaca'}}>{error}</div>}
        <form onSubmit={handleRegister}>
          <div style={{marginBottom:18}}>
            <label style={{fontSize:13,fontWeight:700,display:'block',marginBottom:8,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Full Name</label>
            <input type="text" value={form.name} onChange={e => update('name', e.target.value)} placeholder="Sunil Yadav" required style={inputStyle}/>
          </div>
          <div style={{marginBottom:18}}>
            <label style={{fontSize:13,fontWeight:700,display:'block',marginBottom:8,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Email</label>
            <input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="sunil@example.com" required style={inputStyle}/>
          </div>
          <div style={{marginBottom:18}}>
            <label style={{fontSize:13,fontWeight:700,display:'block',marginBottom:8,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Phone <span style={{color:'#9ca3af',fontWeight:500}}>(optional)</span></label>
            <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="9876543210" style={inputStyle}/>
          </div>
          <div style={{marginBottom:18}}>
            <label style={{fontSize:13,fontWeight:700,display:'block',marginBottom:8,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Password</label>
            <input type="password" value={form.password} onChange={e => update('password', e.target.value)} placeholder="At least 6 characters" required style={inputStyle}/>
          </div>
          <div style={{marginBottom:28}}>
            <label style={{fontSize:13,fontWeight:700,display:'block',marginBottom:8,color: IS_CRYPTO_PORTAL ? '#cdd2da' : '#374151'}}>Confirm Password</label>
            <input type="password" value={form.confirm} onChange={e => update('confirm', e.target.value)} placeholder="Re-enter your password" required style={inputStyle}/>
          </div>
          <button type="submit" disabled={loading} style={{width:'100%',padding:'15px',background:loading?'#93c5fd':'linear-gradient(135deg,#2563eb,#1d4ed8)',color:'#fff',border:'none',borderRadius:12,fontSize:16,fontWeight:800,cursor:loading?'not-allowed':'pointer',boxShadow:'0 4px 16px rgba(37,99,235,0.3)'}}>
            {loading ? 'Creating account...' : 'Create Account →'}
          </button>
        </form>
        <div style={{textAlign:'center',marginTop:24,fontSize:14,color: IS_CRYPTO_PORTAL ? '#a7afba' : '#6b7280'}}>
          Already have an account? <a href="/login" style={{color: IS_CRYPTO_PORTAL ? '#2c8cff' : '#2563eb',fontWeight:700,textDecoration:'none'}}>Sign in</a>
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({children}) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace/>;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <ReconnectBanner />
      <Routes>
        <Route path="/login" element={<Login/>}/>
        <Route path="/register" element={<Register/>}/>
        <Route path="/broker" element={<BrokerCallback/>}/>
        <Route path="/" element={<ProtectedRoute><Dashboard/></ProtectedRoute>}/>
        <Route path="/pre-market" element={<ProtectedRoute><PreMarket/></ProtectedRoute>}/>
        <Route path="/broker-manage" element={<ProtectedRoute><Layout><Broker/></Layout></ProtectedRoute>}/>
        <Route path="/strategy-prompt" element={<ProtectedRoute><Layout><StrategyPrompt/></Layout></ProtectedRoute>}/>
        <Route path="/strategy-terminal" element={<ProtectedRoute><Layout><StrategyTerminal/></Layout></ProtectedRoute>}/>
        <Route path="/strategy-builder" element={<ProtectedRoute><Layout><StrategyBuilder/></Layout></ProtectedRoute>}/>
        <Route path="/strategies" element={<ProtectedRoute><Strategies/></ProtectedRoute>}/>
        <Route path="/positions" element={<ProtectedRoute><Positions/></ProtectedRoute>}/>
        <Route path="/activity" element={<ProtectedRoute><Activity/></ProtectedRoute>}/>
        <Route path="/ai-agent" element={<ProtectedRoute><Layout><AiAgentLog/></Layout></ProtectedRoute>}/>
        <Route path="/backtesting" element={<ProtectedRoute><Layout><StrategyBacktest/></Layout></ProtectedRoute>}/>
        <Route path="/data-analysis" element={<ProtectedRoute><Layout><DataAnalysis/></Layout></ProtectedRoute>}/>
        <Route path="/simulated-trading" element={<ProtectedRoute><Layout><SimulatedTrading/></Layout></ProtectedRoute>}/>
        <Route path="/option-chart" element={<ProtectedRoute><Layout>{IS_CRYPTO_PORTAL ? <CryptoChart/> : <OptionChart/>}</Layout></ProtectedRoute>}/>
        <Route path="/reports" element={<ProtectedRoute><Reports/></ProtectedRoute>}/>
        <Route path="/subscription" element={<ProtectedRoute><Subscription/></ProtectedRoute>}/>
        <Route path="/settings" element={<ProtectedRoute><Layout><Settings/></Layout></ProtectedRoute>}/>
        <Route path="/admin" element={<ProtectedRoute><Layout><AdminPanel/></Layout></ProtectedRoute>}/>
        <Route path="*" element={<Navigate to="/" replace/>}/>
      </Routes>
    </BrowserRouter>
  );
}

